// API Service for Commander PSIM
import { useAuthStore } from '../stores/auth.store';
import { getApiBaseUrl } from '../config';

// Use getter to ensure Tauri is detected at runtime
const getApiBase = () => getApiBaseUrl();

class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const { getAuthHeaders, logout, refreshAuth } = useAuthStore.getState();

  // Add Content-Type header for requests with body
  const headers: Record<string, string> = {
    // CSRF guard (E1): cookie-authed mutations must carry X-Requested-With
    // (or a Bearer). Sending it on every call is harmless for GETs.
    'X-Requested-With': 'XMLHttpRequest',
    ...getAuthHeaders(),
  };
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${getApiBase()}${endpoint}`, {
    ...options,
    cache: 'no-store',
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  // Handle 401 - try to refresh token
  if (response.status === 401) {
    try {
      await refreshAuth();
      // Retry the request with same headers
      const retryHeaders: Record<string, string> = {
        'X-Requested-With': 'XMLHttpRequest',
        ...getAuthHeaders(),
      };
      if (options.body) {
        retryHeaders['Content-Type'] = 'application/json';
      }
      const retryResponse = await fetch(`${getApiBase()}${endpoint}`, {
        ...options,
        cache: 'no-store',
        headers: {
          ...retryHeaders,
          ...options.headers,
        },
      });
      if (!retryResponse.ok) {
        logout();
        throw new ApiError(401, 'UNAUTHORIZED', 'Session expired');
      }
      const data = await retryResponse.json();
      return data.data as T;
    } catch {
      logout();
      throw new ApiError(401, 'UNAUTHORIZED', 'Session expired');
    }
  }

  if (!response.ok) {
    // The server may return { error: "string" } or { error: { message, code } }
    // or just a stringified body. We extract the most informative message we
    // can — but if the only available message is a generic HTTP-status string
    // ("Bad Request", "Internal Server Error", etc.) we leave it; downstream
    // callers should pass the thrown error to friendlyError() to humanise it.
    const error = await response.json().catch(() => ({ error: { message: '' } }));
    const errObj = error.error;
    const serverMsg =
      typeof errObj === 'string' ? errObj :
      errObj?.message ||
      error.message ||
      '';
    const fallbackByStatus =
      response.status === 401 ? 'Your session is no longer valid. Please sign in again.' :
      response.status === 403 ? 'You don’t have permission to perform that action.' :
      response.status === 404 ? 'The item you requested was not found.' :
      response.status === 429 ? 'Too many requests — please wait a moment and try again.' :
      response.status >= 500 ? 'A server error occurred. Try again or contact support.' :
      'The request failed. Please try again.';
    const errMessage = serverMsg || fallbackByStatus;
    const errCode = typeof errObj === 'object' ? errObj?.code : undefined;
    throw new ApiError(
      response.status,
      errCode || 'ERROR',
      errMessage
    );
  }

  // Handle 204 No Content responses
  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json();
  if (data.data === undefined) {
    console.warn('[fetchApi] Response has no data property:', endpoint, data);
  }
  return data.data as T;
}

// Minimal verb adapter over fetchApi. A few slices (chat call-signaling) were
// authored against a bare `api.get/post/...` helper that wasn't defined in the
// upstream file; this provides it so the whole module type-checks.
const api = {
  get: <T>(endpoint: string) => fetchApi<T>(endpoint),
  post: <T>(endpoint: string, body?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(endpoint: string, body?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(endpoint: string, body?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(endpoint: string) => fetchApi<T>(endpoint, { method: 'DELETE' }),
};

// Users API
export interface User {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissions: string[];
  status: 'active' | 'inactive' | 'pending' | 'locked';
  lastLoginAt?: string;
  createdAt: string;
}

export interface CreateUserDto {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  password: string;
  roles: string[];
}

export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  roles?: string[];
  permissions?: string[];
  status?: 'active' | 'inactive';
}

// System Settings API (tenant-wide configuration)
// The api-gateway mounts these routes under /settings (see
// apps/api-gateway/src/rest/index.ts). The handler returns the
// combined object { tenant, notifications, alarm, map, video } from
// GET /settings; there is no per-section GET — `getSection` filters
// client-side. PATCH /settings expects { section, data }.
export const systemSettingsApi = {
  getAll: () => fetchApi<Record<string, unknown>>('/settings'),
  getSection: async (section: string) => {
    const all = await fetchApi<Record<string, unknown>>('/settings');
    return (all?.[section] as Record<string, unknown> | undefined) ?? {};
  },
  updateSection: (section: string, data: Record<string, unknown>) =>
    fetchApi<Record<string, unknown>>('/settings', {
      method: 'PATCH',
      body: JSON.stringify({ section, data }),
    }),
};

export const usersApi = {
  list: () => fetchApi<User[]>('/users'),
  get: (id: string) => fetchApi<User>(`/users/${id}`),
  create: (data: CreateUserDto) =>
    fetchApi<User>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateUserDto) =>
    fetchApi<User>(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/users/${id}`, {
      method: 'DELETE',
    }),
  resetPassword: (id: string) =>
    fetchApi<void>(`/users/${id}/reset-password`, {
      method: 'POST',
    }),
};

// Driver Instance API (database-backed driver configurations)
export interface DriverInstance {
  id: string;
  tenantId: string;
  driverId: string;
  name: string;
  status: 'connected' | 'disconnected' | 'error' | 'disabled' | 'initializing';
  config: DriverInstanceConfig;
  deviceCount: number;
  lastHealthCheck?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DriverInstanceConfig {
  host: string;
  port: number;
  username?: string;
  password?: string;
  ssl?: boolean;
  pollingInterval?: number;
  connectionTimeout?: number;
  retryAttempts?: number;
  customSettings?: Record<string, unknown>;
  // Driver package info
  driverSlug?: string;
  manufacturer?: string;
  model?: string;
  type?: string;
  version?: string;
}

export interface CreateDriverInstanceDto {
  name: string;
  driverSlug: string;
  type: string;
  manufacturer: string;
  model?: string;
  config: {
    host: string;
    port: number;
    username?: string;
    password?: string;
    ssl?: boolean;
    pollingInterval?: number;
    connectionTimeout?: number;
    retryAttempts?: number;
    customSettings?: Record<string, unknown>;
  };
}

export interface UpdateDriverInstanceDto {
  name?: string;
  config?: Partial<DriverInstanceConfig>;
}

// Legacy Driver type for backwards compatibility
export interface Driver {
  id: string;
  name: string;
  type: string;
  manufacturer: string;
  model: string;
  version: string;
  status: 'connected' | 'disconnected' | 'error' | 'disabled' | 'initializing';
  config: DriverConfig;
  deviceCount: number;
  lastHeartbeat?: string;
  createdAt: string;
  updatedAt: string;
  driverSlug?: string; // The driver package slug (e.g., 'axis-vapix')
  runtime?: 'node' | 'python' | 'go' | 'java'; // Driver runtime/language
}

export interface DriverConfig {
  host: string;
  port: number;
  username?: string;
  password?: string;
  ssl: boolean;
  pollingInterval: number;
  connectionTimeout: number;
  retryAttempts: number;
  customSettings?: Record<string, unknown>;
}

export interface CreateDriverDto {
  name: string;
  type: Driver['type'];
  manufacturer: string;
  model: string;
  config: DriverConfig;
}

export interface UpdateDriverDto {
  name?: string;
  config?: Partial<DriverConfig>;
}

// Helper to convert DriverInstance to Driver format for UI compatibility
function instanceToDriver(instance: DriverInstance): Driver {
  if (!instance) {
    console.warn('[instanceToDriver] Received null/undefined instance');
    // Return a placeholder driver to avoid crashes
    return {
      id: '',
      name: 'Unknown',
      type: 'generic',
      manufacturer: 'Unknown',
      model: '',
      version: '0.0.0',
      status: 'error',
      config: {
        host: '',
        port: 80,
        ssl: false,
        pollingInterval: 30000,
        connectionTimeout: 10000,
        retryAttempts: 3,
      },
      deviceCount: 0,
      createdAt: '',
      updatedAt: '',
    };
  }
  // Handle double-encoded jsonb config (string instead of object)
  const rawConfig = instance.config || {};
  const config: Record<string, unknown> = typeof rawConfig === 'string'
    ? (() => { try { return JSON.parse(rawConfig); } catch { return {}; } })()
    : (rawConfig as unknown as Record<string, unknown>);

  // Standard config fields that map directly to DriverConfig
  const standardFields = [
    'host', 'port', 'username', 'password', 'ssl',
    'pollingInterval', 'connectionTimeout', 'retryAttempts',
    'customSettings', 'driverSlug', 'manufacturer', 'model', 'type', 'version'
  ];

  // Collect all non-standard fields into customSettings
  // This preserves driver-specific config like clientId, clientSecret, region, apiKey, etc.
  const existingCustomSettings = (config.customSettings as Record<string, unknown>) || {};
  const extraSettings: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(config)) {
    if (!standardFields.includes(key) && value !== undefined) {
      extraSettings[key] = value;
    }
  }

  // Merge existing customSettings with extra settings (extra settings take precedence)
  const mergedCustomSettings = { ...existingCustomSettings, ...extraSettings };

  // Infer type from driverSlug if type is not explicitly set
  const inferTypeFromSlug = (slug?: string): string => {
    if (!slug) return 'generic';
    const s = slug.toLowerCase();
    if (s.includes('access') || s.includes('brivo') || s.includes('kisi') || s.includes('openpath') || s.includes('paxton') || s.includes('lenel') || s.includes('nedap') || s.includes('rosslare') || s.includes('suprema') || s.includes('genea')) return 'access_control';
    if (s.includes('intrusion') || s.includes('risco') || s.includes('alarm') || s.includes('pima') || s.includes('paradox') || s.includes('bosch-intrusion') || s.includes('dsc') || s.includes('honeywell') || s.includes('elfar') || s.includes('dds')) return 'intrusion';
    if (s.includes('camera') || s.includes('nvr') || s.includes('vms') || s.includes('video') || s.includes('axis') || s.includes('dahua') || s.includes('hikvision') || s.includes('milestone') || s.includes('nx-witness') || s.includes('lumana') || s.includes('exacq') || s.includes('avigilon') || s.includes('verkada') || s.includes('genetec')) return 'camera';
    if (s.includes('intercom') || s.includes('commend') || s.includes('zenitel')) return 'intercom';
    if (s.includes('fire') || s.includes('notifier') || s.includes('simplex') || s.includes('hochiki') || s.includes('cerberus')) return 'fire';
    if (s.includes('fleet') || s.includes('pointer') || s.includes('trafilog') || s.includes('skylock') || s.includes('ituran')) return 'fleet';
    if (s.includes('alert') || s.includes('red-alert')) return 'alert';
    if (s.includes('radar') || s.includes('magos')) return 'radar';
    if (s.includes('light') || s.includes('dali') || s.includes('knx')) return 'lighting';
    if (s.includes('modbus') || s.includes('opc') || s.includes('bacnet') || s.includes('snmp') || s.includes('protocol')) return 'protocol';
    if (s.includes('radio') || s.includes('motorola') || s.includes('turbonet')) return 'radio';
    if (s.includes('video-wall') || s.includes('videowall') || s.includes('vvs')) return 'video_wall';
    if (s.includes('mqtt') || s.includes('rdbms') || s.includes('database')) return 'database';
    if (s.includes('slack') || s.includes('jira') || s.includes('integration')) return 'integration';
    if (s.includes('fence') || s.includes('pids')) return 'intrusion';
    return 'generic';
  };

  const driverSlug = config.driverSlug as string | undefined;
  const inferredType = (config.type as Driver['type']) || inferTypeFromSlug(driverSlug);

  return {
    id: instance.id,
    name: instance.name,
    type: inferredType,
    manufacturer: (config.manufacturer as string) || 'Unknown',
    model: (config.model as string) || '',
    version: (config.version as string) || '1.0.0',
    status: instance.status,
    config: {
      host: (config.host as string) || '',
      port: (config.port as number) || 80,
      username: config.username as string | undefined,
      password: config.password as string | undefined,
      ssl: (config.ssl as boolean) || false,
      pollingInterval: (config.pollingInterval as number) || 30000,
      connectionTimeout: (config.connectionTimeout as number) || 10000,
      retryAttempts: (config.retryAttempts as number) || 3,
      customSettings: Object.keys(mergedCustomSettings).length > 0 ? mergedCustomSettings : undefined,
    },
    deviceCount: instance.deviceCount || 0,
    lastHeartbeat: instance.lastHealthCheck,
    createdAt: instance.createdAt,
    updatedAt: instance.updatedAt,
    driverSlug: config.driverSlug as string | undefined, // Pass through the driver package slug
  };
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const driverInstancesApi = {
  list: async (filters?: { status?: string }): Promise<Driver[]> => {
    const params = new URLSearchParams();
    params.append('limit', '1000');
    if (filters?.status) params.append('status', filters.status);
    const query = params.toString();
    try {
      const response = await fetchApi<PaginatedResponse<DriverInstance>>(
        `/driver-instances${query ? `?${query}` : ''}`
      );
      // Handle both paginated response and direct array response
      const items = response?.items ?? (Array.isArray(response) ? response : []);
      // Hot-path: this list call fires from FleetTracker poll, FleetMapModule,
      // dashboards, etc. Logging the entire items array on every call costs
      // 10–20% on Chrome's main thread under load. Dev-only summary.
      if (import.meta.env.DEV) {
        console.debug('[driverInstancesApi.list] Fetched items:', items.length);
      }
      return items.map(instanceToDriver);
    } catch (error) {
      console.error('[driverInstancesApi.list] Error fetching drivers:', error);
      throw error;
    }
  },
  get: async (id: string): Promise<Driver> => {
    const instance = await fetchApi<DriverInstance>(`/driver-instances/${id}`);
    return instanceToDriver(instance);
  },
  create: async (data: CreateDriverInstanceDto): Promise<Driver> => {
    const instance = await fetchApi<DriverInstance>('/driver-instances', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return instanceToDriver(instance);
  },
  update: async (id: string, data: UpdateDriverInstanceDto): Promise<Driver> => {
    const instance = await fetchApi<DriverInstance>(`/driver-instances/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return instanceToDriver(instance);
  },
  delete: (id: string) =>
    fetchApi<void>(`/driver-instances/${id}`, {
      method: 'DELETE',
    }),
  start: async (id: string): Promise<Driver> => {
    const instance = await fetchApi<DriverInstance>(`/driver-instances/${id}/start`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return instanceToDriver(instance);
  },
  stop: async (id: string): Promise<Driver> => {
    const instance = await fetchApi<DriverInstance>(`/driver-instances/${id}/stop`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return instanceToDriver(instance);
  },
  restart: async (id: string): Promise<Driver> => {
    const instance = await fetchApi<DriverInstance>(`/driver-instances/${id}/restart`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return instanceToDriver(instance);
  },
  testConnection: (id: string) =>
    fetchApi<{ success: boolean; message: string }>(`/driver-instances/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  getAvailableDrivers: async (refresh = false): Promise<AvailableDriversResponse> => {
    const url = refresh
      ? `${getApiBase()}/drivers/available?refresh=true`
      : `${getApiBase()}/drivers/available`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch available drivers');
    }
    return response.json();
  },
  getDataModel: async (id: string): Promise<any> => {
    try {
      return await fetchApi<any>(`/driver-instances/${id}/data-model`);
    } catch {
      return null;
    }
  },
  getZones: async (id: string): Promise<DriverZone[]> => {
    try {
      return await fetchApi<DriverZone[]>(`/driver-instances/${id}/zones`);
    } catch {
      return [];
    }
  },
  getPartitions: async (id: string): Promise<DriverPartition[]> => {
    try {
      return await fetchApi<DriverPartition[]>(`/driver-instances/${id}/partitions`);
    } catch {
      return [];
    }
  },
  // Driver logs API
  getLogs: async (
    id: string,
    options?: { limit?: number; offset?: number; level?: string; search?: string }
  ): Promise<DriverLogsResponse> => {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.level) params.append('level', options.level);
    if (options?.search) params.append('search', options.search);
    const query = params.toString();
    return fetchApi<DriverLogsResponse>(`/driver-instances/${id}/logs${query ? `?${query}` : ''}`);
  },
  getErrorLogs: async (id: string, limit = 50): Promise<DriverLogsResponse> => {
    return fetchApi<DriverLogsResponse>(`/driver-instances/${id}/logs/errors?limit=${limit}`);
  },
  clearLogs: async (id: string): Promise<{ cleared: number }> => {
    return fetchApi<{ cleared: number }>(`/driver-instances/${id}/logs`, {
      method: 'DELETE',
    });
  },
  // Advanced logging
  getAdvancedLogging: async (id: string): Promise<{ enabled: boolean }> => {
    return fetchApi<{ enabled: boolean }>(`/driver-instances/${id}/advanced-logging`);
  },
  setAdvancedLogging: async (id: string, enabled: boolean): Promise<{ enabled: boolean }> => {
    return fetchApi<{ enabled: boolean }>(`/driver-instances/${id}/advanced-logging`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  },
  // OAuth methods (for 3-legged OAuth flows like Brivo)
  getOAuthAuthorizeUrl: async (id: string): Promise<OAuthAuthorizeUrlResponse> => {
    return fetchApi<OAuthAuthorizeUrlResponse>(`/driver-instances/${id}/oauth/authorize-url`);
  },
  getOAuthStatus: async (id: string): Promise<OAuthStatusResponse> => {
    return fetchApi<OAuthStatusResponse>(`/driver-instances/${id}/oauth/status`);
  },
  revokeOAuth: async (id: string): Promise<{ message: string }> => {
    return fetchApi<{ message: string }>(`/driver-instances/${id}/oauth`, {
      method: 'DELETE',
    });
  },
  // Get live state from driver (for access control, intrusion, etc.)
  getLiveState: async (id: string): Promise<Record<string, unknown> | null> => {
    try {
      return await fetchApi<Record<string, unknown>>(`/driver-instances/${id}/state`);
    } catch {
      return null;
    }
  },
};

// OAuth response types (for 3-legged OAuth flows like Brivo)
export interface OAuthAuthorizeUrlResponse {
  authorizeUrl: string;
  redirectUri: string;
  instanceId: string;
  instructions: string;
}

export interface OAuthStatusResponse {
  instanceId: string;
  authorizationStatus: 'pending' | 'authorized';
  hasRefreshToken: boolean;
  message: string;
}

// Driver log types
export type LogCategory = 'general' | 'api' | 'protocol' | 'request' | 'response';

export interface DriverLogEntry {
  id: string;
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  driverInstanceId: string;
  driverSlug: string;
  message: string;
  context?: Record<string, unknown>;
  category?: LogCategory;
}

export interface DriverLogsResponse {
  logs: DriverLogEntry[];
  total: number;
  hasMore?: boolean;
}

// Driver zone/partition types
export interface DriverZone {
  id: string;
  name: string;
  type: string;
  status: 'normal' | 'alarm' | 'triggered' | 'bypassed';
  partitionId?: number;
  bypassed?: boolean;
  alarmed?: boolean;
}

export interface DriverPartition {
  id: string;
  name: string;
  status: string;
  armState: string;
  alarmActive: boolean;
  readyToArm: boolean;
}

// Legacy driversApi - now uses driverInstancesApi
export const driversApi = driverInstancesApi;

// Rules API
export interface Rule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  priority: number;
  trigger: RuleTrigger;
  conditions: RuleCondition[];
  actions: RuleAction[];
  schedule?: RuleSchedule;
  rateLimit?: RuleRateLimit;
  stats: {
    triggerCount: number;
    lastTriggeredAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface RuleTrigger {
  type: 'event.new' | 'event.acknowledge' | 'event.start_sop' | 'event.escalate' | 'event.resolve' | 'event.sop_complete' | 'event.auto_dismiss' | 'event.sla_breach' | 'incident.created' | 'incident.closed' | 'device.offline' | 'device.online' | 'schedule.triggered';
  filters?: Record<string, unknown>;
}

export interface RuleCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'not_contains' | 'in_list' | 'not_in_list';
  value: unknown;
  logic?: 'AND' | 'OR';
}

export interface RuleAction {
  type: 'send_notification' | 'create_incident' | 'escalate_alarm' | 'assign_user' | 'execute_sop' | 'control_device' | 'call_webhook' | 'update_field';
  config: Record<string, unknown>;
}

export interface RuleSchedule {
  activeHours?: { start: string; end: string };
  activeDays?: number[];
  timezone?: string;
}

export interface RuleRateLimit {
  maxExecutions: number;
  windowMinutes: number;
  cooldownMinutes?: number;
}

export interface CreateRuleDto {
  name: string;
  description: string;
  priority?: number;
  trigger: RuleTrigger;
  conditions: RuleCondition[];
  actions: RuleAction[];
  schedule?: RuleSchedule;
  rateLimit?: RuleRateLimit;
}

export interface UpdateRuleDto extends Partial<CreateRuleDto> {
  enabled?: boolean;
}

export const rulesApi = {
  list: () => fetchApi<Rule[]>('/rules'),
  get: (id: string) => fetchApi<Rule>(`/rules/${id}`),
  create: (data: CreateRuleDto) =>
    fetchApi<Rule>('/rules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateRuleDto) =>
    fetchApi<Rule>(`/rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/rules/${id}`, {
      method: 'DELETE',
    }),
  enable: (id: string) =>
    fetchApi<Rule>(`/rules/${id}/enable`, {
      method: 'POST',
    }),
  disable: (id: string) =>
    fetchApi<Rule>(`/rules/${id}/disable`, {
      method: 'POST',
    }),
  test: (id: string, testEvent: Record<string, unknown>) =>
    fetchApi<{ matched: boolean; actions: string[] }>(`/rules/${id}/test`, {
      method: 'POST',
      body: JSON.stringify(testEvent),
    }),
};

// SOPs API
export interface SOP {
  id: string;
  code: string;
  name: string;
  description: string;
  version: string;
  category: 'alarm_response' | 'access_control' | 'emergency' | 'maintenance' | 'visitor' | 'patrol' | 'investigation';
  status: 'draft' | 'active' | 'archived';
  steps: SOPStep[];
  triggers: SOPTrigger[];
  stats: {
    executionCount: number;
    avgCompletionTime?: number;
  };
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface SOPStep {
  id: string;
  order: number;
  title: string;
  description: string;
  instructions?: string; // Extended instructions for complex steps
  type: 'instruction' | 'confirmation' | 'input' | 'selection' | 'checklist' | 'verification' | 'device_control' | 'notification' | 'approval' | 'documentation';
  required: boolean;
  timeout?: number;
  escalation?: {
    action: 'notify_supervisor' | 'escalate_incident' | 'auto_complete';
    config?: Record<string, unknown>;
  };
  options?: SOPStepOption[];
  inputConfig?: {
    type: 'text' | 'textarea' | 'number' | 'date' | 'file';
    validation?: string;
  };
  checklistItems?: string[];
}

export interface SOPStepOption {
  label: string;
  value: string;
  nextStep?: number;
}

export interface SOPTrigger {
  type: 'alarm_type' | 'alarm_priority' | 'incident_type' | 'device_event' | 'manual';
  value: string;
}

export interface CreateSOPDto {
  code: string;
  name: string;
  description: string;
  category: SOP['category'];
  steps: Omit<SOPStep, 'id'>[];
  triggers?: SOPTrigger[];
}

export interface UpdateSOPDto extends Partial<CreateSOPDto> {
  status?: SOP['status'];
}

// SOP get cache: SOPs change rarely during a shift but the Incident Command
// panel re-fetches on every incident-select. A 5-min TTL keyed by id removes
// the redundant network round-trips. Mutations invalidate by id.
const SOP_CACHE_TTL_MS = 5 * 60 * 1000;
const sopGetCache = new Map<string, { at: number; data: SOP }>();
const sopGetInflight = new Map<string, Promise<SOP>>();
function invalidateSopCache(id?: string) {
  if (id) sopGetCache.delete(id); else sopGetCache.clear();
}

export const sopsApi = {
  list: () => fetchApi<SOP[]>('/sops'),
  get: (id: string): Promise<SOP> => {
    const hit = sopGetCache.get(id);
    if (hit && Date.now() - hit.at < SOP_CACHE_TTL_MS) {
      return Promise.resolve(hit.data);
    }
    // Coalesce concurrent requests for the same id so a fast-clicking
    // operator doesn't spawn N parallel /sops/{id} calls.
    const flight = sopGetInflight.get(id);
    if (flight) return flight;
    const p = fetchApi<SOP>(`/sops/${id}`)
      .then((data) => {
        sopGetCache.set(id, { at: Date.now(), data });
        return data;
      })
      .finally(() => { sopGetInflight.delete(id); });
    sopGetInflight.set(id, p);
    return p;
  },
  invalidateCache: invalidateSopCache,
  create: (data: CreateSOPDto) =>
    fetchApi<SOP>('/sops', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateSOPDto) =>
    fetchApi<SOP>(`/sops/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }).then((res) => { invalidateSopCache(id); return res; }),
  delete: (id: string) =>
    fetchApi<void>(`/sops/${id}`, {
      method: 'DELETE',
    }).then((res) => { invalidateSopCache(id); return res; }),
  publish: (id: string) =>
    fetchApi<SOP>(`/sops/${id}/publish`, {
      method: 'POST',
    }).then((res) => { invalidateSopCache(id); return res; }),
  archive: (id: string) =>
    fetchApi<SOP>(`/sops/${id}/archive`, {
      method: 'POST',
    }).then((res) => { invalidateSopCache(id); return res; }),
  duplicate: (id: string) =>
    fetchApi<SOP>(`/sops/${id}/duplicate`, {
      method: 'POST',
    }),
};

// Events/Incidents API
export interface EventStats {
  total: number;
  byHandlingState: Record<string, number>;
  bySeverity: Record<string, number>;
  unhandledCount: number;
}

export interface DeviceEventRecord {
  id: string;
  tenantId: string;
  deviceId: string;
  eventType: string;
  eventCode: string;
  severity: string;
  status: string;
  title: string;
  description: string | null;
  data: Record<string, unknown>;
  sourceTimestamp: string;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  relatedEventIds: string[];
  createdAt: string;
  updatedAt: string;
}

export const eventsApi = {
  getStats: () => fetchApi<EventStats>('/events/stats/summary'),
  list: (params?: Record<string, unknown>) => {
    const query = params ? new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString() : '';
    return fetchApi<{ items: unknown[]; total: number }>(`/events${query ? `?${query}` : ''}`);
  },
  /** Fetch events by an array of IDs from the database */
  fetchByIds: (ids: string[]) =>
    fetchApi<DeviceEventRecord[]>('/events/batch', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
};

// Settings API
export interface SystemSettings {
  general: {
    systemName: string;
    timezone: string;
    dateFormat: string;
    sessionTimeout: number;
  };
  alarms: {
    autoAcknowledgeMinutes: number;
    escalationIntervals: number[];
    priorityColors: Record<string, string>;
    soundEnabled: boolean;
  };
  notifications: {
    emailEnabled: boolean;
    smsEnabled: boolean;
    pushEnabled: boolean;
    defaultRecipients: string[];
  };
  security: {
    passwordMinLength: number;
    passwordRequireUppercase: boolean;
    passwordRequireNumbers: boolean;
    passwordRequireSymbols: boolean;
    maxLoginAttempts: number;
    lockoutDuration: number;
  };
}

export const settingsApi = {
  get: () => fetchApi<Record<string, unknown>>('/settings'),
  update: (section: string, data: Record<string, unknown>) =>
    fetchApi<Record<string, unknown>>('/settings', {
      method: 'PATCH',
      body: JSON.stringify({ section, data }),
    }),
};

// User Preferences API (per-user settings with JSONB ui_settings)
export const userPreferencesApi = {
  get: () => fetchApi<Record<string, unknown>>('/user-preferences'),
  update: (data: Record<string, unknown>) =>
    fetchApi<Record<string, unknown>>('/user-preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  // The backend exposes GET/PUT /user-preferences (no /ui-settings sub-route).
  // GET returns { language, theme, uiSettings, … }; the UI store's _initFromApi
  // reads ui settings as a flat map, so fold `language` into the returned object.
  getUiSettings: async (): Promise<Record<string, unknown>> => {
    const res = await fetchApi<{ language?: string; uiSettings?: Record<string, unknown> }>('/user-preferences');
    return { ...(res.uiSettings ?? {}), ...(res.language ? { language: res.language } : {}) };
  },
  // Persist via PUT { uiSettings: {...} } (the only mutation the backend accepts).
  updateUiSettings: (data: Record<string, unknown>) =>
    fetchApi<Record<string, unknown>>('/user-preferences', {
      method: 'PUT',
      body: JSON.stringify({ uiSettings: data }),
    }),
};

// Devices API
export interface Device {
  id: string;
  name: string;
  category: 'camera' | 'access_control' | 'intrusion' | 'fire' | 'intercom' | 'sensor' | 'encoder' | 'nvr' | 'controller' | 'other';
  status: 'online' | 'offline' | 'warning' | 'error' | 'maintenance' | 'unknown';
  driverInstanceId?: string;
  channel?: number; // Driver channel number for cameras (1-based)
  ipAddress?: string;
  macAddress?: string;
  serialNumber?: string;
  siteId?: string;
  buildingId?: string;
  floorId?: string;
  zoneId?: string;
  latitude?: number;
  longitude?: number;
  config?: Record<string, unknown>;
  tags?: string[];
  // Object tree fields
  objectType?: string;       // Driver-namespaced type (e.g., "risco-lightsys:zone")
  coreType?: string;         // Semantic type (e.g., "zone", "camera", "door")
  parentDeviceId?: string;   // UUID of parent device
  capabilities?: string[];   // What this device can do (e.g., ["alarm", "tamper", "bypass"])
  activeStates?: string[];   // Current active state flags
  stateProperties?: Record<string, boolean | string>;
  stateUpdatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeviceTreeNode extends Device {
  children: Device[];
}

export interface CreateDeviceDto {
  name: string;
  description?: string;
  category: Device['category'];
  driverInstanceId?: string;
  channel?: number;
  ipAddress?: string;
  macAddress?: string;
  serialNumber?: string;
  siteId?: string;
  buildingId?: string;
  floorId?: string;
  zoneId?: string;
  latitude?: number;
  longitude?: number;
  config?: Record<string, unknown>;
  tags?: string[];
}

export interface UpdateDeviceDto extends Partial<CreateDeviceDto> {
  status?: Device['status'];
}

export interface AvailableDriver {
  slug: string;
  name: string;
  version: string;
  description: string;
  vendor: string;
  category: string;
  capabilities: string[];
  configSchema?: Record<string, unknown>;
  runtime?: 'node' | 'python' | 'go' | 'java';
  categoryMeta?: { label: string; icon: string; color: string; bg: string };
}

export interface AvailableDriversResponse {
  success: boolean;
  data: AvailableDriver[];
  byVendor: Record<string, AvailableDriver[]>;
}

// Event type from driver manifest
export interface DriverEventType {
  eventCode: string;
  eventType: string;
  name?: string;
  description?: string;
  defaultSeverity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
  defaultActionType?: 'notification' | 'auto_ack' | 'ack' | 'sop';
  icon?: string;
  color?: string;
  driverSlug: string;
  driverName: string;
  category: string;
}

export interface EventTypesResponse {
  success: boolean;
  data: DriverEventType[];
  byCategory: Record<string, DriverEventType[]>;
  byDriver: Record<string, { driverName: string; eventTypes: DriverEventType[] }>;
}

// Event types API
export const eventTypesApi = {
  getAll: async (refresh = false): Promise<EventTypesResponse> => {
    const url = refresh
      ? `${getApiBase()}/drivers/event-types?refresh=true`
      : `${getApiBase()}/drivers/event-types`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch event types');
    }
    return response.json();
  },
};

export const devicesApi = {
  list: async (filters?: {
    category?: string; status?: string; search?: string;
    coreType?: string; objectType?: string; parentDeviceId?: string;
    driverInstanceId?: string; rootOnly?: boolean;
  }): Promise<Device[]> => {
    const params = new URLSearchParams();
    params.append('limit', '10000');
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.coreType) params.append('coreType', filters.coreType);
    if (filters?.objectType) params.append('objectType', filters.objectType);
    if (filters?.parentDeviceId) params.append('parentDeviceId', filters.parentDeviceId);
    if (filters?.driverInstanceId) params.append('driverInstanceId', filters.driverInstanceId);
    if (filters?.rootOnly) params.append('rootOnly', 'true');
    const query = params.toString();
    const response = await fetchApi<PaginatedResponse<Device>>(`/devices${query ? `?${query}` : ''}`);
    // Handle both paginated response and direct array response
    return response?.items ?? (Array.isArray(response) ? response : []);
  },
  get: (id: string) => fetchApi<Device>(`/devices/${id}`),
  getChildren: (parentId: string) =>
    fetchApi<Device[]>(`/devices/${parentId}/children`),
  getTree: () =>
    fetchApi<Record<string, DeviceTreeNode[]>>('/devices/tree/hierarchy'),
  create: (data: CreateDeviceDto) =>
    fetchApi<Device>('/devices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateDeviceDto) =>
    fetchApi<Device>(`/devices/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/devices/${id}`, {
      method: 'DELETE',
    }),
  updateStatus: (id: string, status: Device['status']) =>
    fetchApi<Device>(`/devices/${id}/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }),
  getStats: () =>
    fetchApi<{
      total: number;
      byStatus: Record<string, number>;
      byCategory: Record<string, number>;
    }>('/devices/stats/summary'),
  executeCommand: (deviceId: string, command: { command: string; params?: Record<string, unknown> }) =>
    fetchApi(`/devices/${deviceId}/commands`, {
      method: 'POST',
      body: JSON.stringify(command),
    }),
  getDataPoints: (deviceId: string) =>
    fetchApi<DataPoint[]>(`/devices/${deviceId}/data-points`),
  getAvailableDrivers: async (): Promise<AvailableDriversResponse> => {
    // Use driver-instances/available to get Go drivers with runtime field
    const response = await fetch(`${getApiBase()}/drivers/available`);
    if (!response.ok) {
      throw new Error('Failed to fetch available drivers');
    }
    return response.json();
  },
};

// Data Point interface (for OPC, Modbus, BACnet, SNMP devices)
export interface DataPoint {
  id: string;
  deviceId: string;
  pointId: string;
  name?: string;
  dataType?: string;
  value?: string;
  numericValue?: number;
  unit?: string;
  quality?: string;
  sourceTimestamp?: string;
  updatedAt?: string;
  metadata?: Record<string, unknown>;
}

// Streams API for Video Wall
export interface Camera {
  id: string;
  deviceId?: string; // Database device UUID (if available, for hardware-independent streaming)
  cameraId?: string; // Driver camera ID (for UUID-based drivers like Lumana, NX Witness)
  instanceId: string;
  channel: number;
  name: string;
  enabled: boolean;
}

export interface StreamSession {
  sessionId: string;
  hlsUrl: string;
  webrtcUrl: string;
  mp4Url?: string;
  livekitUrl?: string;
  livekitToken?: string;
  nativeStream?: boolean;
  token?: string;
}

export interface StreamStatus {
  sessionId: string;
  instanceId: string;
  channel: number;
  streamType: 'main' | 'sub';
  status: 'starting' | 'running' | 'error' | 'stopped';
  startedAt: string;
  error?: string;
}

export const streamsApi = {
  getCameras: async (instanceId: string): Promise<{ cameras: Camera[] }> => {
    const result = await fetchApi<{ cameras: Camera[] }>(`/streams/cameras/${instanceId}`);
    return result;
  },

  startStream: async (instanceId: string, channel: number, streamType: 'main' | 'sub' = 'sub'): Promise<StreamSession> => {
    const result = await fetchApi<StreamSession>('/streams/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instanceId, channel, streamType }),
    });
    return result;
  },

  // Start stream by device UUID (preferred - hardware-independent)
  startStreamByDevice: async (deviceId: string, streamType: 'main' | 'sub' = 'sub'): Promise<StreamSession & { deviceId: string; deviceName: string; channel: number }> => {
    console.log('[API] startStreamByDevice called with deviceId:', deviceId, 'streamType:', streamType);
    const requestBody = { deviceId, streamType };
    console.log('[API] Request body:', JSON.stringify(requestBody));
    const result = await fetchApi<StreamSession & { deviceId: string; deviceName: string; channel: number }>('/streams/start-by-device', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    console.log('[API] startStreamByDevice response:', JSON.stringify(result));
    return result;
  },

  stopStream: (sessionId: string) =>
    fetchApi<{ success: boolean }>(`/streams/${sessionId}/stop`, {
      method: 'POST',
    }),

  // Keep a watched LIVE stream alive (heartbeat). Returns {alive:false} if the
  // server no longer has the session (caller should re-start the stream).
  keepaliveStream: (sessionId: string) =>
    fetchApi<{ alive: boolean }>(`/streams/${sessionId}/keepalive`, { method: 'POST' }),

  // Release this viewer's hold on a live stream (on unmount / stop watching).
  releaseStream: (sessionId: string) =>
    fetchApi<{ released: boolean }>(`/streams/${sessionId}/release`, { method: 'POST' }),

  getStreamStatus: (sessionId: string) =>
    fetchApi<StreamStatus>(`/streams/${sessionId}/status`),

  listStreams: () =>
    fetchApi<{ streams: StreamStatus[] }>('/streams'),

  // Get detailed stream service status (for diagnostics)
  getServiceStatus: () =>
    fetchApi<{
      go2rtc: { connected: boolean; activeStreams: number };
      proxy: {
        sessions: number;
        sessionDetails: Array<{
          id: string;
          instanceId: string;
          channel: number;
          streamType: string;
          streamName: string;
          status: string;
          error?: string;
          startedAt: string;
          lastAccess: string;
        }>;
      };
    }>('/streams/status'),

  // Get snapshot directly from camera/NVR (returns blob URL)
  getSnapshot: async (instanceId: string, channel: number, cameraName: string): Promise<void> => {
    const response = await fetch(`${getApiBase()}/streams/snapshot/${instanceId}/${channel}`);
    if (!response.ok) {
      throw new Error('Failed to get snapshot');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${cameraName}_${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`;
    link.click();
    URL.revokeObjectURL(url);
  },
};

// ============================================
// RECORDING & PLAYBACK API
// ============================================

export type RecordingType = 'continuous' | 'motion' | 'alarm' | 'manual' | 'event' | 'pre_alarm' | 'post_alarm';

export interface RecordingSegment {
  channelId: number;
  startTime: string;
  endTime: string;
  duration: number;
  type: RecordingType;
  fileSize?: number;
  hasAudio?: boolean;
  resolution?: { width: number; height: number };
}

export interface RecordingCalendar {
  channelId: number;
  year: number;
  month: number;
  daysWithRecordings: number[];
}

export interface PlaybackSession {
  sessionId: string;
  hlsUrl: string;
  webrtcUrl: string;
  mp4Url?: string;
  deviceId: string;
  channel: number;
  startTime: string;
  endTime?: string;
  token?: string;
}

// Playback control types
export type PlaybackAction = 'play' | 'pause' | 'seek' | 'seekRelative' | 'setSpeed' | 'jumpToStart' | 'jumpToLive';

export interface PlaybackCommand {
  action: PlaybackAction;
  seekTime?: string;     // ISO datetime for 'seek'
  seekOffset?: number;   // Seconds for 'seekRelative' (negative = backward)
  speed?: number;        // 0.25 to 16 for 'setSpeed'
}

export interface PlaybackStatus {
  sessionId: string;
  channelId: number;
  currentTime: string;   // ISO datetime
  bufferStart: string;   // ISO datetime
  bufferEnd: string;     // ISO datetime
  speed: number;
  paused: boolean;
  isLive: boolean;
  duration: number;      // seconds
  // Stream restart fields (stateless RTSP seek)
  streamRestarted?: boolean;
  hlsUrl?: string;
  webrtcUrl?: string;
  newStreamUrl?: string;
  requiresStreamRestart?: boolean;
}

export const recordingsApi = {
  // Search for recordings within a time range
  searchRecordings: async (
    deviceId: string,
    startTime: Date,
    endTime: Date,
    options?: {
      types?: RecordingType[];
      hasEvents?: boolean;
      minDuration?: number;
    }
  ): Promise<{ deviceId: string; channel: number; recordings: RecordingSegment[] }> => {
    const result = await fetchApi<{ deviceId: string; channel: number; recordings: RecordingSegment[] }>('/recordings/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        ...options,
      }),
    });
    return result;
  },

  // Get recording calendar (which days have recordings)
  getCalendar: async (
    deviceId: string,
    year: number,
    month: number
  ): Promise<RecordingCalendar> => {
    const params = new URLSearchParams({
      deviceId,
      year: year.toString(),
      month: month.toString(),
    });
    const result = await fetchApi<RecordingCalendar>(`/recordings/calendar?${params}`);
    return result;
  },

  // Get playback RTSP URL (for direct playback)
  getPlaybackUrl: async (
    deviceId: string,
    startTime: Date,
    endTime?: Date,
    streamType: 'main' | 'sub' = 'sub'
  ): Promise<{ deviceId: string; channel: number; rtspUrl: string; startTime: string; endTime?: string }> => {
    const result = await fetchApi<{ deviceId: string; channel: number; rtspUrl: string; startTime: string; endTime?: string }>('/recordings/playback-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceId,
        startTime: startTime.toISOString(),
        endTime: endTime?.toISOString(),
        streamType,
      }),
    });
    return result;
  },

  // Start playback stream (proxied through go2rtc)
  startPlayback: async (
    deviceId: string,
    startTime: Date,
    endTime?: Date,
    streamType: 'main' | 'sub' = 'sub'
  ): Promise<PlaybackSession> => {
    const result = await fetchApi<PlaybackSession>('/recordings/playback/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceId,
        startTime: startTime.toISOString(),
        endTime: endTime?.toISOString(),
        streamType,
      }),
    });
    return result;
  },

  // Stop playback stream
  stopPlayback: (sessionId: string) =>
    fetchApi<{ success: boolean }>(`/recordings/playback/${sessionId}/stop`, {
      method: 'POST',
    }),

  // Get download URL for recorded video
  getDownloadUrl: async (
    deviceId: string,
    startTime: Date,
    endTime: Date,
    format: 'mp4' | 'avi' | 'mkv' = 'mp4'
  ): Promise<{ deviceId: string; channel: number; downloadUrl: string; startTime: string; endTime: string; format: string }> => {
    const result = await fetchApi<{ deviceId: string; channel: number; downloadUrl: string; startTime: string; endTime: string; format: string }>('/recordings/download-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        format,
      }),
    });
    return result;
  },

  // Export recording (download via proxy)
  exportRecording: async (
    deviceId: string,
    startTime: Date,
    endTime: Date,
    format: 'mp4' | 'avi' | 'mkv' = 'mp4'
  ): Promise<void> => {
    const params = new URLSearchParams({ format });
    const url = `${getApiBase()}/recordings/export/${deviceId}/${startTime.toISOString()}/${endTime.toISOString()}?${params}`;

    // Open in new tab for download
    window.open(url, '_blank');
  },

  // ============================================
  // PLAYBACK CONTROL (driver-controlled)
  // ============================================

  // Send playback control command (play, pause, seek, speed, etc.)
  controlPlayback: async (
    sessionId: string,
    command: PlaybackCommand
  ): Promise<PlaybackStatus> => {
    const result = await fetchApi<PlaybackStatus>(`/recordings/playback/${sessionId}/control`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(command),
    });
    return result;
  },

  // Get current playback status
  getPlaybackStatus: async (sessionId: string): Promise<PlaybackStatus> => {
    console.log('[API] getPlaybackStatus called for session:', sessionId);
    try {
      const result = await fetchApi<PlaybackStatus>(`/recordings/playback/${sessionId}/status`);
      console.log('[API] getPlaybackStatus response:', result);
      return result;
    } catch (error) {
      console.error('[API] getPlaybackStatus ERROR:', error);
      throw error;
    }
  },

  // Convenience methods for common playback actions
  play: (sessionId: string) =>
    recordingsApi.controlPlayback(sessionId, { action: 'play' }),

  pause: (sessionId: string) =>
    recordingsApi.controlPlayback(sessionId, { action: 'pause' }),

  seek: (sessionId: string, time: Date) =>
    recordingsApi.controlPlayback(sessionId, { action: 'seek', seekTime: time.toISOString() }),

  seekRelative: (sessionId: string, offsetSeconds: number) =>
    recordingsApi.controlPlayback(sessionId, { action: 'seekRelative', seekOffset: offsetSeconds }),

  setSpeed: (sessionId: string, speed: number) =>
    recordingsApi.controlPlayback(sessionId, { action: 'setSpeed', speed }),

  jumpToStart: (sessionId: string) =>
    recordingsApi.controlPlayback(sessionId, { action: 'jumpToStart' }),

  jumpToLive: (sessionId: string) =>
    recordingsApi.controlPlayback(sessionId, { action: 'jumpToLive' }),
};

// ============================================
// PTZ CONTROL API
// ============================================

export interface PTZCapabilities {
  pan: boolean;
  tilt: boolean;
  zoom: boolean;
  absolutePosition: boolean;
  relativePosition: boolean;
  continuousMove: boolean;
  presets: boolean;
  tours: boolean;
  home: boolean;
  maxPresets?: number;
}

export interface PTZPosition {
  pan: number;
  tilt: number;
  zoom: number;
}

export interface PTZPreset {
  id: string;
  name: string;
  position: PTZPosition;
  thumbnailUrl?: string;
}

export interface PTZMoveParams {
  pan?: number;
  tilt?: number;
  zoom?: number;
  speed?: number;
}

export const ptzApi = {
  // Get PTZ capabilities for a device
  getCapabilities: (deviceId: string) =>
    fetchApi<{ capabilities: PTZCapabilities | null }>(`/devices/${deviceId}/ptz/capabilities`),

  // Get current PTZ position
  getPosition: (deviceId: string) =>
    fetchApi<{ position: PTZPosition | null }>(`/devices/${deviceId}/ptz/position`),

  // Move PTZ (continuous movement)
  move: (deviceId: string, params: PTZMoveParams) =>
    fetchApi<{ success: boolean; error?: string }>(`/devices/${deviceId}/ptz/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    }),

  // Stop PTZ movement
  stop: (deviceId: string) =>
    fetchApi<{ success: boolean; error?: string }>(`/devices/${deviceId}/ptz/stop`, {
      method: 'POST',
    }),

  // Go to home position
  home: (deviceId: string) =>
    fetchApi<{ success: boolean; error?: string }>(`/devices/${deviceId}/ptz/home`, {
      method: 'POST',
    }),

  // Get all presets
  getPresets: (deviceId: string) =>
    fetchApi<{ presets: PTZPreset[] }>(`/devices/${deviceId}/ptz/presets`),

  // Go to a preset
  gotoPreset: (deviceId: string, presetId: string) =>
    fetchApi<{ success: boolean; error?: string }>(`/devices/${deviceId}/ptz/goto-preset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ presetId }),
    }),

  // Create a new preset at current position
  createPreset: (deviceId: string, name: string) =>
    fetchApi<{ preset: PTZPreset }>(`/devices/${deviceId}/ptz/presets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }),

  // Delete a preset
  deletePreset: (deviceId: string, presetId: string) =>
    fetchApi<{ success: boolean; error?: string }>(`/devices/${deviceId}/ptz/presets/${presetId}`, {
      method: 'DELETE',
    }),
};

// ============================================
// LOCATION HIERARCHY API
// ============================================

// Organization interface
export interface Organization {
  id: string;
  tenantId: string;
  name: string;
  parentId: string | null;
  path: string;
  level: number;
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrganizationDto {
  name: string;
  parentId?: string | null;
  settings?: Record<string, unknown>;
}

export interface UpdateOrganizationDto {
  name?: string;
  parentId?: string | null;
  settings?: Record<string, unknown>;
}

export const organizationsApi = {
  list: () => fetchApi<Organization[]>('/organizations'),
  get: (id: string) => fetchApi<Organization>(`/organizations/${id}`),
  create: (data: CreateOrganizationDto) =>
    fetchApi<Organization>('/organizations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateOrganizationDto) =>
    fetchApi<Organization>(`/organizations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/organizations/${id}`, {
      method: 'DELETE',
    }),
};

// Site interface
export interface Site {
  id: string;
  tenantId: string;
  organizationId: string;
  name: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSiteDto {
  name: string;
  organizationId: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateSiteDto {
  name?: string;
  organizationId?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  metadata?: Record<string, unknown>;
}

export const sitesApi = {
  list: (organizationId?: string) => {
    const params = organizationId ? `?organizationId=${organizationId}` : '';
    return fetchApi<Site[]>(`/sites${params}`);
  },
  get: (id: string) => fetchApi<Site>(`/sites/${id}`),
  create: (data: CreateSiteDto) =>
    fetchApi<Site>('/sites', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateSiteDto) =>
    fetchApi<Site>(`/sites/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/sites/${id}`, {
      method: 'DELETE',
    }),
};

// Building interface
export interface Building {
  id: string;
  tenantId: string;
  siteId: string;
  name: string;
  address?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBuildingDto {
  name: string;
  siteId: string;
  address?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateBuildingDto {
  name?: string;
  siteId?: string;
  address?: string;
  metadata?: Record<string, unknown>;
}

export const buildingsApi = {
  list: (siteId?: string) => {
    const params = siteId ? `?siteId=${siteId}` : '';
    return fetchApi<Building[]>(`/buildings${params}`);
  },
  get: (id: string) => fetchApi<Building>(`/buildings/${id}`),
  create: (data: CreateBuildingDto) =>
    fetchApi<Building>('/buildings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateBuildingDto) =>
    fetchApi<Building>(`/buildings/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/buildings/${id}`, {
      method: 'DELETE',
    }),
};

// Floor interface
export interface Floor {
  id: string;
  tenantId: string;
  buildingId: string;
  name: string;
  level: number;
  floorPlanUrl?: string;
  width?: number;
  height?: number;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFloorDto {
  name: string;
  buildingId: string;
  level: number;
  floorPlanUrl?: string;
  width?: number;
  height?: number;
  metadata?: Record<string, unknown>;
}

export interface UpdateFloorDto {
  name?: string;
  buildingId?: string;
  level?: number;
  floorPlanUrl?: string;
  width?: number;
  height?: number;
  metadata?: Record<string, unknown>;
}

export const floorsApi = {
  list: (buildingId?: string) => {
    const params = buildingId ? `?buildingId=${buildingId}` : '';
    return fetchApi<Floor[]>(`/floors${params}`);
  },
  get: (id: string) => fetchApi<Floor>(`/floors/${id}`),
  create: (data: CreateFloorDto) =>
    fetchApi<Floor>('/floors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateFloorDto) =>
    fetchApi<Floor>(`/floors/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/floors/${id}`, {
      method: 'DELETE',
    }),
  // Floor plan data methods
  getFloorPlan: (id: string) =>
    fetchApi<unknown>(`/floors/${id}/floor-plan`),
  saveFloorPlan: (id: string, data: unknown) =>
    fetchApi<Floor>(`/floors/${id}/floor-plan`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
};

// Area (Zone) interface
export interface Area {
  id: string;
  tenantId: string;
  floorId: string;
  name: string;
  type: string;
  polygon: Array<{ x: number; y: number }>;
  color?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAreaDto {
  name: string;
  floorId: string;
  type?: string;
  polygon?: Array<{ x: number; y: number }>;
  color?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateAreaDto {
  name?: string;
  floorId?: string;
  type?: string;
  polygon?: Array<{ x: number; y: number }>;
  color?: string;
  metadata?: Record<string, unknown>;
}

export const areasApi = {
  list: (floorId?: string) => {
    const params = floorId ? `?floorId=${floorId}` : '';
    return fetchApi<Area[]>(`/areas${params}`);
  },
  get: (id: string) => fetchApi<Area>(`/areas/${id}`),
  create: (data: CreateAreaDto) =>
    fetchApi<Area>('/areas', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateAreaDto) =>
    fetchApi<Area>(`/areas/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/areas/${id}`, {
      method: 'DELETE',
    }),
};

// User Group interface
export interface UserGroup {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  color?: string;
  permissions: string[];
  modules: string[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface GroupMember {
  userId: string;
  role: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface GroupLocationAccess {
  id: string;
  groupId: string;
  locationType: 'organization' | 'site' | 'building' | 'floor' | 'area';
  locationId: string;
  accessLevel: 'view' | 'control' | 'manage' | 'admin';
  includeChildren: boolean;
  createdAt: string;
}

export interface UserGroupWithMembers extends UserGroup {
  members: GroupMember[];
  locationAccess?: GroupLocationAccess[];
}

export interface CreateGroupDto {
  name: string;
  description?: string;
  color?: string;
  permissions?: string[];
  modules?: string[];
  metadata?: Record<string, unknown>;
}

export interface UpdateGroupDto {
  name?: string;
  description?: string;
  color?: string;
  permissions?: string[];
  modules?: string[];
  metadata?: Record<string, unknown>;
}

export const userGroupsApi = {
  list: () => fetchApi<UserGroup[]>('/groups'),
  get: (id: string) => fetchApi<UserGroupWithMembers>(`/groups/${id}`),
  create: (data: CreateGroupDto) =>
    fetchApi<UserGroup>('/groups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateGroupDto) =>
    fetchApi<UserGroup>(`/groups/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<void>(`/groups/${id}`, {
      method: 'DELETE',
    }),

  // Member management
  addMember: (groupId: string, userId: string, role?: string) =>
    fetchApi<{ groupId: string; userId: string; role: string }>(`/groups/${groupId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, role }),
    }),
  removeMember: (groupId: string, userId: string) =>
    fetchApi<void>(`/groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
    }),
  updateMemberRole: (groupId: string, userId: string, role: string) =>
    fetchApi<{ groupId: string; userId: string; role: string }>(`/groups/${groupId}/members/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    }),

  // Location access management
  getLocationAccess: (groupId: string) =>
    fetchApi<GroupLocationAccess[]>(`/groups/${groupId}/locations`),
  setLocationAccess: (
    groupId: string,
    locationType: string,
    locationId: string,
    accessLevel?: string,
    includeChildren?: boolean
  ) =>
    fetchApi<GroupLocationAccess>(`/groups/${groupId}/locations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locationType, locationId, accessLevel, includeChildren }),
    }),
  setMultipleLocationAccess: (
    groupId: string,
    accesses: Array<{
      locationType: string;
      locationId: string;
      accessLevel?: string;
      includeChildren?: boolean;
    }>
  ) =>
    fetchApi<GroupLocationAccess[]>(`/groups/${groupId}/locations`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accesses }),
    }),
  removeLocationAccess: (groupId: string, locationType: string, locationId: string) =>
    fetchApi<void>(`/groups/${groupId}/locations/${locationType}/${locationId}`, {
      method: 'DELETE',
    }),
};

// ============================================
// CHAT API
// ============================================

export interface ChatConversation {
  id: string;
  tenantId: string;
  type: 'direct' | 'group' | 'user_group';
  name?: string;
  description?: string;
  avatarUrl?: string;
  userGroupId?: string;
  createdBy: string;
  lastMessageId?: string;
  lastMessageAt?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ChatParticipant {
  id: string;
  conversationId: string;
  userId: string;
  role: 'owner' | 'admin' | 'member';
  nickname?: string;
  isPinned: boolean;
  pinnedAt?: string;
  isMuted: boolean;
  mutedUntil?: string;
  lastReadMessageId?: string;
  lastReadAt?: string;
  unreadCount: number;
  joinedAt: string;
  leftAt?: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  replyToId?: string;
  messageType: 'text' | 'image' | 'video' | 'audio' | 'file' | 'system' | 'location';
  content?: string;
  metadata: Record<string, unknown>;
  isEdited: boolean;
  editedAt?: string;
  isDeleted: boolean;
  deletedAt?: string;
  createdAt: string;
  sender?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  attachments?: ChatAttachment[];
  reactions?: ChatReaction[];
  replyTo?: ChatMessage;
  readBy?: string[];
}

export interface ChatAttachment {
  id: string;
  messageId: string;
  fileType: 'image' | 'video' | 'audio' | 'document';
  fileName: string;
  fileSize: number;
  mimeType: string;
  storagePath: string;
  thumbnailPath?: string;
  thumbnailUrl?: string; // Signed URL for thumbnail
  width?: number;
  height?: number;
  duration?: number;
  metadata: Record<string, unknown>;
  createdAt: string;
  url?: string; // Signed URL from storage
}

export interface ChatReaction {
  id: string;
  messageId: string;
  userId: string;
  emoji: string;
  createdAt: string;
}

export interface ConversationWithDetails extends ChatConversation {
  participants: ChatParticipant[];
  lastMessage?: ChatMessage;
  unreadCount?: number;
  isPinned?: boolean;
  isMuted?: boolean;
}

export interface CreateConversationDto {
  type: 'direct' | 'group' | 'user_group';
  name?: string;
  description?: string;
  avatarUrl?: string;
  userGroupId?: string;
  participantIds: string[];
  metadata?: Record<string, unknown>;
}

export interface UpdateConversationDto {
  name?: string;
  description?: string;
  avatarUrl?: string | null;
}

export interface SendMessageDto {
  content?: string;
  messageType?: 'text' | 'image' | 'video' | 'audio' | 'file' | 'system' | 'location';
  replyToId?: string;
  attachmentIds?: string[];
  metadata?: Record<string, unknown>;
}

export interface MessageQueryOptions {
  cursor?: string;
  limit?: number;
}

export const chatApi = {
  // Conversations
  listConversations: () => fetchApi<ConversationWithDetails[]>('/chat/conversations'),

  getConversation: (id: string) =>
    fetchApi<ConversationWithDetails>(`/chat/conversations/${id}`),

  createConversation: (data: CreateConversationDto) =>
    fetchApi<ConversationWithDetails>('/chat/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createDirectConversation: (userId: string) =>
    fetchApi<ConversationWithDetails>(`/chat/conversations/direct/${userId}`, {
      method: 'POST',
    }),

  createFromUserGroup: (groupId: string) =>
    fetchApi<ConversationWithDetails>(`/chat/conversations/from-group/${groupId}`, {
      method: 'POST',
    }),

  updateConversation: (id: string, data: UpdateConversationDto) =>
    fetchApi<ChatConversation>(`/chat/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  leaveConversation: (id: string) =>
    fetchApi<void>(`/chat/conversations/${id}`, {
      method: 'DELETE',
    }),

  // Participants
  addParticipant: (conversationId: string, userId: string, role?: 'admin' | 'member') =>
    fetchApi<ChatParticipant>(`/chat/conversations/${conversationId}/participants`, {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    }),

  removeParticipant: (conversationId: string, userId: string) =>
    fetchApi<void>(`/chat/conversations/${conversationId}/participants/${userId}`, {
      method: 'DELETE',
    }),

  updateParticipantRole: (conversationId: string, userId: string, role: 'admin' | 'member') =>
    fetchApi<ChatParticipant>(`/chat/conversations/${conversationId}/participants/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  // Messages
  getMessages: (conversationId: string, options?: MessageQueryOptions) => {
    const params = new URLSearchParams();
    if (options?.cursor) params.append('cursor', options.cursor);
    if (options?.limit) params.append('limit', options.limit.toString());
    const query = params.toString();
    return fetchApi<ChatMessage[]>(`/chat/conversations/${conversationId}/messages${query ? `?${query}` : ''}`);
  },

  sendMessage: (conversationId: string, data: SendMessageDto) =>
    fetchApi<ChatMessage>(`/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateMessage: (messageId: string, content: string) =>
    fetchApi<ChatMessage>(`/chat/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    }),

  deleteMessage: (messageId: string) =>
    fetchApi<void>(`/chat/messages/${messageId}`, {
      method: 'DELETE',
    }),

  searchMessages: (conversationId: string, query: string, limit?: number) => {
    const params = new URLSearchParams({ query });
    if (limit) params.append('limit', limit.toString());
    return fetchApi<ChatMessage[]>(`/chat/conversations/${conversationId}/messages/search?${params}`);
  },

  // Reactions
  addReaction: (messageId: string, emoji: string) =>
    fetchApi<ChatReaction>(`/chat/messages/${messageId}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }),

  removeReaction: (messageId: string, emoji: string) =>
    fetchApi<void>(`/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`, {
      method: 'DELETE',
    }),

  // Read receipts
  markConversationRead: (conversationId: string, messageId?: string) =>
    fetchApi<{ read: boolean }>(`/chat/conversations/${conversationId}/read`, {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    }),

  // Typing indicators
  sendTyping: (conversationId: string) =>
    fetchApi<{ typing: boolean }>(`/chat/conversations/${conversationId}/typing`, {
      method: 'POST',
    }),

  stopTyping: (conversationId: string) =>
    fetchApi<{ typing: boolean }>(`/chat/conversations/${conversationId}/typing`, {
      method: 'DELETE',
    }),

  getTypingUsers: (conversationId: string) =>
    fetchApi<{ users: string[] }>(`/chat/conversations/${conversationId}/typing`),

  // Pin/Mute
  pinConversation: (conversationId: string) =>
    fetchApi<{ pinned: boolean }>(`/chat/conversations/${conversationId}/pin`, {
      method: 'POST',
    }),

  unpinConversation: (conversationId: string) =>
    fetchApi<{ pinned: boolean }>(`/chat/conversations/${conversationId}/pin`, {
      method: 'DELETE',
    }),

  muteConversation: (conversationId: string, until?: string) =>
    fetchApi<{ muted: boolean; mutedUntil?: string }>(`/chat/conversations/${conversationId}/mute`, {
      method: 'POST',
      body: JSON.stringify({ until }),
    }),

  unmuteConversation: (conversationId: string) =>
    fetchApi<{ muted: boolean }>(`/chat/conversations/${conversationId}/mute`, {
      method: 'DELETE',
    }),

  // Attachments
  getAttachment: (id: string) =>
    fetchApi<ChatAttachment & { url: string }>(`/chat/attachments/${id}`),

  uploadAttachment: async (
    conversationId: string,
    messageId: string,
    file: File
  ): Promise<ChatAttachment & { url: string }> => {
    const { getAuthHeaders } = useAuthStore.getState();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversationId', conversationId);
    formData.append('messageId', messageId);
    const response = await fetch(`${getApiBase()}/chat/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    if (!response.ok) throw new Error('Upload failed');
    const result = await response.json();
    return result.data || result;
  },

  forwardMessage: (
    sourceConversationId: string,
    messageId: string,
    targetConversationIds: string[]
  ) =>
    api.post<{ forwarded: number; messages: ChatMessage[] }>(
      `/chat/conversations/${sourceConversationId}/messages/forward`,
      { messageId, targetConversationIds }
    ),

  // Call signaling
  initiateCall: (conversationId: string, type: 'audio' | 'video') =>
    api.post<{ callId: string; participants: string[] }>(
      `/chat/conversations/${conversationId}/call`,
      { type }
    ),

  acceptCall: (callId: string) =>
    api.post<{ success: boolean }>(`/chat/calls/${callId}/accept`, {}),

  rejectCall: (callId: string) =>
    api.post<{ success: boolean }>(`/chat/calls/${callId}/reject`, {}),

  endCall: (callId: string) =>
    api.post<{ success: boolean }>(`/chat/calls/${callId}/end`, {}),

  sendSignal: (callId: string, targetUserId: string, signal: unknown) =>
    api.post<{ success: boolean }>(`/chat/calls/${callId}/signal`, {
      targetUserId,
      signal,
    }),
};

// Audit Log API
export interface AuditLog {
  id: string;
  tenantId: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  details?: { oldValues?: unknown; newValues?: unknown };
  ipAddress: string | null;
  userAgent: string | null;
  severity: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  createdAt: string;
}

export interface AuditLogFilters {
  userId?: string;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  severity?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface AuditPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface AuditStatistics {
  totalLogs: number;
  byAction: Record<string, number>;
  byResourceType: Record<string, number>;
  bySeverity: Record<string, number>;
  recentActivity: { date: string; count: number }[];
}

export const auditApi = {
  list: (params?: { page?: number; limit?: number; filters?: AuditLogFilters }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value) searchParams.append(key, String(value));
      });
    }
    const query = searchParams.toString();
    return fetchApi<{ logs: AuditLog[]; pagination: AuditPagination }>(`/audit${query ? `?${query}` : ''}`);
  },

  get: (id: string) => fetchApi<AuditLog>(`/audit/${id}`),

  getStatistics: () => fetchApi<AuditStatistics>('/audit/statistics'),

  getUserActivity: (userId: string, params?: { page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.limit) searchParams.append('limit', String(params.limit));
    const query = searchParams.toString();
    return fetchApi<AuditLog[]>(`/audit/user/${userId}${query ? `?${query}` : ''}`);
  },

  getRecentActivity: (limit: number = 20) =>
    fetchApi<{ logs: AuditLog[]; pagination: AuditPagination }>(`/audit?limit=${limit}`).then(result => result.logs),
};

// ============================================
// DASHBOARD LAYOUTS API
// ============================================

export interface DashboardLayoutConfig {
  columns?: number;
  rows?: number;
  modules?: Array<{
    id: string;
    type: string;
    title?: string;
    x: number;
    y: number;
    width: number;
    height: number;
    settings?: Record<string, unknown>;
  }>;
}

export interface DashboardLayout {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  config: DashboardLayoutConfig;
  visibility: 'private' | 'public' | 'shared';
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Added by API for display
  creatorName?: string;
  canEdit?: boolean;
}

export interface DashboardLayoutPermission {
  id: string;
  layoutId: string;
  userId?: string;
  role?: string;
  canEdit: boolean;
  createdAt: string;
}

export interface CreateDashboardLayoutDto {
  name: string;
  description?: string;
  config: DashboardLayoutConfig;
  visibility?: 'private' | 'public' | 'shared';
}

export interface UpdateDashboardLayoutDto {
  name?: string;
  description?: string;
  config?: DashboardLayoutConfig;
  visibility?: 'private' | 'public' | 'shared';
}

export interface LayoutPermissionInput {
  userId?: string;
  role?: string;
  canEdit?: boolean;
}

export const layoutsApi = {
  list: () =>
    fetchApi<{ success: boolean; data: DashboardLayout[] }>('/layouts').then(r => r.data),

  get: (id: string) =>
    fetchApi<{ success: boolean; data: DashboardLayout }>(`/layouts/${id}`).then(r => r.data),

  create: (data: CreateDashboardLayoutDto) =>
    fetchApi<{ success: boolean; data: DashboardLayout }>('/layouts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => r.data),

  update: (id: string, data: UpdateDashboardLayoutDto) =>
    fetchApi<{ success: boolean; data: DashboardLayout }>(`/layouts/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(r => r.data),

  delete: (id: string) =>
    fetchApi<void>(`/layouts/${id}`, {
      method: 'DELETE',
    }),

  clone: (id: string, name?: string) =>
    fetchApi<{ success: boolean; data: DashboardLayout }>(`/layouts/${id}/clone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(r => r.data),

  getPermissions: (id: string) =>
    fetchApi<{ success: boolean; data: DashboardLayoutPermission[] }>(`/layouts/${id}/permissions`).then(r => r.data),

  setPermissions: (id: string, permissions: LayoutPermissionInput[]) =>
    fetchApi<{ success: boolean; data: DashboardLayoutPermission[] }>(`/layouts/${id}/permissions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ permissions }),
    }).then(r => r.data),
};

// ============================================
// Event Rules API
// ============================================

export interface EventRuleCondition {
  field: string;
  operator: 'eq' | 'neq' | 'in' | 'not_in' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'matches';
  value: unknown;
  params?: Record<string, unknown>;
}

export interface EventRuleAction {
  type: 'set_severity' | 'set_action_type' | 'set_sop' | 'add_notification' | 'execute_command' | 'create_incident' | 'correlate' | 'suppress' | 'add_note' | 'play_sound' | 'transform' | 'route' | 'escalate' | 'auto_acknowledge';
  params: Record<string, unknown>;
}

export interface EventRule {
  id: string;
  name: string;
  description?: string;
  conditions: EventRuleCondition[];
  actions: EventRuleAction[];
  config?: Record<string, unknown>;
  priority: number;
  stopProcessing: boolean;
  enabled: boolean;
  schedule?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CreateEventRuleDto {
  name: string;
  description?: string;
  conditions: EventRuleCondition[];
  actions: EventRuleAction[];
  config?: Record<string, unknown>;
  priority?: number;
  stopProcessing?: boolean;
  enabled?: boolean;
  schedule?: Record<string, unknown>;
}

export interface UpdateEventRuleDto {
  name?: string;
  description?: string;
  conditions?: EventRuleCondition[];
  actions?: EventRuleAction[];
  config?: Record<string, unknown> | null;
  priority?: number;
  stopProcessing?: boolean;
  enabled?: boolean;
  schedule?: Record<string, unknown> | null;
}

export interface EventRuleTestResult {
  rule: { id: string; name: string };
  matches: boolean;
  matchedConditions: EventRuleCondition[];
  failedConditions: EventRuleCondition[];
  actionsIfMatched: EventRuleAction[];
}

export const eventRulesApi = {
  list: (params?: { page?: number; limit?: number; enabled?: boolean; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.enabled !== undefined) searchParams.set('enabled', String(params.enabled));
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return fetchApi<{ items: EventRule[]; total: number; page: number; limit: number }>(
      `/event-rules${query ? `?${query}` : ''}`
    );
  },

  getActive: () =>
    fetchApi<EventRule[]>('/event-rules/active'),

  get: (id: string) =>
    fetchApi<EventRule>(`/event-rules/${id}`),

  create: (data: CreateEventRuleDto) =>
    fetchApi<EventRule>('/event-rules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: UpdateEventRuleDto) =>
    fetchApi<EventRule>(`/event-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<void>(`/event-rules/${id}`, {
      method: 'DELETE',
    }),

  toggle: (id: string, enabled?: boolean) =>
    fetchApi<EventRule>(`/event-rules/${id}/toggle`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),

  duplicate: (id: string) =>
    fetchApi<EventRule>(`/event-rules/${id}/duplicate`, {
      method: 'POST',
    }),

  reorder: (ruleIds: string[], priorities: number[]) =>
    fetchApi<{ message: string; count: number }>('/event-rules/reorder', {
      method: 'POST',
      body: JSON.stringify({ ruleIds, priorities }),
    }),

  testRule: (id: string, event: Record<string, unknown>) =>
    fetchApi<EventRuleTestResult>(`/event-rules/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({ event }),
    }),

  testAllRules: (event: Record<string, unknown>) =>
    fetchApi<{
      rulesEvaluated: number;
      matchingRules: number;
      stoppedEarly: boolean;
      results: Array<{
        rule: { id: string; name: string; priority: number };
        matches: boolean;
        matchedConditions: number;
        failedConditions: number;
        actionsIfMatched: EventRuleAction[];
      }>;
      appliedActions: EventRuleAction[];
    }>('/event-rules/test', {
      method: 'POST',
      body: JSON.stringify({ event }),
    }),

  getStats: () =>
    fetchApi<{ total: number; enabled: number; disabled: number }>('/event-rules/stats'),

  getByEventType: (eventType: string) =>
    fetchApi<EventRule[]>(`/event-rules/by-event-type/${encodeURIComponent(eventType)}`),

  getExecutionLog: (params?: { ruleId?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.ruleId) searchParams.set('ruleId', params.ruleId);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return fetchApi<Array<{
      id: string;
      ruleId: string;
      ruleName: string;
      eventId: string;
      eventType: string;
      deviceId: string;
      matchedAt: string;
      actionsExecuted: string[];
      duration: number;
    }>>(`/event-rules/execution-log${query ? `?${query}` : ''}`);
  },
};

export const manualEventsApi = {
  create: (data: {
    title: string;
    description?: string;
    severity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
    ruleId?: string;
    ruleName?: string;
    data?: Record<string, unknown>;
    location?: { description?: string; latitude?: number; longitude?: number };
  }) =>
    fetchApi<{ eventId: string; message: string }>('/events/manual', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ============================================
// INCIDENTS API
// ============================================

export interface Incident {
  id: string;
  tenantId: string;
  incidentNumber: string;
  incidentTypeId?: string;
  type: string;
  title: string;
  description?: string;
  category: string;
  status: 'draft' | 'open' | 'in_progress' | 'escalated' | 'pending_review' | 'closed' | 'reopened';
  priority: 'p1' | 'p2' | 'p3' | 'p4' | 'p5';
  reportedBy?: string;
  reportedAt: string;
  assignedTo?: string;
  assignedAt?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  resolution?: string;
  closedBy?: string;
  closedAt?: string;
  locationId?: string;
  siteId?: string;
  buildingId?: string;
  floorId?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  locationDescription?: string;
  relatedAlarms: string[];
  relatedDevices: string[];
  evidence: unknown[];
  notes: unknown[];
  formData: Record<string, unknown>;
  sopId?: string;
  sopExecutionId?: string;
  tags: string[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateIncidentDto {
  title: string;
  description?: string;
  category: Incident['category'];
  priority: Incident['priority'];
  type?: string;
  siteId?: string;
  buildingId?: string;
  floorId?: string;
  latitude?: number;
  longitude?: number;
  locationDescription?: string;
  assignedTo?: string;
}

export interface UpdateIncidentDto {
  title?: string;
  description?: string;
  category?: Incident['category'];
  status?: Incident['status'];
  priority?: Incident['priority'];
  assignedTo?: string;
  resolution?: string;
}

// ============================================
// INCIDENT TYPES API
// ============================================

export interface IncidentTypeConfig {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  category: string;
  priority: string;
  formId?: string | null;
  sopId?: string | null;
  icon?: string;
  color?: string;
  requiresForm?: boolean;
  isActive?: boolean;
  config?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DynamicFormConfig {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  version: number;
  status: string;
  schema: unknown[];
  uiSchema?: Record<string, unknown>;
  validation?: Record<string, unknown>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface IncidentTypeWithForm extends IncidentTypeConfig {
  form: DynamicFormConfig | null;
}

export const incidentTypesApi = {
  list: async (activeOnly = false): Promise<IncidentTypeConfig[]> => {
    const params = activeOnly ? '?active=true' : '';
    return fetchApi<IncidentTypeConfig[]>(`/incident-types${params}`);
  },
  get: async (id: string): Promise<IncidentTypeConfig> => {
    return fetchApi<IncidentTypeConfig>(`/incident-types/${id}`);
  },
  create: async (data: Partial<IncidentTypeConfig>): Promise<IncidentTypeConfig> => {
    return fetchApi<IncidentTypeConfig>('/incident-types', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },
  update: async (id: string, data: Partial<IncidentTypeConfig>): Promise<IncidentTypeConfig> => {
    return fetchApi<IncidentTypeConfig>(`/incident-types/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },
  delete: async (id: string): Promise<void> => {
    await fetchApi(`/incident-types/${id}`, { method: 'DELETE' });
  },
  getWithForm: async (id: string): Promise<IncidentTypeWithForm> => {
    return fetchApi<IncidentTypeWithForm>(`/incident-types/${id}/form`);
  },
};

// Incident Categories — configurable categories for incident types
export interface IncidentCategory {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  description?: string;
  sortOrder: number;
  enabled: boolean;
}

export const incidentCategoriesApi = {
  list: async (): Promise<IncidentCategory[]> => {
    const result = await fetchApi<{ items: IncidentCategory[] }>('/incident-categories');
    return result?.items || [];
  },
  get: (id: string) => fetchApi<IncidentCategory>(`/incident-categories/${id}`),
  create: (data: Partial<IncidentCategory>) =>
    fetchApi<IncidentCategory>('/incident-categories', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<IncidentCategory>) =>
    fetchApi<IncidentCategory>(`/incident-categories/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    fetchApi<void>(`/incident-categories/${id}`, { method: 'DELETE' }),
};

// Dynamic Forms — incident form schema definitions
export const dynamicFormsApi = {
  list: async (status?: string): Promise<DynamicFormConfig[]> => {
    const params = status ? `?status=${status}` : '';
    return fetchApi<DynamicFormConfig[]>(`/dynamic-forms${params}`);
  },
  get: async (id: string): Promise<DynamicFormConfig> => {
    return fetchApi<DynamicFormConfig>(`/dynamic-forms/${id}`);
  },
  create: async (data: { name: string; description?: string; schema: unknown[] }): Promise<DynamicFormConfig> => {
    return fetchApi<DynamicFormConfig>('/dynamic-forms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },
  update: async (id: string, data: { name?: string; description?: string; schema?: unknown[]; status?: string }): Promise<DynamicFormConfig> => {
    return fetchApi<DynamicFormConfig>(`/dynamic-forms/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },
  delete: async (id: string): Promise<void> => {
    await fetchApi(`/dynamic-forms/${id}`, { method: 'DELETE' });
  },
  clone: async (id: string): Promise<DynamicFormConfig> => {
    return fetchApi<DynamicFormConfig>(`/dynamic-forms/${id}/clone`, {
      method: 'POST',
    });
  },
  listTemplates: async (): Promise<DynamicFormConfig[]> => {
    return fetchApi<DynamicFormConfig[]>('/dynamic-forms?status=published');
  },
};

// Incident Templates — manual incident creation templates (bypass rules engine)
export interface IncidentTemplateConfig {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  defaultPriority?: string | null;
  defaultCategory?: string | null;
  incidentTypeId?: string | null;
  defaultSopId?: string | null;
  assignedRole?: string | null;
  mapAnimation?: string | null;
  requiredFields?: string[] | null;
  autoActions?: Array<{
    type: string;
    integrationId?: string;
    actionName?: string;
    deviceId?: string;
    command?: string;
    parameters?: Record<string, unknown>;
  }> | null;
  enabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const incidentTemplatesApi = {
  list: async (enabledOnly = false): Promise<IncidentTemplateConfig[]> => {
    const params = enabledOnly ? '?enabled=true' : '';
    return fetchApi<IncidentTemplateConfig[]>(`/incident-templates${params}`);
  },
  get: async (id: string): Promise<IncidentTemplateConfig> => {
    return fetchApi<IncidentTemplateConfig>(`/incident-templates/${id}`);
  },
  create: async (data: Partial<IncidentTemplateConfig>): Promise<IncidentTemplateConfig> => {
    return fetchApi<IncidentTemplateConfig>('/incident-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id: string, data: Partial<IncidentTemplateConfig>): Promise<IncidentTemplateConfig> => {
    return fetchApi<IncidentTemplateConfig>(`/incident-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  delete: async (id: string): Promise<void> => {
    await fetchApi(`/incident-templates/${id}`, { method: 'DELETE' });
  },
  toggle: async (id: string): Promise<IncidentTemplateConfig> => {
    return fetchApi<IncidentTemplateConfig>(`/incident-templates/${id}/toggle`, {
      method: 'POST',
    });
  },
  createManualIncident: async (data: {
    templateId?: string;
    title: string;
    description?: string;
    priority?: string;
    category?: string;
    siteId?: string;
    buildingId?: string;
    floorId?: string;
    latitude?: number;
    longitude?: number;
    address?: string;
    sopId?: string;
    assignedTo?: string;
  }): Promise<Incident> => {
    return fetchApi<Incident>('/incident-templates/create-incident', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

export const incidentsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: Incident['status'] | Incident['status'][];
    category?: Incident['category'] | Incident['category'][];
    priority?: Incident['priority'] | Incident['priority'][];
    assignedTo?: string;
    search?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) {
      const statuses = Array.isArray(params.status) ? params.status : [params.status];
      statuses.forEach(s => searchParams.append('status', s));
    }
    if (params?.category) {
      const categories = Array.isArray(params.category) ? params.category : [params.category];
      categories.forEach(c => searchParams.append('category', c));
    }
    if (params?.priority) {
      const priorities = Array.isArray(params.priority) ? params.priority : [params.priority];
      priorities.forEach(p => searchParams.append('priority', p));
    }
    if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return fetchApi<{ items: Incident[]; total: number; page: number; limit: number; hasMore: boolean }>(
      `/incidents${query ? `?${query}` : ''}`
    );
  },

  get: (id: string) => fetchApi<Incident>(`/incidents/${id}`),

  getByNumber: (incidentNumber: string) => fetchApi<Incident>(`/incidents/number/${incidentNumber}`),

  create: (data: CreateIncidentDto) =>
    fetchApi<Incident>('/incidents', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: UpdateIncidentDto) =>
    fetchApi<Incident>(`/incidents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  updateStatus: (id: string, status: Incident['status']) =>
    fetchApi<Incident>(`/incidents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  assign: (id: string, userId: string) =>
    fetchApi<Incident>(`/incidents/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),

  close: (id: string, resolution: string) =>
    fetchApi<Incident>(`/incidents/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    }),

  reopen: (id: string) =>
    fetchApi<Incident>(`/incidents/${id}/reopen`, {
      method: 'POST',
    }),

  getStatistics: () =>
    fetchApi<{
      total: number;
      byStatus: Record<Incident['status'], number>;
      byCategory: Record<Incident['category'], number>;
      byPriority: Record<Incident['priority'], number>;
    }>('/incidents/stats/summary'),

  // Notes (adds timeline entry of type 'note')
  addNote: (id: string, content: string) =>
    fetchApi<Incident>(`/incidents/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),

  // Timeline
  getTimeline: (id: string) =>
    fetchApi<Array<{
      id: string;
      incidentId: string;
      type: string;
      timestamp: string;
      userId?: string;
      content?: string;
      data?: Record<string, unknown>;
    }>>(`/incidents/${id}/timeline`),

  // Related incidents (correlation)
  getRelated: (id: string) => fetchApi<Incident[]>(`/incidents/${id}/related`),

  // Communications log
  getCommunications: (id: string) => fetchApi<any[]>(`/incidents/${id}/communications`),

  // Evidence
  getEvidence: (id: string) =>
    fetchApi<Array<{
      id: string;
      incidentId: string;
      type: string;
      filename?: string;
      url?: string;
      description?: string;
      uploadedBy?: string;
      createdAt: string;
    }>>(`/incidents/${id}/evidence`),

  // Upload attachments (multipart — uses raw fetch, not fetchApi which sets Content-Type: application/json)
  uploadAttachments: async (incidentId: string, files: File[], content?: string) => {
    const { getAuthHeaders } = useAuthStore.getState();
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    if (content?.trim()) formData.append('content', content.trim());
    const response = await fetch(`${getApiBase()}/incidents/${incidentId}/attachments`, {
      method: 'POST',
      headers: getAuthHeaders(), // No Content-Type — browser sets multipart boundary
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.error?.message || 'Upload failed');
    }
    const result = await response.json();
    return result.data as Array<{ id: string; filename: string; mimeType: string; size: number; type: string; url: string }>;
  },

  // Get signed URL for a specific attachment
  getAttachmentUrl: (incidentId: string, attachmentId: string) =>
    fetchApi<{ url: string; filename: string; mimeType: string; size: number; type: string }>(
      `/incidents/${incidentId}/attachments/${attachmentId}`
    ),

  // Open incidents shortcut
  getOpen: () => fetchApi<Incident[]>('/incidents/open'),

  // === Compliance Export (E3) ===
  getReport: (id: string) =>
    fetchApi<{
      generatedAt: string;
      generatedBy: string;
      incident: Incident;
      timeline: Array<{ id: string; type: string; timestamp: string; content?: string; data?: Record<string, unknown> }>;
      evidence: Array<{ id: string; type: string; filename?: string; createdAt: string }>;
      metrics: { totalTimelineEntries: number; totalEvidence: number; responseTimeMs: number | null };
    }>(`/incidents/${id}/report`),

  emailReport: (id: string, recipients: string[], subject?: string, message?: string) =>
    fetchApi<{ sent: boolean; recipients: string[] }>(`/incidents/${id}/report/email`, {
      method: 'POST',
      body: JSON.stringify({ recipients, subject, message }),
    }),

  exportIncidents: (params?: { dateFrom?: string; dateTo?: string; format?: 'json' | 'csv'; status?: string; priority?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    if (params?.format) searchParams.set('format', params.format);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.priority) searchParams.set('priority', params.priority);
    const query = searchParams.toString();
    return fetchApi<{ format: string; count: number; data?: string; items?: Incident[] }>(
      `/incidents/export${query ? `?${query}` : ''}`
    );
  },

  getAuditSummary: (params?: { dateFrom?: string; dateTo?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    const query = searchParams.toString();
    return fetchApi<{
      generatedAt: string;
      statusDistribution: Record<string, number>;
      categoryDistribution: Record<string, number>;
      metrics: { avgResponseTimeMs: number | null; sopCompletionRate: number | null };
    }>(`/incidents/audit/summary${query ? `?${query}` : ''}`);
  },

  // === Incident Locking (F1) ===
  getLock: (id: string) =>
    fetchApi<{ lock: IncidentLock | null }>(`/incidents/${id}/lock`),

  acquireLock: (id: string, lockType: 'handling' | 'editing' = 'handling') =>
    fetchApi<{ lock: IncidentLock }>(`/incidents/${id}/lock`, {
      method: 'POST',
      body: JSON.stringify({ lockType }),
    }),

  releaseLock: (id: string) =>
    fetchApi<{ released: boolean }>(`/incidents/${id}/lock`, { method: 'DELETE' }),

  forceUnlock: (id: string) =>
    fetchApi<{ released: boolean; previousHolder: string | null }>(`/incidents/${id}/force-unlock`, { method: 'POST' }),

  extendLock: (id: string) =>
    fetchApi<{ lock: IncidentLock }>(`/incidents/${id}/lock/extend`, { method: 'POST' }),

  listLocks: () =>
    fetchApi<IncidentLock[]>(`/incidents/locks`),

  // === Shift Handoff (F3) ===
  handoff: (targetUserId: string, incidentIds: string[], notes: string) =>
    fetchApi<{ success: boolean }>(`/incidents/handoff`, {
      method: 'POST',
      body: JSON.stringify({ targetUserId, incidentIds, notes }),
    }),

  // === Workflow Config ===
  getWorkflows: () =>
    fetchApi<Record<string, any>>('/incidents/workflows/config'),

  saveWorkflows: (workflows: Record<string, any>) =>
    fetchApi<Record<string, any>>('/incidents/workflows/config', {
      method: 'PUT',
      body: JSON.stringify(workflows),
    }),
};

// Incident Lock type
export interface IncidentLock {
  incidentId: string;
  lockedBy: string;
  lockedByName: string;
  lockedAt: string;
  lockType: 'handling' | 'editing';
  expiresAt: string;
}

// ============================================
// OPERATOR PRESENCE API (F2)
// ============================================

export const operatorPresenceApi = {
  list: () =>
    fetchApi<Array<{
      userId: string;
      userName: string;
      status: 'online' | 'busy' | 'away' | 'offline';
      currentIncidentId?: string;
      currentIncidentTitle?: string;
      lastActivityAt: string;
      connectedAt: string;
    }>>('/operators/presence'),

  heartbeat: (data?: { status?: string; currentIncidentId?: string; currentIncidentTitle?: string }) =>
    fetchApi('/operators/presence/heartbeat', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),

  updateStatus: (status: 'online' | 'busy' | 'away' | 'offline') =>
    fetchApi('/operators/presence/status', {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  disconnect: () =>
    fetchApi('/operators/presence/disconnect', { method: 'POST' }),
};

// ============================================
// INVESTIGATIONS API
// ============================================

export interface Investigation {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  status: 'open' | 'in_progress' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignedTo?: string;
  createdBy?: string;
  closedAt?: string;
  conclusion?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface InvestigationIncident {
  id: string;
  investigationId: string;
  incidentId: string;
  notes?: string;
  addedAt: string;
  addedBy?: string;
}

export interface InvestigationNote {
  id: string;
  investigationId: string;
  userId?: string;
  /** Resolved display name of the note author (null for system notes). */
  userName?: string | null;
  type: 'note' | 'status_change' | 'incident_added' | 'incident_removed' | 'attachment' | 'system';
  content: string;
  data?: Record<string, unknown>;
  createdAt: string;
}

export interface InvestigationWithDetails extends Investigation {
  incidents?: InvestigationIncident[];
  notes?: InvestigationNote[];
  incidentCount?: number;
}

export interface CreateInvestigationDto {
  name: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  assignedTo?: string;
}

export interface UpdateInvestigationDto {
  name?: string;
  description?: string;
  status?: 'open' | 'in_progress' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  assignedTo?: string | null;
  conclusion?: string;
}

export const investigationsApi = {
  list: (params?: { page?: number; limit?: number; status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    const query = searchParams.toString();
    return fetchApi<{ items: Investigation[]; total: number; page: number; limit: number }>(
      `/investigations${query ? `?${query}` : ''}`
    );
  },

  get: (id: string) => fetchApi<InvestigationWithDetails>(`/investigations/${id}`),

  create: (data: CreateInvestigationDto) =>
    fetchApi<Investigation>('/investigations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: UpdateInvestigationDto) =>
    fetchApi<Investigation>(`/investigations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<void>(`/investigations/${id}`, {
      method: 'DELETE',
    }),

  // Incident linking
  getIncidents: (id: string) =>
    fetchApi<InvestigationIncident[]>(`/investigations/${id}/incidents`),

  addIncidents: (id: string, incidentIds: string[]) =>
    fetchApi<{ added: number; incidents: InvestigationIncident[] }>(`/investigations/${id}/incidents`, {
      method: 'POST',
      body: JSON.stringify({ incidentIds }),
    }),

  removeIncident: (id: string, incidentId: string) =>
    fetchApi<void>(`/investigations/${id}/incidents/${incidentId}`, {
      method: 'DELETE',
    }),

  // Notes
  getNotes: (id: string) =>
    fetchApi<InvestigationNote[]>(`/investigations/${id}/notes`),

  addNote: (id: string, content: string, type?: InvestigationNote['type']) =>
    fetchApi<InvestigationNote>(`/investigations/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content, type }),
    }),

  deleteNote: (id: string, noteId: string) =>
    fetchApi<void>(`/investigations/${id}/notes/${noteId}`, {
      method: 'DELETE',
    }),

  // Status transitions
  close: (id: string, conclusion?: string) =>
    fetchApi<Investigation>(`/investigations/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ conclusion }),
    }),

  reopen: (id: string) =>
    fetchApi<Investigation>(`/investigations/${id}/reopen`, {
      method: 'POST',
    }),

  // Statistics
  getStatistics: () =>
    fetchApi<{ total: number; byStatus: Record<string, number> }>('/investigations/stats/summary'),

  // Email status
  checkEmailStatus: () =>
    fetchApi<{ available: boolean }>('/investigations/email-status'),

  // Email actions
  sendReport: async (id: string, pdfBlob: Blob, recipients: string[]): Promise<{ sent: boolean; recipients: string[]; via: string }> => {
    const formData = new FormData();
    formData.append('report', pdfBlob, `investigation-report-${id}.pdf`);
    formData.append('to', JSON.stringify(recipients));

    const response = await fetch(`${getApiBase()}/investigations/${id}/send-report`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: { message: 'Send failed' } }));
      throw new Error(err.error?.message || 'Failed to send report');
    }

    const result = await response.json();
    return result.data;
  },

  sendPackage: async (id: string, zipBlob: Blob, recipients: string[]): Promise<{ sent: boolean; recipients: string[]; via: string }> => {
    const formData = new FormData();
    formData.append('package', zipBlob, `investigation-package-${id}.zip`);
    formData.append('to', JSON.stringify(recipients));

    const response = await fetch(`${getApiBase()}/investigations/${id}/send-package`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: { message: 'Send failed' } }));
      throw new Error(err.error?.message || 'Failed to send package');
    }

    const result = await response.json();
    return result.data;
  },

  // ── Attachments / materials ──────────────────────────────────────
  // The attachment `url` is the server streaming route. An <img>/<video>
  // tag can't send an Authorization header, so the access token is
  // appended as a `?token=` query param (the auth plugin accepts it).
  listAttachments: async (id: string): Promise<InvestigationAttachmentDto[]> => {
    const list = await fetchApi<InvestigationAttachmentDto[]>(`/investigations/${id}/attachments`);
    return list.map(withAttachmentToken);
  },

  uploadAttachments: async (id: string, files: File[]): Promise<InvestigationAttachmentDto[]> => {
    const { getAuthHeaders } = useAuthStore.getState();
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const response = await fetch(`${getApiBase()}/investigations/${id}/attachments`, {
      method: 'POST',
      headers: getAuthHeaders(), // no Content-Type — browser sets the multipart boundary
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.error?.message || 'Upload failed');
    }
    const result = await response.json();
    return (result.data as InvestigationAttachmentDto[]).map(withAttachmentToken);
  },

  removeAttachment: (id: string, attachmentId: string) =>
    fetchApi<{ deleted: boolean }>(`/investigations/${id}/attachments/${attachmentId}`, {
      method: 'DELETE',
    }),

  // ── AI media analysis ────────────────────────────────────────────
  // Start analysis. Images return status 'done' immediately; videos
  // return 'pending' and continue in the background — poll getAnalysis.
  analyzeAttachment: (id: string, attachmentId: string) =>
    fetchApi<AiAnalysisResultDto>(
      `/investigations/${id}/attachments/${attachmentId}/analyze`,
      { method: 'POST' },
    ),

  getAnalysis: (id: string, attachmentId: string) =>
    fetchApi<AiAnalysisResultDto | null>(
      `/investigations/${id}/attachments/${attachmentId}/analysis`,
    ),
};

/** AI analysis result for an investigation attachment. */
export interface AiAnalysisResultDto {
  status: 'pending' | 'done' | 'failed';
  model?: string;
  /** Image analysis — a single description. */
  description?: string;
  /** Video analysis — one entry per sampled frame. */
  timeline?: { tSeconds: number; description: string }[];
  error?: string;
  analyzedAt?: string;
}

/** Append the access token to an attachment's streaming `url` so that
 *  plain <img>/<video>/<a> tags (which can't set an Authorization
 *  header) can load it. The auth plugin accepts a `?token=` query param. */
function withAttachmentToken(a: InvestigationAttachmentDto): InvestigationAttachmentDto {
  const token = useAuthStore.getState().accessToken;
  if (!token || !a.url) return a;
  const sep = a.url.includes('?') ? '&' : '?';
  return { ...a, url: `${a.url}${sep}token=${encodeURIComponent(token)}` };
}

/** An investigation attachment as returned by the API. `url` is a
 *  server-side streaming route; `metadata.aiAnalysis` holds the AI
 *  analysis result once an attachment has been analysed. */
export interface InvestigationAttachmentDto {
  id: string;
  investigationId: string;
  type: 'image' | 'video' | 'audio' | 'document';
  filename: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrl?: string | null;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  uploadedBy?: string | null;
  /** Resolved display name of the uploader (null if unknown). */
  uploadedByName?: string | null;
  createdAt: string;
}

// ============================================
// SOP EXECUTIONS API
// ============================================

export interface SOPExecution {
  id: string;
  tenantId: string;
  sopId: string;
  sopVersion: number;
  incidentId?: string | null;
  alarmId?: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  currentStepId?: string | null;
  currentStepIndex: number;
  startedBy?: string | null;
  startedAt: string;
  completedAt?: string | null;
  durationMs?: number | null;
  triggerSource: string;
  triggerData: Record<string, unknown>;
  stepResults: Record<string, unknown>;
  variables: Record<string, unknown>;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const sopExecutionsApi = {
  create: (data: {
    sopId: string;
    incidentId?: string;
    alarmId?: string;
    triggerSource: string;
    triggerData?: Record<string, unknown>;
    stepResults?: Record<string, unknown>;
  }) =>
    fetchApi<SOPExecution>('/sop-executions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  get: (id: string) => fetchApi<SOPExecution>(`/sop-executions/${id}`),

  updateStep: (id: string, data: {
    stepIndex: number;
    stepId: string;
    stepResults: Record<string, unknown>;
  }) =>
    fetchApi<SOPExecution>(`/sop-executions/${id}/steps`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  complete: (id: string, stepResults: Record<string, unknown>) =>
    fetchApi<SOPExecution>(`/sop-executions/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ stepResults }),
    }),

  fail: (id: string, errorMessage?: string) =>
    fetchApi<SOPExecution>(`/sop-executions/${id}/fail`, {
      method: 'POST',
      body: JSON.stringify({ errorMessage }),
    }),

  getActive: () =>
    fetchApi<SOPExecution[]>('/sop-executions/active'),

  getBySop: (sopId: string) =>
    fetchApi<SOPExecution[]>(`/sop-executions/by-sop/${sopId}`),

  getByIncident: (incidentId: string) =>
    fetchApi<SOPExecution[]>(`/sop-executions/by-incident/${incidentId}`),

  sendNotification: (id: string, data: { channel: string; recipients?: string[]; message: string }) =>
    fetchApi<{ sent: boolean }>(`/sop-executions/${id}/send-notification`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  uploadAttachment: async (id: string, file: File): Promise<{ url: string; filename: string }> => {
    const { getAuthHeaders } = useAuthStore.getState();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${getApiBase()}/sop-executions/${id}/attachments`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
      throw new ApiError(response.status, error.error?.code || 'UPLOAD_ERROR', error.error?.message || 'File upload failed');
    }
    const data = await response.json();
    return data.data as { url: string; filename: string };
  },
};

// ============================================
// Entity Overrides API (Device Configuration)
// ============================================

export interface EntityOverride {
  id: string;
  tenantId: string;
  entityType: 'device' | 'zone' | 'partition' | 'door' | 'output';
  entityId: string;
  driverInstanceId: string;
  displayName: string | null;
  eventsEnabled: boolean;
  historyLogging: boolean;
  assignedCameraIds: string[];
  customConfig: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertEntityOverrideDto {
  entityType: EntityOverride['entityType'];
  entityId: string;
  driverInstanceId: string;
  displayName?: string | null;
  eventsEnabled?: boolean;
  historyLogging?: boolean;
  assignedCameraIds?: string[];
  customConfig?: Record<string, unknown>;
}

export const entityOverridesApi = {
  list: async (filters?: {
    entityType?: string;
    driverInstanceId?: string;
  }): Promise<EntityOverride[]> => {
    const params = new URLSearchParams();
    if (filters?.entityType) params.append('entityType', filters.entityType);
    if (filters?.driverInstanceId) params.append('driverInstanceId', filters.driverInstanceId);
    params.append('limit', '10000');
    const query = params.toString();
    const response = await fetchApi<PaginatedResponse<EntityOverride>>(
      `/entity-overrides${query ? `?${query}` : ''}`
    );
    return response?.items ?? (Array.isArray(response) ? response : []);
  },

  upsert: (data: UpsertEntityOverrideDto) =>
    fetchApi<EntityOverride>('/entity-overrides', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  batchUpsert: (overrides: UpsertEntityOverrideDto[]) =>
    fetchApi<EntityOverride[]>('/entity-overrides/batch', {
      method: 'PUT',
      body: JSON.stringify({ overrides }),
    }),

  delete: (id: string) =>
    fetchApi<void>(`/entity-overrides/${id}`, {
      method: 'DELETE',
    }),
};

// ============================================
// AI / Intelligence API
// ============================================

export interface SituationalAwareness {
  overallRiskLevel: string;
  activeIncidents: number;
  activeAlarms: number;
  anomaliesDetected: number;
  zones: Array<{ zone: string; riskScore: number; activeEvents: number }>;
  recommendations: string[];
}

export interface Anomaly {
  id: string;
  deviceId: string;
  deviceName?: string;
  eventType: string;
  severity: string;
  anomalyScore: number;
  description: string;
  detectedAt: string;
}

export interface EventCorrelation {
  eventTypeA: string;
  eventTypeB: string;
  cooccurrenceCount: number;
  windowCount: number;
  confidence: number;
}

export interface RiskAssessment {
  zones: Array<{ zone: string; riskScore: number; factors: string[] }>;
  devices: Array<{ deviceId: string; deviceName: string; riskScore: number }>;
  overallRisk: number;
}

export interface EventClassification {
  category: string;
  confidence: number;
  suggestedPriority: string;
  suggestedSeverity: string;
  tags: string[];
}

export interface IncidentRecommendation {
  incidentId: string;
  recommendations: Array<{ action: string; priority: string; reason: string }>;
  suggestedSops: Array<{ sopId: string; name: string; matchScore: number }>;
  similarIncidents: Array<{ id: string; title: string; similarity: number }>;
}

export interface SOPRecommendation {
  sops: Array<{ id: string; name: string; category: string; matchScore: number; triggers: unknown[] }>;
}

export interface NLPQueryResult {
  answer: string;
  data?: unknown;
  query: string;
  confidence: number;
}

export interface Prediction {
  type: string;
  description: string;
  probability: number;
  timeframe: string;
  affectedDevices: string[];
}

export interface ThreatAnalysisResult {
  threats: Array<{ type: string; severity: string; description: string; confidence: number }>;
  overallThreatLevel: string;
}

export interface ScenarioSimulation {
  scenario: string;
  outcomes: Array<{ description: string; probability: number; impact: string }>;
  recommendations: string[];
}

export interface AIPerformanceMetrics {
  accuracy: number;
  precision: number;
  recall: number;
  avgResponseTimeMs: number;
  totalQueries: number;
}

export interface OperatorInsight {
  operatorId: string;
  operatorName: string;
  avgResponseMinutes: number;
  incidentsHandled: number;
  resolutionRate: number;
  peakHours: number[];
}

export interface CapacityPlanningResult {
  currentUtilization: number;
  projectedGrowth: number;
  recommendations: Array<{ resource: string; action: string; urgency: string }>;
}

export interface ComplianceCheckResult {
  overallStatus: string;
  checks: Array<{ name: string; status: string; details: string }>;
  score: number;
}

export interface MaintenancePrediction {
  deviceId: string;
  deviceName: string;
  predictedIssue: string;
  confidence: number;
  estimatedDate: string;
  recommendation: string;
}

export interface EnergyAnalysis {
  totalConsumption: number;
  byDevice: Array<{ deviceId: string; deviceName: string; consumption: number }>;
  trend: string;
  recommendations: string[];
}

function buildQueryString(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.append(k, String(v));
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export const aiApi = {
  getSituationalAwareness: (params?: { from?: string; to?: string }) =>
    fetchApi<SituationalAwareness>(`/ai/situational-awareness${buildQueryString(params)}`),

  getAnomalies: (params?: { from?: string; to?: string; sensitivity?: number; subsystem?: string }) =>
    fetchApi<{ anomalies: Anomaly[]; total: number }>(`/ai/anomalies${buildQueryString(params)}`),

  getCorrelations: (params?: { from?: string; to?: string; windowMinutes?: number; minEvents?: number; scope?: string }) =>
    fetchApi<{ correlations: EventCorrelation[] }>(`/ai/correlations${buildQueryString(params)}`),

  getRiskAssessment: (params?: { from?: string; to?: string }) =>
    fetchApi<RiskAssessment>(`/ai/risk-score${buildQueryString(params)}`),

  classifyEvent: (data: { incidentId?: string; title?: string; description?: string; events?: Record<string, unknown>[] }) =>
    fetchApi<EventClassification>('/ai/classify-incident', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Send a camera-frame snapshot to the vision LLM and get back a
   * short scene description. Used by the incident-placement flow to
   * pre-fill the description field from what the operator just drew
   * a rectangle around on the video wall.
   *
   * `image` should be the base64 payload only (without the
   * `data:image/jpeg;base64,` prefix) — the BE strips a prefix if
   * present, but stripping it FE-side keeps the request smaller.
   */
  describeScene: (data: { image: string; prompt?: string }) =>
    fetchApi<{ description: string; model: string; durationMs: number }>('/ai/describe-scene', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * On-prem text translation via local Ollama. Used to translate dynamic
   * strings the build-time i18n pass can't cover: AI vision output,
   * AI chat output, driver-emitted error messages.
   * All processing stays inside the Docker stack — nothing leaves the host.
   */
  translate: (data: { text: string; to: 'he' | 'en'; from?: 'he' | 'en' }) =>
    fetchApi<{ text: string; model: string; durationMs: number }>('/ai/translate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getRecommendations: (context: string, entityId?: string) =>
    fetchApi<IncidentRecommendation>('/ai/recommendations', {
      method: 'POST',
      body: JSON.stringify({ context, entityId }),
    }),

  recommendSop: (params: { incidentId?: string; eventType?: string; severity?: string; category?: string }) =>
    fetchApi<SOPRecommendation>(`/ai/recommend-sop${buildQueryString(params)}`),

  nlpQuery: (data: { question: string; language?: string; context?: Record<string, unknown> }) =>
    fetchApi<NLPQueryResult>('/ai/query', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPredictions: (params?: { metric?: string; horizon?: string }) =>
    fetchApi<{ predictions: Prediction[] }>(`/ai/predictions${buildQueryString(params)}`),

  getThreatAnalysis: (params?: { from?: string; to?: string }) =>
    fetchApi<ThreatAnalysisResult>(`/ai/risk-score${buildQueryString(params)}`),

  simulateScenario: (data: { scenario: string; conditions?: Record<string, unknown>; timeframe?: string }) =>
    fetchApi<ScenarioSimulation>('/ai/simulate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPerformanceMetrics: () =>
    fetchApi<AIPerformanceMetrics>('/ai/models'),

  getOperatorInsights: (params?: { from?: string; to?: string; operatorId?: string }) =>
    fetchApi<{ operators: OperatorInsight[] }>(`/ai/escalation-suggestions${buildQueryString(params)}`),

  getCapacityPlanning: () =>
    fetchApi<CapacityPlanningResult>('/ai/situational-awareness'),

  getComplianceCheck: () =>
    fetchApi<ComplianceCheckResult>('/ai/situational-awareness'),

  getMaintenancePredictions: (params?: { horizonDays?: number }) =>
    fetchApi<{ predictions: MaintenancePrediction[] }>(`/ai/predictions${buildQueryString({ metric: 'incidents', horizon: '7d', ...params })}`),

  getEnergyAnalysis: (params?: { from?: string; to?: string }) =>
    fetchApi<EnergyAnalysis>(`/ai/situational-awareness${buildQueryString(params)}`),

  // Direct AI prompt/query
  summarize: (data: { type: 'incident' | 'shift' | 'period'; entityId?: string; from?: string; to?: string }) =>
    fetchApi<{ summary: string; highlights: string[]; metrics: Record<string, unknown> }>('/ai/summarize', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  giveFeedback: (data: { responseId: string; rating: number; comment?: string }) =>
    fetchApi<void>('/ai/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ─── AI Chat Arc 1: conversational orchestrator ──────────────
  chat: (data: { conversationId?: string; message: string }) =>
    fetchApi<AIChatResponse>('/ai/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  listConversations: () =>
    fetchApi<AIConversation[]>('/ai/conversations'),

  createConversation: () =>
    fetchApi<AIConversation>('/ai/conversations', { method: 'POST' }),

  getConversation: (id: string) =>
    fetchApi<AIConversation>(`/ai/conversations/${id}`),

  deleteConversation: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/ai/conversations/${id}`, { method: 'DELETE' }),

  listMessages: (conversationId: string) =>
    fetchApi<AIMessage[]>(`/ai/conversations/${conversationId}/messages`),
};

export interface AIConversation {
  id: string;
  tenantId: string;
  userId: string;
  sessionId: string | null;
  status: string;
  startedAt: string;
  lastActivityAt: string;
  closedAt: string | null;
  messageCount: number | null;
  toolCallCount: number | null;
}

export interface AIMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  richContent?: unknown;
  toolCalls?: Array<{ tool: string; args: unknown }> | null;
  toolResults?: unknown;
  timestamp: string;
  processingTimeMs?: number | null;
  tokenCount?: number | null;
  feedbackRating?: string | null;
}

export type AIVizKind = 'table' | 'bar' | 'pie' | 'line' | 'stats' | 'scalar' | 'none';

export interface AIToolCallTrace {
  tool: string;
  args: unknown;
  summary?: string;
  truncated?: boolean;
  /** Raw tool result — rows / object / scalar. May be clipped by server. */
  data?: unknown;
  /** Original shape tag from the tool. */
  shape?: string;
  /** Auto-detected best-viz hint. Frontend may override. */
  viz?: AIVizKind;
}

export interface AIChatResponse {
  conversationId: string;
  messageId: string;
  answer: string;
  toolCalls: AIToolCallTrace[];
  aborted: 'tool_cap' | 'timeout' | 'malformed' | null;
  elapsedMs: number;
}

// ============================================
// Analytics API
// ============================================

export interface HeatmapData {
  type: string;
  resolution: string;
  from: string;
  to: string;
  data: Array<{ bucket: string; device_id?: string; severity?: string; category?: string; priority?: string; state?: string; count: number }>;
}

export interface AnalyticsCorrelation {
  event_type_a: string;
  event_type_b: string;
  cooccurrence_count: number;
  window_count: number;
  confidence: number;
}

export interface TrendData {
  metric: string;
  currentPeriod: { from: string; to: string; total: number; daily: Array<{ bucket: string; count: number }> };
  previousPeriod: { from: string; to: string; total: number; daily: Array<{ bucket: string; count: number }> };
  change: { absolute: number; percent: number; direction: 'up' | 'down' | 'flat' };
}

export interface StalledIncident {
  id: string;
  incident_number: string;
  title: string;
  status: string;
  priority: string;
  category: string;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  idle_minutes: number;
}

export const analyticsApi = {
  getHeatmap: (params?: { type?: string; from?: string; to?: string; resolution?: string }) =>
    fetchApi<HeatmapData>(`/analytics/heatmap${buildQueryString(params)}`),

  getCorrelations: (params?: { from?: string; to?: string; minConfidence?: number }) =>
    fetchApi<{ from: string; to: string; minConfidence: number; correlations: AnalyticsCorrelation[] }>(
      `/analytics/correlations${buildQueryString(params)}`
    ),

  getTrends: (params?: { metric?: string; from?: string; to?: string; compareFrom?: string; compareTo?: string }) =>
    fetchApi<TrendData>(`/analytics/trends${buildQueryString(params)}`),

  getStalledIncidents: (params?: { idleMinutes?: number; excludeStatuses?: string }) =>
    fetchApi<{ idleMinutes: number; excludeStatuses: string[]; count: number; incidents: StalledIncident[] }>(
      `/incidents/stalled${buildQueryString(params)}`
    ),
};

// ============================================
// Report Templates API
// ============================================

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  dataSource: string;
  columns: Array<{ field: string; label: string; type?: string }>;
  filters: Record<string, unknown>;
  groupBy?: string[];
  sortBy?: string;
  sortOrder?: string;
  isPublic: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReportExecution {
  jobId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress?: number;
  resultUrl?: string;
  error?: string;
  createdAt: string;
  completedAt?: string;
}

export interface ReportDataSource {
  id: string;
  name: string;
  description: string;
  fields: string[];
}

export interface ReportField {
  name: string;
  type: string;
  label: string;
  filterable: boolean;
  sortable: boolean;
}

export interface ReportAsset {
  id: string;
  filename: string;
  url: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export const reportTemplatesApi = {
  list: (params?: { type?: string; isPublic?: boolean }) =>
    fetchApi<ReportTemplate[]>(`/report-templates${buildQueryString(params)}`),

  get: (id: string) =>
    fetchApi<ReportTemplate>(`/report-templates/${id}`),

  create: (data: Partial<ReportTemplate>) =>
    fetchApi<ReportTemplate>('/report-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<ReportTemplate>) =>
    fetchApi<ReportTemplate>(`/report-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<void>(`/report-templates/${id}`, {
      method: 'DELETE',
    }),

  preview: (data: { templateId?: string; dataSource: string; columns: unknown[]; filters?: Record<string, unknown>; limit?: number }) =>
    fetchApi<{ columns: string[]; rows: unknown[][] }>('/report-templates/preview', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  execute: (data: { templateId?: string; dataSource: string; columns: unknown[]; filters?: Record<string, unknown>; format?: string }) =>
    fetchApi<ReportExecution>('/reports/execute', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getJobStatus: (jobId: string) =>
    fetchApi<ReportExecution>(`/reports/execute/${jobId}/status`),

  downloadJob: (jobId: string) =>
    fetchApi<Blob>(`/reports/execute/${jobId}/download`),

  getHistory: (params?: { page?: number; limit?: number; templateId?: string }) =>
    fetchApi<{ items: ReportExecution[]; total: number }>(`/reports/history${buildQueryString(params)}`),

  getDataSources: () =>
    fetchApi<ReportDataSource[]>('/report-metadata/data-sources'),

  getFields: (source: string) =>
    fetchApi<ReportField[]>(`/report-metadata/fields/${source}`),

  uploadAsset: async (file: File): Promise<ReportAsset> => {
    const { getAuthHeaders } = useAuthStore.getState();
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${getApiBase()}/report-assets/upload`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
      throw new ApiError(response.status, error.error?.code || 'UPLOAD_ERROR', error.error?.message || 'Asset upload failed');
    }
    const data = await response.json();
    return data.data as ReportAsset;
  },

  listAssets: () =>
    fetchApi<ReportAsset[]>('/report-assets'),

  deleteAsset: (id: string) =>
    fetchApi<void>(`/report-assets/${id}`, {
      method: 'DELETE',
    }),
};

// ============================================
// Report Data API (Specialized Reports)
// ============================================

export interface OperatorPerformance {
  operator_id: string;
  operator_name: string;
  operator_email: string;
  incidents_handled: number;
  incidents_resolved: number;
  incidents_open: number;
  avg_response_minutes: number;
  avg_resolution_minutes: number;
  high_priority_handled: number;
  resolution_rate: number;
}

export interface DeviceHealthReport {
  summary: {
    totalDevices: number;
    online: number;
    offline: number;
    other: number;
    totalEvents: number;
    overallUptimePct: number;
  };
  devices: Array<{
    device_id: string;
    device_name: string;
    device_type: string;
    current_status: string;
    driver_instance_id: string;
    serial_number: string;
    last_seen_at: string;
    event_count: number;
    critical_events: number;
    high_events: number;
    estimated_uptime_pct: number;
    hours_since_last_seen: number;
  }>;
}

export interface CommunicationsHealth {
  driverIntegrations: {
    total: number;
    connected: number;
    disconnected: number;
    instances: Array<{
      instance_id: string;
      instance_name: string;
      driver_id: string;
      current_status: string;
      last_health_check: string;
      device_count: number;
      devices_online: number;
      events_in_period: number;
      error_events: number;
    }>;
  };
  messagingProviders: unknown[];
}

export const reportDataApi = {
  getOperatorPerformance: (params?: { from?: string; to?: string; operatorId?: string; groupBy?: string }) =>
    fetchApi<{ from: string; to: string; operators: OperatorPerformance[] }>(
      `/reports/operator-performance${buildQueryString(params)}`
    ),

  getDeviceHealth: (params?: { from?: string; to?: string; driverInstanceId?: string; deviceType?: string }) =>
    fetchApi<DeviceHealthReport>(`/reports/device-health${buildQueryString(params)}`),

  getCommunicationsHealth: (params?: { from?: string; to?: string }) =>
    fetchApi<CommunicationsHealth>(`/reports/communications-health${buildQueryString(params)}`),
};

// ============================================
// Dashboard KPIs API
// ============================================

export interface DashboardKPI {
  id: string;
  name: string;
  description?: string | null;
  metric: string;
  data_source: string;
  calculation: string;
  filter?: Record<string, unknown>;
  thresholds?: Record<string, unknown>;
  unit?: string;
  icon?: string;
  color?: string;
  period?: string;
  sort_order: number;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  // Computed fields from backend
  currentValue: number;
  previousValue?: number;
  changePercent?: number | null;
  status?: string;
}

export const dashboardKpisApi = {
  list: () =>
    fetchApi<DashboardKPI[]>('/dashboard/kpis'),

  get: (id: string) =>
    fetchApi<DashboardKPI>(`/dashboard/kpis/${id}`),

  create: (data: Partial<DashboardKPI>) =>
    fetchApi<DashboardKPI>('/dashboard/kpis', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<DashboardKPI>) =>
    fetchApi<DashboardKPI>(`/dashboard/kpis/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<void>(`/dashboard/kpis/${id}`, {
      method: 'DELETE',
    }),
};

// Suppression Rules API (Phase 3)
export const suppressionRulesApi = {
  list: (enabledOnly = false) => fetchApi<any[]>(`/suppression-rules${enabledOnly ? '?enabled=true' : ''}`),
  get: (id: string) => fetchApi<any>(`/suppression-rules/${id}`),
  create: (data: { name: string; eventType: string; matchFields?: string[]; windowSeconds?: number; enabled?: boolean; priority?: number }) =>
    fetchApi<any>('/suppression-rules', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; eventType: string; matchFields?: string[]; windowSeconds?: number; enabled?: boolean; priority?: number }>) =>
    fetchApi<any>(`/suppression-rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<any>(`/suppression-rules/${id}`, { method: 'DELETE' }),
  toggle: (id: string) => fetchApi<any>(`/suppression-rules/${id}/toggle`, { method: 'POST' }),
};

// SOP Library API (Phase 7)
export const sopLibraryApi = {
  list: (params?: { category?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchApi<any[]>(`/sop-library${qs ? `?${qs}` : ''}`);
  },
  get: (libraryId: string) => fetchApi<any>(`/sop-library/${libraryId}`),
  use: (libraryId: string) => fetchApi<any>(`/sop-library/${libraryId}/use`, { method: 'POST' }),
  aiGenerate: (prompt: string) => fetchApi<any>('/sop-library/ai/generate', { method: 'POST', body: JSON.stringify({ prompt }) }),
  aiCustomize: (sop: any, instructions: string) => fetchApi<any>('/sop-library/ai/customize', { method: 'POST', body: JSON.stringify({ sop, instructions }) }),
  aiSuggest: (sop: any) => fetchApi<any>('/sop-library/ai/suggest', { method: 'POST', body: JSON.stringify({ sop }) }),
};

// Incident Library API — browse and use pre-built incident templates
export interface LibraryIncidentTemplate {
  libraryId: string;
  version: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  defaultPriority: string;
  assignedRole?: string;
  mapAnimation?: string;
  requiredFields?: string[];
  autoActions?: Array<{
    type: string;
    integrationId?: string;
    actionName?: string;
    deviceId?: string;
    command?: string;
    parameters?: Record<string, unknown>;
  }>;
  tags: string[];
  triggerEventTypes?: string[];
  suggestedSopLibraryId?: string;
  metadata: {
    industry: string[];
    compliance: string[];
    lastReviewed: string;
  };
}

export const incidentLibraryApi = {
  list: (params?: { category?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchApi<LibraryIncidentTemplate[]>(`/incident-library${qs ? `?${qs}` : ''}`);
  },
  get: (libraryId: string) => fetchApi<LibraryIncidentTemplate>(`/incident-library/${libraryId}`),
  use: (libraryId: string) => fetchApi<any>(`/incident-library/${libraryId}/use`, { method: 'POST' }),
};

// Correlation Rules API (Phase 4)
export const correlationRulesApi = {
  list: (enabledOnly = false) => fetchApi<any[]>(`/correlation-rules${enabledOnly ? '?enabled=true' : ''}`),
  get: (id: string) => fetchApi<any>(`/correlation-rules/${id}`),
  create: (data: { name: string; eventTypes?: string[]; windowSeconds?: number; scope?: string; mergeIncident?: boolean; enabled?: boolean; priority?: number }) =>
    fetchApi<any>('/correlation-rules', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; eventTypes?: string[]; windowSeconds?: number; scope?: string; mergeIncident?: boolean; enabled?: boolean; priority?: number }>) =>
    fetchApi<any>(`/correlation-rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<any>(`/correlation-rules/${id}`, { method: 'DELETE' }),
  toggle: (id: string) => fetchApi<any>(`/correlation-rules/${id}/toggle`, { method: 'POST' }),
};

// Calendar Layers API
export const calendarLayersApi = {
  list: (enabled?: boolean) => {
    const params = enabled !== undefined ? `?enabled=${enabled}` : '';
    return fetchApi<any>(`/calendar-layers${params}`);
  },
  get: (id: string) => fetchApi<any>(`/calendar-layers/${id}`),
  create: (data: { name: string; description?: string; color?: string; icon?: string; sortOrder?: number; enabled?: boolean }) =>
    fetchApi<any>('/calendar-layers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; description?: string; color?: string; icon?: string; sortOrder?: number; enabled?: boolean }>) =>
    fetchApi<any>(`/calendar-layers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<void>(`/calendar-layers/${id}`, { method: 'DELETE' }),
  reorder: (orderedIds: string[]) =>
    fetchApi<any>('/calendar-layers/reorder', { method: 'POST', body: JSON.stringify({ orderedIds }) }),
};

// Calendar Events API
export const calendarEventsApi = {
  list: (params?: { from?: string; to?: string; layerIds?: string; search?: string; enabled?: string; priority?: string; page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.layerIds) query.set('layerIds', params.layerIds);
    if (params?.search) query.set('search', params.search);
    if (params?.enabled) query.set('enabled', params.enabled);
    if (params?.priority) query.set('priority', params.priority);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchApi<any>(`/calendar-events${qs ? `?${qs}` : ''}`);
  },
  getRange: (from: string, to: string, layerIds?: string) => {
    const params = new URLSearchParams({ from, to });
    if (layerIds) params.set('layerIds', layerIds);
    return fetchApi<any[]>(`/calendar-events/range?${params.toString()}`);
  },
  get: (id: string) => fetchApi<any>(`/calendar-events/${id}`),
  create: (data: any) =>
    fetchApi<any>('/calendar-events', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) =>
    fetchApi<any>(`/calendar-events/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<void>(`/calendar-events/${id}`, { method: 'DELETE' }),
  duplicate: (id: string) =>
    fetchApi<any>(`/calendar-events/${id}/duplicate`, { method: 'POST' }),
  enable: (id: string) =>
    fetchApi<any>(`/calendar-events/${id}/enable`, { method: 'POST' }),
  disable: (id: string) =>
    fetchApi<any>(`/calendar-events/${id}/disable`, { method: 'POST' }),
  stats: () => fetchApi<any>('/calendar-events/stats'),
  testAction: (action: { id: string; type: string; timing: string; offsetMinutes?: number; config: Record<string, unknown>; enabled: boolean }, eventContext?: { title?: string; startTime?: string; endTime?: string }) =>
    fetchApi<{ success: boolean; actionType: string; logs: string[]; error?: string }>('/calendar-events/test-action', { method: 'POST', body: JSON.stringify({ action, eventContext }) }),
};

export const schedulesApi = {
  list: (params?: { enabled?: string; scheduleType?: string; search?: string; page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.enabled) query.set('enabled', params.enabled);
    if (params?.scheduleType) query.set('scheduleType', params.scheduleType);
    if (params?.search) query.set('search', params.search);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return fetchApi<any>(`/schedules${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => fetchApi<any>(`/schedules/${id}`),
  create: (data: any) =>
    fetchApi<any>('/schedules', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) =>
    fetchApi<any>(`/schedules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<void>(`/schedules/${id}`, { method: 'DELETE' }),
  enable: (id: string) =>
    fetchApi<any>(`/schedules/${id}/enable`, { method: 'POST' }),
  disable: (id: string) =>
    fetchApi<any>(`/schedules/${id}/disable`, { method: 'POST' }),
  duplicate: (id: string, name: string) =>
    fetchApi<any>(`/schedules/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ name }) }),
  checkActive: (id: string) => fetchApi<any>(`/schedules/${id}/active`),
  stats: () => fetchApi<any>('/schedules/stats/summary'),
};

// Playbooks API
export const playbooksApi = {
  list: () => fetchApi<any>('/playbooks'),
  get: (id: string) => fetchApi<any>(`/playbooks/${id}`),
  create: (data: any) =>
    fetchApi<any>('/playbooks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) =>
    fetchApi<any>(`/playbooks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<void>(`/playbooks/${id}`, { method: 'DELETE' }),
  execute: (id: string, context?: any) =>
    fetchApi<any>(`/playbooks/${id}/execute`, { method: 'POST', body: JSON.stringify(context || {}) }),
  enable: (id: string) =>
    fetchApi<any>(`/playbooks/${id}`, { method: 'PATCH', body: JSON.stringify({ enabled: true }) }),
  disable: (id: string) =>
    fetchApi<any>(`/playbooks/${id}`, { method: 'PATCH', body: JSON.stringify({ enabled: false }) }),
};

// Compliance API
export const complianceApi = {
  getStatus: () => fetchApi<any>('/compliance/status'),
  getRetention: () => fetchApi<any>('/compliance/retention'),
  updateRetention: (data: any) =>
    fetchApi<any>('/compliance/retention', { method: 'PATCH', body: JSON.stringify(data) }),
  runCheck: () =>
    fetchApi<any>('/compliance/retention/check', { method: 'POST' }),
};

// Shifts API (for ShiftsPage to replace demo data)
export const shiftsApi = {
  list: () => fetchApi<any>('/shifts'),
  get: (id: string) => fetchApi<any>(`/shifts/${id}`),
  create: (data: any) =>
    fetchApi<any>('/shifts', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) =>
    fetchApi<any>(`/shifts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<void>(`/shifts/${id}`, { method: 'DELETE' }),
  getEnabled: () => fetchApi<any>('/shifts/enabled'),
  setEnabled: (enabled: boolean) =>
    fetchApi<any>('/shifts/enabled', { method: 'PATCH', body: JSON.stringify({ enabled }) }),
  getAssignments: (shiftId: string) => fetchApi<any>(`/shifts/${shiftId}/assignments`),
  assignUser: (shiftId: string, data: any) =>
    fetchApi<any>(`/shifts/${shiftId}/assignments`, { method: 'POST', body: JSON.stringify(data) }),
  getCurrent: (siteId?: string) => {
    const params = siteId ? `?siteId=${siteId}` : '';
    return fetchApi<any>(`/shifts/current${params}`);
  },
  createHandover: (data: any) =>
    fetchApi<any>('/shifts/handover', { method: 'POST', body: JSON.stringify(data) }),
  getHandovers: () => fetchApi<any>('/shifts/handovers'),
  updateHandoverStatus: (id: string, status: string) =>
    fetchApi<any>(`/shifts/handovers/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};

// Data Management API
export const dataManagementApi = {
  exportConfig: () =>
    fetchApi<any>('/data/export', { method: 'POST' }),
  validateImport: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchApi<any>('/data/import/validate', { method: 'POST', body: formData });
  },
  importConfig: (file: File, options: { overwrite?: boolean; merge?: boolean }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    return fetchApi<any>('/data/import', { method: 'POST', body: formData });
  },
  exportEvents: (params: { fromDate?: string; toDate?: string; format?: string }) =>
    fetchApi<any>('/data/export/events', { method: 'POST', body: JSON.stringify(params) }),
  exportIncidents: (params: { fromDate?: string; toDate?: string; format?: string }) =>
    fetchApi<any>('/data/export/incidents', { method: 'POST', body: JSON.stringify(params) }),
  exportAudit: (params: { fromDate?: string; toDate?: string; format?: string }) =>
    fetchApi<any>('/data/export/audit', { method: 'POST', body: JSON.stringify(params) }),
};

// ============================================
// RESPONDER LOCATION TRACKING API
// ============================================

export interface ResponderLocation {
  id: string;
  userId: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  altitude?: number;
  speed?: number;
  heading?: number;
  source: string;
  timestamp: string;
}

export interface SafetyAlertResponse {
  id: string;
  userId: string;
  userName?: string;
  alertType: 'duress' | 'man_down';
  status: 'active' | 'resolved' | 'false_alarm';
  latitude?: number;
  longitude?: number;
  incidentId?: string;
  createdAt: string;
}

export const responderLocationApi = {
  /** Get all users' latest positions (control room view) */
  getAll: () =>
    fetchApi<ResponderLocation[]>('/location/all'),

  /** Get specific user's latest position */
  getUser: (userId: string) =>
    fetchApi<ResponderLocation>(`/location/user/${userId}`),

  /** Get active safety alerts (duress + man-down) */
  getActiveAlerts: () =>
    fetchApi<SafetyAlertResponse[]>('/alerts/active'),

  /** Resolve a safety alert */
  resolveAlert: (id: string, note?: string) =>
    fetchApi<SafetyAlertResponse>(`/alerts/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
};

// ─── HMI Tag Browsing ──────────────────────────────────────────────────────

export interface ProtocolTagMeta {
  tagId: string;
  name: string;
  address: string;
  dataType: string;
  accessMode: string;
  unit?: string;
  description?: string;
  group?: string;
}

export async function browseDriverTags(
  driverInstanceId: string,
  path?: string,
  headers?: Record<string, string>,
): Promise<ProtocolTagMeta[]> {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/driver-instances/${driverInstanceId}/command`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ command: 'browse', params: { path } }),
  });
  if (!res.ok) return [];
  const json = await res.json();
  return (json.data?.tags ?? json.tags ?? []) as ProtocolTagMeta[];
}

// ============================================
// GEOFENCING & ROUTES API
// ============================================

export const geofencesApi = {
  list: () => fetchApi<any[]>('/geofences'),
  get: (id: string) => fetchApi<any>(`/geofences/${id}`),
  create: (data: any) => fetchApi<any>('/geofences', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => fetchApi<any>(`/geofences/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<any>(`/geofences/${id}`, { method: 'DELETE' }),
  toggle: (id: string) => fetchApi<any>(`/geofences/${id}/toggle`, { method: 'PATCH' }),
};

export const patrolRoutesApi = {
  list: () => fetchApi<any[]>('/geofence-routes'),
  get: (id: string) => fetchApi<any>(`/geofence-routes/${id}`),
  create: (data: any) => fetchApi<any>('/geofence-routes', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<any>(`/geofence-routes/${id}`, { method: 'DELETE' }),
};

export const routeExecutionsApi = {
  getActive: () => fetchApi<any[]>('/route-executions/active'),
  getByRoute: (routeId: string) => fetchApi<any[]>(`/route-executions/by-route/${routeId}`),
};

// ============================================
// DYNAMIC FORMS MODULE
// ============================================

export interface FormCategory {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FormTemplate {
  id: string;
  tenantId: string;
  categoryId: string;
  name: string;
  description: string | null;
  version: number;
  status: string;
  schema: unknown[];
  settings: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FormSubmission {
  id: string;
  tenantId: string;
  templateId: string;
  templateVersion: number;
  status: string;
  data: Record<string, unknown>;
  geoData: Record<string, unknown> | null;
  submittedBy: string | null;
  submittedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export const formCategoriesApi = {
  list: () => fetchApi<{ categories: FormCategory[] }>('/form-categories'),
  create: (data: { name: string; slug: string; description?: string; icon?: string; color?: string; sortOrder?: number }) =>
    fetchApi<FormCategory>('/form-categories', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  update: (id: string, data: Partial<FormCategory>) =>
    fetchApi<FormCategory>(`/form-categories/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  delete: (id: string) =>
    fetchApi<FormCategory>(`/form-categories/${id}`, { method: 'DELETE' }),
  listAccess: (id: string) =>
    fetchApi<{ rules: unknown[] }>(`/form-categories/${id}/access`),
  setAccess: (id: string, data: { groupId?: string; userId?: string; accessLevel: string }) =>
    fetchApi<unknown>(`/form-categories/${id}/access`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  removeAccess: (id: string, ruleId: string) =>
    fetchApi<unknown>(`/form-categories/${id}/access/${ruleId}`, { method: 'DELETE' }),
};

export const formTemplatesApi = {
  list: (opts?: { categoryId?: string; status?: string }) => {
    const params = new URLSearchParams();
    if (opts?.categoryId) params.set('categoryId', opts.categoryId);
    if (opts?.status) params.set('status', opts.status);
    const qs = params.toString();
    return fetchApi<{ templates: FormTemplate[] }>(`/form-templates${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => fetchApi<FormTemplate>(`/form-templates/${id}`),
  create: (data: { categoryId: string; name: string; description?: string; schema?: unknown[]; settings?: Record<string, unknown> }) =>
    fetchApi<FormTemplate>('/form-templates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  update: (id: string, data: { name?: string; description?: string; schema?: unknown[]; settings?: Record<string, unknown>; status?: string }) =>
    fetchApi<FormTemplate>(`/form-templates/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  publish: (id: string) =>
    fetchApi<FormTemplate>(`/form-templates/${id}/publish`, { method: 'POST' }),
  archive: (id: string) =>
    fetchApi<FormTemplate>(`/form-templates/${id}/archive`, { method: 'POST' }),
};

export const formSubmissionsApi = {
  list: (opts?: { templateId?: string; status?: string; submittedBy?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (opts?.templateId) params.set('templateId', opts.templateId);
    if (opts?.status) params.set('status', opts.status);
    if (opts?.submittedBy) params.set('submittedBy', opts.submittedBy);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return fetchApi<{ submissions: FormSubmission[]; total: number }>(`/form-submissions${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => fetchApi<FormSubmission>(`/form-submissions/${id}`),
  submit: (
    templateId: string,
    data: Record<string, unknown>,
    notes?: string,
    metadata?: Record<string, unknown>
  ) =>
    fetchApi<
      FormSubmission & {
        quiz: {
          score: number;
          maxScore: number;
          pointsEarned: number;
          passed: boolean;
          passingScore: number;
          correctFieldIds: string[];
          incorrectFieldIds: string[];
          gradedAt: string;
        } | null;
        quizOutcome: {
          graded: boolean;
          result?: {
            score: number;
            passed: boolean;
            passingScore: number;
            correctFieldIds: string[];
            incorrectFieldIds: string[];
          };
          enrollmentCompleted?: boolean;
          skillsGranted?: string[];
        } | null;
      }
    >('/form-submissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateId, data, notes, metadata }),
    }),
  update: (id: string, data: { data?: Record<string, unknown>; notes?: string; status?: string }) =>
    fetchApi<FormSubmission>(`/form-submissions/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  approve: (id: string) =>
    fetchApi<FormSubmission>(`/form-submissions/${id}/approve`, { method: 'POST' }),
  reject: (id: string, reason?: string) =>
    fetchApi<FormSubmission>(`/form-submissions/${id}/reject`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }),
  delete: (id: string) =>
    fetchApi<FormSubmission>(`/form-submissions/${id}`, { method: 'DELETE' }),
  stats: () => fetchApi<{ categories: number; templates: number; submissions: number; submissionsLast7d: number }>('/form-submissions/stats'),
  exportCsv: (templateId: string) => `/api/v1/form-submissions/export?templateId=${templateId}&format=csv`,
};

// ============================================
// DOCUMENT MANAGEMENT API
// ============================================

export interface DocumentFolder {
  id: string;
  tenantId: string;
  parentId: string | null;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  path: string;
  depth: number;
  sortOrder: number;
  folderType: string;
  retentionDays: number | null;
  defaultAccessLevel: string;
  isSystem: boolean;
  isArchived: boolean;
  createdBy: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentMeta {
  id: string;
  tenantId: string;
  folderId: string | null;
  title: string;
  description: string | null;
  documentNumber: string | null;
  currentVersionId: string | null;
  currentVersionNumber: number;
  filename: string;
  mimeType: string;
  fileSize: number;
  storageKey: string;
  status: string;
  documentType: string;
  classification: string;
  expiresAt: string | null;
  expiryNotificationDays: number | null;
  createdBy: string;
  updatedBy: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  nextReviewAt: string | null;
  downloadCount: number;
  viewCount: number;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
}

export interface DocumentVersion {
  id: string;
  tenantId: string;
  documentId: string;
  versionNumber: number;
  filename: string;
  mimeType: string;
  fileSize: number;
  storageKey: string;
  changeNote: string | null;
  uploadedBy: string;
  checksum: string | null;
  createdAt: string;
}

export interface DocumentShareInfo {
  id: string;
  tenantId: string;
  documentId: string;
  shareToken: string;
  expiresAt: string | null;
  maxDownloads: number | null;
  downloadCount: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  shareUrl?: string;
}

export interface DocumentLink {
  id: string;
  documentId: string;
  entityType: string;
  entityId: string;
  linkType: string;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface DocumentAck {
  id: string;
  documentId: string;
  versionNumber: number;
  userId: string;
  acknowledgedAt: string;
  signature: string | null;
  notes: string | null;
}

export interface DocumentReviewInfo {
  id: string;
  tenantId: string;
  documentId: string;
  versionNumber: number;
  reviewerId: string;
  status: string;
  comments: string | null;
  assignedBy: string;
  assignedAt: string;
  completedAt: string | null;
  documentTitle?: string;
  documentNumber?: string;
}

export const documentsApi = {
  // Folders
  listFolders: (opts?: { includeArchived?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.includeArchived) params.set('includeArchived', 'true');
    const qs = params.toString();
    return fetchApi<{ folders: DocumentFolder[] }>(`/documents/folders${qs ? `?${qs}` : ''}`);
  },
  createFolder: (data: { name: string; slug: string; parentId?: string; description?: string; icon?: string; color?: string; folderType?: string; retentionDays?: number }) =>
    fetchApi<DocumentFolder>('/documents/folders', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  updateFolder: (id: string, data: Partial<DocumentFolder>) =>
    fetchApi<DocumentFolder>(`/documents/folders/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  deleteFolder: (id: string) =>
    fetchApi(`/documents/folders/${id}`, { method: 'DELETE' }),
  getFolderContents: (id: string, opts?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return fetchApi<{ subfolders: DocumentFolder[]; documents: DocumentMeta[]; total: number }>(`/documents/folders/${id}/contents${qs ? `?${qs}` : ''}`);
  },
  seedFolders: (selectedSlugs?: string[]) =>
    fetchApi<{ folders: DocumentFolder[]; count: number }>('/documents/folders/seed', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(selectedSlugs ? { selectedSlugs } : {}) }),
  getSeedTemplates: () =>
    fetchApi<{ templates: Array<{ slug: string; name: string; folderType: string; icon: string; description: string; children: Array<{ name: string; slug: string; folderType: string }> }> }>('/documents/folders/seed/templates'),

  // Documents
  list: (opts?: { folderId?: string; status?: string; documentType?: string; classification?: string; search?: string; tags?: string[]; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (opts?.folderId) params.set('folderId', opts.folderId);
    if (opts?.status) params.set('status', opts.status);
    if (opts?.documentType) params.set('documentType', opts.documentType);
    if (opts?.classification) params.set('classification', opts.classification);
    if (opts?.search) params.set('search', opts.search);
    if (opts?.tags?.length) params.set('tags', opts.tags.join(','));
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return fetchApi<{ items: DocumentMeta[]; total: number }>(`/documents${qs ? `?${qs}` : ''}`);
  },
  getById: (id: string) => fetchApi<DocumentMeta>(`/documents/${id}`),
  update: (id: string, data: Record<string, unknown>) =>
    fetchApi<DocumentMeta>(`/documents/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi(`/documents/${id}`, { method: 'DELETE' }),
  deletePermanent: (id: string) => fetchApi(`/documents/${id}?permanent=true`, { method: 'DELETE' }),
  getDownloadUrl: (id: string) => fetchApi<{ url: string; filename: string; mimeType: string }>(`/documents/${id}/download`),
  getPreviewUrl: (id: string) => fetchApi<{ url: string; filename: string; mimeType: string }>(`/documents/${id}/preview`),

  // Upload (multipart — uses raw fetch with 401 retry)
  upload: async (files: File[], opts?: { folderId?: string; documentType?: string; classification?: string; title?: string; description?: string; tags?: string[] }) => {
    const buildFormData = () => {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      if (opts?.folderId) formData.append('folderId', opts.folderId);
      if (opts?.documentType) formData.append('documentType', opts.documentType);
      if (opts?.classification) formData.append('classification', opts.classification);
      if (opts?.title) formData.append('title', opts.title);
      if (opts?.description) formData.append('description', opts.description);
      if (opts?.tags?.length) formData.append('tags', opts.tags.join(','));
      return formData;
    };

    const { getAuthHeaders, refreshAuth, logout } = useAuthStore.getState();
    let response = await fetch(`${getApiBase()}/documents/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: buildFormData(),
    });

    // Handle 401 — refresh token and retry
    if (response.status === 401) {
      try {
        await refreshAuth();
        response = await fetch(`${getApiBase()}/documents/upload`, {
          method: 'POST',
          headers: useAuthStore.getState().getAuthHeaders(),
          body: buildFormData(),
        });
      } catch {
        logout();
        throw new ApiError(401, 'UNAUTHORIZED', 'Session expired');
      }
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
      throw new ApiError(response.status, err.error?.code || 'ERROR', err.error?.message || 'Upload failed');
    }
    const data = await response.json();
    return data.data as { documents: DocumentMeta[]; count: number };
  },

  uploadVersion: async (documentId: string, file: File, changeNote?: string) => {
    const buildFormData = () => {
      const formData = new FormData();
      formData.append('files', file);
      if (changeNote) formData.append('changeNote', changeNote);
      return formData;
    };
    const { getAuthHeaders, refreshAuth, logout } = useAuthStore.getState();
    let response = await fetch(`${getApiBase()}/documents/${documentId}/versions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: buildFormData(),
    });
    if (response.status === 401) {
      try {
        await refreshAuth();
        response = await fetch(`${getApiBase()}/documents/${documentId}/versions`, {
          method: 'POST',
          headers: useAuthStore.getState().getAuthHeaders(),
          body: buildFormData(),
        });
      } catch {
        logout();
        throw new ApiError(401, 'UNAUTHORIZED', 'Session expired');
      }
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
      throw new ApiError(response.status, err.error?.code || 'ERROR', err.error?.message || 'Upload failed');
    }
    const data = await response.json();
    return data.data as DocumentVersion;
  },

  // Versions
  listVersions: (id: string) => fetchApi<{ versions: DocumentVersion[] }>(`/documents/${id}/versions`),
  restoreVersion: (id: string, versionId: string) =>
    fetchApi<DocumentVersion>(`/documents/${id}/versions/${versionId}/restore`, { method: 'POST' }),
  downloadVersion: (id: string, versionId: string) =>
    fetchApi<{ url: string; filename: string }>(`/documents/${id}/versions/${versionId}/download`),

  // Tags
  listTags: () => fetchApi<{ tags: { tag: string; count: number }[] }>('/documents/tags'),
  addTags: (id: string, tags: string[]) =>
    fetchApi<{ tags: string[] }>(`/documents/${id}/tags`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tags }) }),
  removeTag: (id: string, tag: string) =>
    fetchApi(`/documents/${id}/tags/${encodeURIComponent(tag)}`, { method: 'DELETE' }),

  // Links
  getLinks: (id: string) => fetchApi<{ links: DocumentLink[] }>(`/documents/${id}/links`),
  addLink: (id: string, data: { entityType: string; entityId: string; linkType?: string; notes?: string }) =>
    fetchApi<DocumentLink>(`/documents/${id}/links`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  removeLink: (id: string, linkId: string) =>
    fetchApi(`/documents/${id}/links/${linkId}`, { method: 'DELETE' }),
  getByEntity: (entityType: string, entityId: string) =>
    fetchApi<{ documents: DocumentMeta[] }>(`/documents/by-entity/${entityType}/${entityId}`),

  // Shares
  createShare: (id: string, opts?: { password?: string; expiresInHours?: number; maxDownloads?: number }) =>
    fetchApi<DocumentShareInfo>(`/documents/${id}/share`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts || {}) }),
  listShares: (id: string) => fetchApi<{ shares: DocumentShareInfo[] }>(`/documents/${id}/shares`),
  revokeShare: (id: string, shareId: string) =>
    fetchApi(`/documents/${id}/shares/${shareId}`, { method: 'DELETE' }),

  // Acknowledgments
  acknowledge: (id: string, opts?: { signature?: string; notes?: string }) =>
    fetchApi<DocumentAck>(`/documents/${id}/acknowledge`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts || {}) }),
  getAcknowledgments: (id: string) => fetchApi<{ acknowledgments: DocumentAck[] }>(`/documents/${id}/acknowledgments`),

  // Stats/Admin
  stats: () => fetchApi<{ totalDocuments: number; totalFolders: number; totalSizeBytes: number; byStatus: Record<string, number>; byType: Record<string, number>; expiringCount: number }>('/documents/stats'),
  getExpiring: (days?: number) => fetchApi<{ documents: DocumentMeta[] }>(`/documents/expiring${days ? `?days=${days}` : ''}`),

  // Access control
  getMyAccess: (opts: { documentId?: string; folderId?: string }) => {
    const params = new URLSearchParams();
    if (opts.documentId) params.set('documentId', opts.documentId);
    if (opts.folderId) params.set('folderId', opts.folderId);
    return fetchApi<{ accessLevel: string | null }>(`/documents/my-access?${params}`);
  },

  // Approval workflow
  submitForReview: (id: string, data: { reviewerIds: string[]; notes?: string }) =>
    fetchApi<{ reviews: DocumentReviewInfo[] }>(`/documents/${id}/submit-for-review`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  approveDoc: (id: string, comments?: string) =>
    fetchApi<DocumentReviewInfo>(`/documents/${id}/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comments }) }),
  rejectDoc: (id: string, comments: string) =>
    fetchApi<DocumentReviewInfo>(`/documents/${id}/reject`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comments }) }),
  requestRevision: (id: string, comments: string) =>
    fetchApi<DocumentReviewInfo>(`/documents/${id}/request-revision`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comments }) }),
  listReviews: (id: string) =>
    fetchApi<{ reviews: DocumentReviewInfo[] }>(`/documents/${id}/reviews`),
  listPendingReviews: () =>
    fetchApi<{ reviews: (DocumentReviewInfo & { documentTitle?: string; documentNumber?: string })[] }>('/documents/pending-reviews'),
};

// ============================================
// PATROL MANAGEMENT API
// ============================================

export interface PatrolRoute {
  id: string;
  name: string;
  description?: string;
  siteId?: string;
  checkpoints: PatrolCheckpointDef[];
  estimatedDurationMin?: number;
  distanceMeters?: number;
  enabled: boolean;
  recurrenceRule?: Record<string, unknown> | null;
  maxPatrolDurationMin?: number | null;
  maxCheckpointIntervalMin?: number | null;
  maxGpsSilenceMin?: number | null;
  alertSeverity?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PatrolCheckpointDef {
  id: string;
  name: string;
  location?: { lat: number; lng: number };
  verificationMethod: 'nfc' | 'qr' | 'gps' | 'manual';
  nfcTagId?: string;
  qrCode?: string;
  radiusMeters?: number;
}

export interface Patrol {
  id: string;
  routeId: string;
  routeName: string;
  assigneeId?: string;
  assigneeName?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'missed' | 'cancelled';
  scheduledStart?: string;
  scheduledEnd?: string;
  startedAt?: string;
  completedAt?: string;
  totalCheckpoints: number;
  completedCheckpoints: number;
  gpsTrack: { lat: number; lng: number; timestamp: string }[];
  notes?: string;
  summary?: string;
  incidentIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PatrolCheckpointRecord {
  id: string;
  patrolId: string;
  checkpointId: string;
  checkpointName: string;
  sequenceIndex: number;
  status: 'verified' | 'skipped' | 'missed';
  verificationMethod?: string;
  verificationData: Record<string, unknown>;
  location?: { lat: number; lng: number; accuracy?: number };
  notes?: string;
  photoUri?: string;
  arrivedAt: string;
  createdAt: string;
}

export interface PatrolStats {
  total: number;
  scheduled: number;
  inProgress: number;
  completed: number;
  missed: number;
}

export const patrolRouteMgmtApi = {
  list: (search?: string) => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return fetchPaginatedApi<{ items: PatrolRoute[]; total: number }>(`/patrol-routes${qs}`).then(r => r.items || []);
  },
  get: (id: string) => fetchPaginatedApi<PatrolRoute>(`/patrol-routes/${id}`),
  create: (data: Partial<PatrolRoute>) =>
    fetchPaginatedApi<PatrolRoute>('/patrol-routes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  update: (id: string, data: Partial<PatrolRoute>) =>
    fetchPaginatedApi<PatrolRoute>(`/patrol-routes/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  delete: (id: string) => fetchPaginatedApi<void>(`/patrol-routes/${id}`, { method: 'DELETE' }),
};

// Helper to fetch paginated API responses (which return { items, total } directly, not wrapped in data)
async function fetchPaginatedApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const { getAuthHeaders, logout, refreshAuth } = useAuthStore.getState();
  const headers: Record<string, string> = { ...getAuthHeaders() };
  if (options.body) headers['Content-Type'] = 'application/json';
  const response = await fetch(`${getApiBase()}${endpoint}`, { ...options, cache: 'no-store', headers: { ...headers, ...options.headers } });
  if (response.status === 401) {
    try { await refreshAuth(); const r2 = await fetch(`${getApiBase()}${endpoint}`, { ...options, cache: 'no-store', headers: { ...useAuthStore.getState().getAuthHeaders(), ...options.headers } }); const d = await r2.json(); return (d.data ?? d) as T; } catch { logout(); throw new ApiError(401, 'UNAUTHORIZED', 'Session expired'); }
  }
  if (!response.ok) { const e = await response.json().catch(() => ({ error: { message: 'Request failed' } })); throw new ApiError(response.status, e.error?.code || 'ERROR', e.error?.message || 'Request failed'); }
  const data = await response.json();
  return (data.data ?? data) as T;
}

export const patrolsApi = {
  list: (opts?: { limit?: number; offset?: number; status?: string; assigneeId?: string; routeId?: string; fromDate?: string; toDate?: string }) => {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    if (opts?.status) params.set('status', opts.status);
    if (opts?.assigneeId) params.set('assigneeId', opts.assigneeId);
    if (opts?.routeId) params.set('routeId', opts.routeId);
    if (opts?.fromDate) params.set('fromDate', opts.fromDate);
    if (opts?.toDate) params.set('toDate', opts.toDate);
    const qs = params.toString();
    return fetchPaginatedApi<{ items: Patrol[]; total: number }>(`/patrols${qs ? `?${qs}` : ''}`).then(r => r.items || []);
  },
  get: (id: string) => fetchPaginatedApi<Patrol>(`/patrols/${id}`),
  mine: () => fetchPaginatedApi<Patrol[]>('/patrols/mine'),
  getCheckpoints: (patrolId: string) => fetchPaginatedApi<PatrolCheckpointRecord[]>(`/patrols/${patrolId}/checkpoints`),
  stats: () => fetchPaginatedApi<PatrolStats>('/patrols/stats/summary'),
};

// ============================================
// RISK ASSESSMENT API
// ============================================

export interface RiskItem { id: string; tenantId: string; riskNumber: string; title: string; description: string | null; category: string; status: string; ownerId: string | null; likelihood: number; impact: number; riskScore: number; inherentRiskScore: number | null; residualLikelihood: number | null; residualImpact: number | null; residualRiskScore: number | null; riskTreatment: string | null; siteId: string | null; tags: string[]; dueDate: string | null; createdBy: string | null; createdAt: string; updatedAt: string; controls?: RiskControlItem[]; }
export interface RiskControlItem { id: string; controlNumber: string; title: string; description: string | null; controlType: string; category: string | null; effectiveness: string; status: string; ownerId: string | null; createdAt: string; }
export interface RiskAssessmentItem { id: string; assessmentNumber: string; title: string; templateType: string; status: string; assessorId: string | null; reviewerId: string | null; scheduledDate: string | null; startedAt: string | null; completedAt: string | null; createdAt: string; }
export interface RiskVulnItem { id: string; vulnerabilityNumber: string; title: string; source: string; severity: string; cvssScore: number | null; status: string; assignedTo: string | null; remediationDueDate: string | null; createdAt: string; }
export interface RiskThreatItem { id: string; name: string; actorType: string; category: string | null; motivation: string | null; capability: string | null; likelihood: number | null; isActive: boolean; }
export interface ComplianceFrameworkItem { id: string; name: string; description: string | null; version: string | null; status: string; complianceScore: number | null; totalRequirements: number; metRequirements: number; }
export interface ComplianceRequirementItem { id: string; frameworkId: string; requirementCode: string; title: string; category: string | null; status: string; priority: string | null; }

export const riskApi = {
  // Dashboard
  getDashboard: () => fetchApi<any>('/risk/dashboard'),
  getMatrix: () => fetchApi<{ matrix: { likelihood: number; impact: number; count: number }[] }>('/risk/matrix'),
  getTopRisks: (limit?: number) => fetchApi<{ risks: RiskItem[] }>(`/risk/top${limit ? `?limit=${limit}` : ''}`),
  // Risk Register
  listRisks: (opts?: { category?: string; status?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (opts?.category) params.set('category', opts.category);
    if (opts?.status) params.set('status', opts.status);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return fetchApi<{ items: RiskItem[]; total: number }>(`/risk${qs ? `?${qs}` : ''}`);
  },
  getRisk: (id: string) => fetchApi<RiskItem>(`/risk/${id}`),
  createRisk: (data: Record<string, unknown>) => fetchApi<RiskItem>('/risk', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  updateRisk: (id: string, data: Record<string, unknown>) => fetchApi<RiskItem>(`/risk/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  deleteRisk: (id: string) => fetchApi(`/risk/${id}`, { method: 'DELETE' }),
  getRiskHistory: (id: string) => fetchApi<{ history: any[] }>(`/risk/${id}/history`),
  // Controls
  listControls: (opts?: { limit?: number }) => { const qs = opts?.limit ? `?limit=${opts.limit}` : ''; return fetchApi<{ items: RiskControlItem[]; total: number }>(`/risk/controls${qs}`); },
  createControl: (data: Record<string, unknown>) => fetchApi<RiskControlItem>('/risk/controls', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  updateControl: (id: string, data: Record<string, unknown>) => fetchApi<RiskControlItem>(`/risk/controls/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  mapControlToRisk: (controlId: string, riskId: string) => fetchApi(`/risk/controls/${controlId}/risks/${riskId}`, { method: 'POST' }),
  unmapControlFromRisk: (controlId: string, riskId: string) => fetchApi(`/risk/controls/${controlId}/risks/${riskId}`, { method: 'DELETE' }),
  // Assessments
  listAssessments: (opts?: { status?: string }) => { const qs = opts?.status ? `?status=${opts.status}` : ''; return fetchApi<{ items: RiskAssessmentItem[]; total: number }>(`/risk/assessments${qs}`); },
  createAssessment: (data: Record<string, unknown>) => fetchApi<RiskAssessmentItem>('/risk/assessments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  transitionAssessment: (id: string, status: string) => fetchApi<RiskAssessmentItem>(`/risk/assessments/${id}/transition`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) }),
  getAssessmentItems: (id: string) => fetchApi<{ items: any[] }>(`/risk/assessments/${id}`),
  createAssessmentItem: (assessmentId: string, data: Record<string, unknown>) => fetchApi<any>(`/risk/assessments/${assessmentId}/items`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  updateAssessmentItem: (itemId: string, data: Record<string, unknown>) => fetchApi<any>(`/risk/assessments/items/${itemId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  // Vulnerabilities
  listVulnerabilities: (opts?: { severity?: string; status?: string }) => { const p = new URLSearchParams(); if (opts?.severity) p.set('severity', opts.severity); if (opts?.status) p.set('status', opts.status); const qs = p.toString(); return fetchApi<{ items: RiskVulnItem[]; total: number }>(`/risk/vulnerabilities${qs ? `?${qs}` : ''}`); },
  createVulnerability: (data: Record<string, unknown>) => fetchApi<RiskVulnItem>('/risk/vulnerabilities', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  updateVulnerability: (id: string, data: Record<string, unknown>) => fetchApi<RiskVulnItem>(`/risk/vulnerabilities/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  getVulnAging: () => fetchApi<Record<string, number>>('/risk/vulnerabilities/aging'),
  // Threats
  listThreats: () => fetchApi<{ threats: RiskThreatItem[] }>('/risk/threats'),
  createThreat: (data: Record<string, unknown>) => fetchApi<RiskThreatItem>('/risk/threats', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  // Compliance
  listFrameworks: () => fetchApi<{ frameworks: ComplianceFrameworkItem[] }>('/risk/compliance/frameworks'),
  createFramework: (data: Record<string, unknown>) => fetchApi<ComplianceFrameworkItem>('/risk/compliance/frameworks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  listRequirements: (frameworkId: string) => fetchApi<{ requirements: ComplianceRequirementItem[] }>(`/risk/compliance/frameworks/${frameworkId}/requirements`),
  createRequirement: (frameworkId: string, data: Record<string, unknown>) => fetchApi<ComplianceRequirementItem>(`/risk/compliance/frameworks/${frameworkId}/requirements`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  updateRequirement: (id: string, data: Record<string, unknown>) => fetchApi<ComplianceRequirementItem>(`/risk/compliance/requirements/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  getGapAnalysis: (frameworkId: string) => fetchApi<{ gaps: ComplianceRequirementItem[] }>(`/risk/compliance/frameworks/${frameworkId}/gap-analysis`),
  // Seed Data
  getSeedCatalog: () => fetchApi<{
    controls: { count: number; byType: Record<string, number> };
    threats: { count: number; types: string[] };
    risks: { count: number; categories: string[] };
    frameworks: Array<{ slug: string; name: string; description: string; version: string; authority: string; requirementCount: number }>;
  }>('/risk/seed/catalog'),
  seedData: (options: { controls?: boolean; threats?: boolean; risks?: boolean; frameworks?: string[] }) =>
    fetchApi<{ controls: number; threats: number; risks: number; frameworks: number; requirements: number }>('/risk/seed', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(options) }),
};

// ============================================
// WORK ORDERS API (Maintenance module — Phase 1)
// ============================================

export type WorkOrderStatus =
  | 'open'
  | 'acknowledged'
  | 'in_progress'
  | 'on_site'
  | 'escalated'
  | 'closed'
  | 'reopened';

export type WorkOrderCategory =
  | 'camera'
  | 'card_reader'
  | 'door_hardware'
  | 'fire_panel'
  | 'sensor'
  | 'nvr'
  | 'network'
  | 'server'
  | 'hvac'
  | 'other';

export type WorkOrderSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';
export type WorkOrderSource = 'manual' | 'auto_rule' | 'patrol' | 'pm_schedule';

export interface WorkOrder {
  id: string;
  tenantId: string;
  workOrderNumber: string;
  title: string;
  description: string | null;
  category: WorkOrderCategory;
  faultType: string | null;
  severity: WorkOrderSeverity;
  priority: string;
  status: WorkOrderStatus;
  source: WorkOrderSource;
  deviceId: string | null;
  siteId: string | null;
  buildingId: string | null;
  floorId: string | null;
  reportedBy: string | null;
  reportedAt: string;
  currentAssigneeId: string | null;
  currentStep: number;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolution: string | null;
  closedBy: string | null;
  closedAt: string | null;
  dueAt: string | null;
  slaDeadline: string | null;
  externalTicketId: string | null;
  externalTicketUrl: string | null;
  externalIntegrationId: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface WorkOrderTimelineEntry {
  id: string;
  workOrderId: string;
  timestamp: string;
  type: string;
  userId: string | null;
  content: string;
  data: Record<string, unknown>;
  visibility: 'user' | 'system';
}

export interface WorkOrderAssignment {
  id: string;
  workOrderId: string;
  stepNumber: number;
  assigneeId: string;
  assignedAt: string;
  acceptedAt: string | null;
  completedAt: string | null;
  rejectedAt: string | null;
  rejectReason: string | null;
  status: string;
  dispatchResult: Record<string, unknown>;
  primaryExternal: boolean;
}

export interface WorkOrderPart {
  id: string;
  workOrderId: string;
  sku: string | null;
  name: string;
  qty: number;
  unitCostCents: number | null;
  status: string;
  orderedAt: string | null;
  receivedAt: string | null;
  vendorId: string | null;
  notes: string | null;
}

export interface WorkOrderDetail extends WorkOrder {
  timeline: WorkOrderTimelineEntry[];
  assignments: WorkOrderAssignment[];
  parts: WorkOrderPart[];
}

export interface CreateWorkOrderDto {
  title: string;
  description?: string;
  category: WorkOrderCategory;
  faultType?: string;
  severity?: WorkOrderSeverity;
  priority?: string;
  source?: WorkOrderSource;
  deviceId?: string;
  siteId?: string;
  buildingId?: string;
  floorId?: string;
  dueAt?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface WorkOrderRuleTrace {
  ruleId: string;
  ruleName: string;
  matched: boolean;
  failedCondition?: { field: string; operator: string; value: unknown; actual: unknown };
}

export interface WorkOrderRoutingSummary {
  matchedRule: { id: string; name: string } | null;
  assignee: { id: string; kind: string; name: string } | null;
  step: number | null;
  dispatchResult: {
    success: boolean;
    message: string;
    attempts: number;
    error?: string;
  } | null;
  trace: WorkOrderRuleTrace[];
}

export interface CreateWorkOrderResult {
  workOrder: WorkOrder;
  routing: WorkOrderRoutingSummary | null;
}

export const workOrdersApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: WorkOrderStatus | WorkOrderStatus[];
    category?: WorkOrderCategory | WorkOrderCategory[];
    severity?: WorkOrderSeverity | WorkOrderSeverity[];
    siteId?: string;
    deviceId?: string;
    assigneeId?: string;
    unassigned?: boolean;
    search?: string;
  }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.status) {
      sp.set('status', Array.isArray(params.status) ? params.status.join(',') : params.status);
    }
    if (params?.category) {
      sp.set(
        'category',
        Array.isArray(params.category) ? params.category.join(',') : params.category
      );
    }
    if (params?.severity) {
      sp.set(
        'severity',
        Array.isArray(params.severity) ? params.severity.join(',') : params.severity
      );
    }
    if (params?.siteId) sp.set('siteId', params.siteId);
    if (params?.deviceId) sp.set('deviceId', params.deviceId);
    if (params?.assigneeId) sp.set('assigneeId', params.assigneeId);
    if (params?.unassigned) sp.set('unassigned', 'true');
    if (params?.search) sp.set('search', params.search);
    const q = sp.toString();
    return fetchApi<{ items: WorkOrder[]; total: number; page: number; limit: number; hasMore: boolean }>(
      `/work-orders${q ? `?${q}` : ''}`
    );
  },

  get: (id: string) => fetchApi<WorkOrderDetail>(`/work-orders/${id}`),

  create: (data: CreateWorkOrderDto) =>
    fetchApi<CreateWorkOrderResult>('/work-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<CreateWorkOrderDto> & { resolution?: string }) =>
    fetchApi<WorkOrder>(`/work-orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/work-orders/${id}`, { method: 'DELETE' }),

  transition: (id: string, toStatus: WorkOrderStatus, opts?: { reason?: string; resolution?: string }) =>
    fetchApi<WorkOrder>(`/work-orders/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ toStatus, ...opts }),
    }),

  addNote: (id: string, content: string, visibility: 'user' | 'system' = 'user') =>
    fetchApi<WorkOrderTimelineEntry>(`/work-orders/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content, visibility }),
    }),

  getTimeline: (id: string, opts?: { visibility?: 'user' | 'system' | 'all'; limit?: number }) => {
    const sp = new URLSearchParams();
    if (opts?.visibility) sp.set('visibility', opts.visibility);
    if (opts?.limit) sp.set('limit', String(opts.limit));
    const q = sp.toString();
    return fetchApi<WorkOrderTimelineEntry[]>(`/work-orders/${id}/timeline${q ? `?${q}` : ''}`);
  },

  listParts: (id: string) => fetchApi<WorkOrderPart[]>(`/work-orders/${id}/parts`),

  addPart: (
    id: string,
    part: { sku?: string; name: string; qty?: number; unitCostCents?: number; vendorId?: string; notes?: string }
  ) =>
    fetchApi<WorkOrderPart>(`/work-orders/${id}/parts`, {
      method: 'POST',
      body: JSON.stringify(part),
    }),

  updatePart: (id: string, partId: string, patch: Partial<WorkOrderPart>) =>
    fetchApi<WorkOrderPart>(`/work-orders/${id}/parts/${partId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deletePart: (id: string, partId: string) =>
    fetchApi<{ deleted: boolean }>(`/work-orders/${id}/parts/${partId}`, { method: 'DELETE' }),

  rejectStep: (id: string, data: { reason: string; addTags?: string[]; removeTags?: string[]; comment?: string }) =>
    fetchApi<{ workOrder: WorkOrder; routing: WorkOrderRoutingSummary | null }>(
      `/work-orders/${id}/reject-step`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  escalateNow: (id: string, reason: string) =>
    fetchApi<{ workOrder: WorkOrder; routing: WorkOrderRoutingSummary | null }>(
      `/work-orders/${id}/escalate-now`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  commentExternal: (id: string, comment: string) =>
    fetchApi<{ success: boolean }>(`/work-orders/${id}/comment-external`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    }),
};

// ============================================
// ASSIGNEES API (Work Order Routing — Phase 2)
// ============================================

export type AssigneeKind =
  | 'internal_user'
  | 'internal_team'
  | 'external_integration'
  | 'external_email';

export interface Assignee {
  id: string;
  tenantId: string;
  kind: AssigneeKind;
  name: string;
  description: string | null;
  config: Record<string, unknown>;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export const assigneesApi = {
  list: (params?: { kind?: AssigneeKind; enabledOnly?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.kind) sp.set('kind', params.kind);
    if (params?.enabledOnly) sp.set('enabledOnly', 'true');
    const q = sp.toString();
    return fetchApi<Assignee[]>(`/assignees${q ? `?${q}` : ''}`);
  },
  get: (id: string) => fetchApi<Assignee>(`/assignees/${id}`),
  create: (data: { kind: AssigneeKind; name: string; description?: string; config?: Record<string, unknown>; enabled?: boolean }) =>
    fetchApi<Assignee>('/assignees', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ kind: AssigneeKind; name: string; description: string; config: Record<string, unknown>; enabled: boolean }>) =>
    fetchApi<Assignee>(`/assignees/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ deleted: boolean }>(`/assignees/${id}`, { method: 'DELETE' }),
};

// ============================================
// ROUTING RULES API (Work Order Routing — Phase 2)
// ============================================

export interface RoutingCondition {
  field: string;
  operator: string;
  value: unknown;
}

export interface RoutingConditionGroup {
  operator?: 'AND' | 'OR' | 'NOT';
  conditions?: RoutingCondition[];
  groups?: RoutingConditionGroup[];
}

export interface RoutingFlowStep {
  stepNumber: number;
  assigneeId: string;
  escalateAfterMinutes?: number | null;
  notifyChannels?: string[];
  onFailure?: {
    retryCount?: number;
    retryBackoffSeconds?: number;
    fallbackAssigneeId?: string;
  };
}

export interface RoutingFlow {
  steps: RoutingFlowStep[];
  stopOnAcceptance?: boolean;
  parallelDispatch?: boolean;
}

export interface RoutingRule {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  conditions: RoutingConditionGroup;
  flow: RoutingFlow;
  flowTemplateId: string | null;
  priority: number;
  stopProcessing: boolean;
  enabled: boolean;
  matchCount: number;
  lastMatchedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const routingRulesApi = {
  list: () => fetchApi<RoutingRule[]>('/routing-rules'),
  get: (id: string) => fetchApi<RoutingRule>(`/routing-rules/${id}`),
  create: (data: {
    name: string;
    description?: string;
    conditions?: RoutingConditionGroup;
    flow: RoutingFlow;
    flowTemplateId?: string | null;
    priority?: number;
    stopProcessing?: boolean;
    enabled?: boolean;
  }) => fetchApi<RoutingRule>('/routing-rules', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; description: string; conditions: RoutingConditionGroup; flow: RoutingFlow; flowTemplateId: string | null; priority: number; stopProcessing: boolean; enabled: boolean }>) =>
    fetchApi<RoutingRule>(`/routing-rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ deleted: boolean }>(`/routing-rules/${id}`, { method: 'DELETE' }),
  reorder: (orderedIds: string[]) =>
    fetchApi<{ reordered: number }>('/routing-rules/reorder', {
      method: 'POST',
      body: JSON.stringify({ orderedIds }),
    }),
  simulate: (body: { workOrderId?: string; sampleWorkOrder?: { category: string; faultType?: string; severity?: string; priority?: string; tags?: string[] } }) =>
    fetchApi<{ matchedRule: { id: string; name: string; flow: RoutingFlow } | null; trace: WorkOrderRuleTrace[] }>(
      '/routing-rules/simulate',
      { method: 'POST', body: JSON.stringify(body) }
    ),

  validateTemplate: (body: {
    template: unknown;
    integrationType?: string;
    sampleWorkOrder?: { category?: string; title?: string; severity?: string; faultType?: string; tags?: string[] };
  }) =>
    fetchApi<{
      rendered: unknown;
      referencedPaths: string[];
      escapeMode: string;
    }>('/routing-rules/validate-template', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// ============================================
// FLOW TEMPLATES API (Phase 5)
// ============================================

export interface FlowTemplate {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  flow: RoutingFlow;
  tags: string[];
  enabled: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const flowTemplatesApi = {
  list: () => fetchApi<FlowTemplate[]>('/flow-templates'),
  get: (id: string) => fetchApi<FlowTemplate>(`/flow-templates/${id}`),
  create: (data: { name: string; description?: string; flow: RoutingFlow; tags?: string[]; enabled?: boolean }) =>
    fetchApi<FlowTemplate>('/flow-templates', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; description: string; flow: RoutingFlow; tags: string[]; enabled: boolean }>) =>
    fetchApi<FlowTemplate>(`/flow-templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ deleted: boolean }>(`/flow-templates/${id}`, { method: 'DELETE' }),
};

// ============================================
// SITE FLOW BINDINGS API (Phase 5)
// ============================================

export interface SiteFlowBinding {
  id: string;
  tenantId: string;
  siteId: string | null;
  deviceCategory: string | null;
  flowTemplateId: string;
  priority: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export const siteFlowBindingsApi = {
  list: () => fetchApi<SiteFlowBinding[]>('/site-flow-bindings'),
  create: (data: {
    siteId?: string | null;
    deviceCategory?: string | null;
    flowTemplateId: string;
    priority?: number;
    enabled?: boolean;
  }) => fetchApi<SiteFlowBinding>('/site-flow-bindings', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ siteId: string | null; deviceCategory: string | null; flowTemplateId: string; priority: number; enabled: boolean }>) =>
    fetchApi<SiteFlowBinding>(`/site-flow-bindings/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ deleted: boolean }>(`/site-flow-bindings/${id}`, { method: 'DELETE' }),
};

// ============================================
// MAINTENANCE SCHEDULES API (Phase 6 — Preventive Maintenance)
// ============================================

export type PMScheduleType = 'cron' | 'interval';

export interface PMScope {
  deviceIds?: string[];
  deviceCategories?: string[];
  siteIds?: string[];
  tags?: string[];
}

export interface PMWorkOrderTemplate {
  title: string;
  description?: string;
  category?: string;
  faultType?: string;
  severity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
  priority?: string;
  tags?: string[];
}

export interface MaintenanceSchedule {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  scheduleType: PMScheduleType;
  cronExpr: string | null;
  intervalDays: number | null;
  scope: PMScope;
  workOrderTemplate: PMWorkOrderTemplate;
  rateLimitPerMinute: number;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastRunStats: Record<string, unknown> | null;
  enabled: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const maintenanceSchedulesApi = {
  list: () => fetchApi<MaintenanceSchedule[]>('/maintenance-schedules'),
  get: (id: string) => fetchApi<MaintenanceSchedule>(`/maintenance-schedules/${id}`),
  create: (data: {
    name: string;
    description?: string;
    scheduleType: PMScheduleType;
    cronExpr?: string | null;
    intervalDays?: number | null;
    scope?: PMScope;
    workOrderTemplate: PMWorkOrderTemplate;
    rateLimitPerMinute?: number;
    enabled?: boolean;
  }) =>
    fetchApi<MaintenanceSchedule>('/maintenance-schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (
    id: string,
    data: Partial<{
      name: string;
      description: string;
      scheduleType: PMScheduleType;
      cronExpr: string | null;
      intervalDays: number | null;
      scope: PMScope;
      workOrderTemplate: PMWorkOrderTemplate;
      rateLimitPerMinute: number;
      enabled: boolean;
    }>
  ) =>
    fetchApi<MaintenanceSchedule>(`/maintenance-schedules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/maintenance-schedules/${id}`, { method: 'DELETE' }),
  runNow: (id: string) =>
    fetchApi<{ matched: number; created: number; routed: number; errors: number }>(
      `/maintenance-schedules/${id}/run-now`,
      { method: 'POST' }
    ),
};

// ============================================
// NOTIFICATIONS CENTER API (cross-cutting — Phase 0)
// ============================================

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'critical';
export type EmailDigestMode = 'immediate' | 'daily' | 'weekly';

export interface Notification {
  id: string;
  tenantId: string;
  recipientUserId: string;
  sourceModule: string;
  sourceEventType: string;
  title: string;
  body: string | null;
  severity: NotificationSeverity;
  link: string | null;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  dismissedAt: string | null;
  data: Record<string, unknown>;
  channelsDelivered: Record<string, unknown>;
  createdAt: string;
  expiresAt: string | null;
  updatedAt: string;
}

export interface NotificationPreference {
  id: string;
  tenantId: string;
  userId: string;
  sourceModule: string;
  sourceEventType: string;
  emailEnabled: boolean;
  emailDigest: EmailDigestMode;
  inAppEnabled: boolean;
  pushEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export const notificationsApi = {
  list: (params?: { unreadOnly?: boolean; module?: string; since?: string; page?: number; limit?: number }) => {
    const sp = new URLSearchParams();
    if (params?.unreadOnly) sp.set('unreadOnly', 'true');
    if (params?.module) sp.set('module', params.module);
    if (params?.since) sp.set('since', params.since);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    const q = sp.toString();
    return fetchApi<{
      items: Notification[];
      total: number;
      page: number;
      limit: number;
      hasMore: boolean;
      unreadCount: number;
    }>(`/notifications${q ? `?${q}` : ''}`);
  },
  unreadCount: () => fetchApi<{ unreadCount: number }>('/notifications/unread-count'),
  markRead: (id: string) =>
    fetchApi<Notification>(`/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: (module?: string) =>
    fetchApi<{ markedRead: number }>('/notifications/mark-all-read', {
      method: 'POST',
      body: JSON.stringify({ module }),
    }),
  dismiss: (id: string) =>
    fetchApi<Notification>(`/notifications/${id}/dismiss`, { method: 'POST' }),
};

export const notificationPreferencesApi = {
  list: () => fetchApi<NotificationPreference[]>('/notification-preferences'),
  upsert: (data: {
    sourceModule: string;
    sourceEventType: string;
    emailEnabled?: boolean;
    emailDigest?: EmailDigestMode;
    inAppEnabled?: boolean;
    pushEnabled?: boolean;
  }) =>
    fetchApi<NotificationPreference>('/notification-preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// ============================================
// SKILLS API (Training module — Phase 1)
// ============================================

export type SkillSource = 'manual' | 'certification' | 'course_completion' | 'import';

export interface Skill {
  id: string;
  tenantId: string;
  slug: string;
  name: string;
  description: string | null;
  categoryId: string | null;
  icon: string | null;
  color: string | null;
  autoExpireWithCertificationId: string | null;
  enabled: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserSkill {
  id: string;
  tenantId: string;
  userId: string;
  skillId: string;
  source: SkillSource;
  sourceRef: string | null;
  acquiredAt: string;
  notes: string | null;
  grantedBy: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserSkillWithSkill extends UserSkill {
  skill: Skill | null;
}

export interface UserWithSkill extends UserSkill {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string | null;
  };
}

export interface SkillCategory {
  id: string;
  tenantId: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  sortOrder: number;
  enabled: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const skillCategoriesApi = {
  list: (enabledOnly = false) => {
    const q = enabledOnly ? '?enabledOnly=true' : '';
    return fetchApi<SkillCategory[]>(`/skill-categories${q}`);
  },
  create: (data: {
    slug: string;
    name: string;
    description?: string | null;
    icon?: string | null;
    color?: string | null;
    sortOrder?: number;
    enabled?: boolean;
  }) =>
    fetchApi<SkillCategory>('/skill-categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<SkillCategory>) =>
    fetchApi<SkillCategory>(`/skill-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/skill-categories/${id}`, { method: 'DELETE' }),
};

export const skillsApi = {
  list: (params?: { enabledOnly?: boolean; categoryId?: string }) => {
    const sp = new URLSearchParams();
    if (params?.enabledOnly) sp.set('enabledOnly', 'true');
    if (params?.categoryId) sp.set('categoryId', params.categoryId);
    const q = sp.toString();
    return fetchApi<Skill[]>(`/skills${q ? `?${q}` : ''}`);
  },
  get: (id: string) => fetchApi<Skill>(`/skills/${id}`),
  create: (data: {
    slug: string;
    name: string;
    description?: string | null;
    categoryId?: string | null;
    icon?: string | null;
    color?: string | null;
    enabled?: boolean;
  }) => fetchApi<Skill>('/skills', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Omit<Skill, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>>) =>
    fetchApi<Skill>(`/skills/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ deleted: boolean }>(`/skills/${id}`, { method: 'DELETE' }),
  usersWithSkill: (id: string) => fetchApi<UserWithSkill[]>(`/skills/${id}/users`),

  // User skills (per-user grant/revoke)
  userSkills: (userId: string, includeInactive = false) => {
    const q = includeInactive ? '?includeInactive=true' : '';
    return fetchApi<UserSkillWithSkill[]>(`/skills/users/${userId}${q}`);
  },
  grant: (userId: string, data: { skillId: string; source?: SkillSource; sourceRef?: string; notes?: string }) =>
    fetchApi<UserSkillWithSkill>(`/skills/users/${userId}/grant`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  revoke: (userId: string, skillId: string) =>
    fetchApi<UserSkill>(`/skills/users/${userId}/${skillId}`, { method: 'DELETE' }),
};

// ============================================
// TRAINING (Phase 2)
// ============================================

export type TrainingStepType = 'lesson' | 'video' | 'document' | 'quiz' | 'external_link';

export interface TrainingStep {
  id: string;
  title: string;
  type: TrainingStepType;
  content?: string;
  mediaUrl?: string;
  documentId?: string;
  /** @deprecated Use quizTemplateId for new quiz steps. Forms-as-quiz is kept for backward compat only. */
  formTemplateId?: string;
  /** Link to a Quiz module template. Takes precedence over formTemplateId when set. */
  quizTemplateId?: string;
  order: number;
  required: boolean;
  durationMinutes?: number;
}

export interface TrainingCourse {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  category: string | null;
  tags: string[];
  content: TrainingStep[];
  estimatedDurationMinutes: number | null;
  passingScore: number | null;
  prerequisiteCourseIds: string[];
  awardsSkillIds: string[];
  awardsCertificationId: string | null;
  enabled: boolean;
  version: number;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type TrainingEnrollmentStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'waived';

export interface TrainingEnrollment {
  id: string;
  tenantId: string;
  userId: string;
  courseId: string;
  assignmentId: string | null;
  assignedBy: string | null;
  assignedAt: string;
  dueAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  status: TrainingEnrollmentStatus;
  currentStepIndex: number;
  progress: Record<
    string,
    { completedAt?: string; timeSpentMs?: number; submissionId?: string; score?: number; passed?: boolean }
  >;
  finalScore: number | null;
  attempts: number;
  issuedCertificationId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TrainingEnrollmentWithCourse extends TrainingEnrollment {
  course: TrainingCourse | null;
}

export interface TrainingEnrollmentWithUser extends TrainingEnrollment {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string | null;
  };
}

export type TrainingAssignmentTargetType = 'user' | 'user_group' | 'role';

export interface TrainingAssignment {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  targetType: TrainingAssignmentTargetType;
  targetIds: string[];
  courseId: string | null;
  certificationId: string | null;
  dueDaysFromAssign: number | null;
  enabled: boolean;
  lastMaterializedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MaterializeResult {
  assignmentId: string;
  targetsResolved: number;
  enrollmentsCreated: number;
  enrollmentsExisting: number;
  notificationsQueued: number;
  notificationsFailed: number;
}

export const trainingCoursesApi = {
  list: (params?: { enabledOnly?: boolean; category?: string }) => {
    const sp = new URLSearchParams();
    if (params?.enabledOnly) sp.set('enabledOnly', 'true');
    if (params?.category) sp.set('category', params.category);
    const q = sp.toString();
    return fetchApi<TrainingCourse[]>(`/training-courses${q ? `?${q}` : ''}`);
  },
  get: (id: string) => fetchApi<TrainingCourse>(`/training-courses/${id}`),
  create: (data: Partial<TrainingCourse>) =>
    fetchApi<TrainingCourse>('/training-courses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<TrainingCourse>) =>
    fetchApi<TrainingCourse>(`/training-courses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/training-courses/${id}`, { method: 'DELETE' }),
  enrollments: (id: string) =>
    fetchApi<TrainingEnrollmentWithUser[]>(`/training-courses/${id}/enrollments`),
};

export const trainingAssignmentsApi = {
  list: (params?: { enabledOnly?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.enabledOnly) sp.set('enabledOnly', 'true');
    const q = sp.toString();
    return fetchApi<TrainingAssignment[]>(`/training-assignments${q ? `?${q}` : ''}`);
  },
  get: (id: string) => fetchApi<TrainingAssignment>(`/training-assignments/${id}`),
  create: (data: {
    name: string;
    description?: string;
    targetType: TrainingAssignmentTargetType;
    targetIds: string[];
    courseId?: string;
    dueDaysFromAssign?: number;
    enabled?: boolean;
  }) =>
    fetchApi<TrainingAssignment & { materializeResult: MaterializeResult | null }>(
      '/training-assignments',
      { method: 'POST', body: JSON.stringify(data) }
    ),
  update: (id: string, data: Partial<TrainingAssignment>) =>
    fetchApi<TrainingAssignment>(`/training-assignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/training-assignments/${id}`, { method: 'DELETE' }),
  materialize: (id: string) =>
    fetchApi<MaterializeResult>(`/training-assignments/${id}/materialize`, { method: 'POST' }),
};

export const trainingEnrollmentsApi = {
  list: (params?: { userId?: string; status?: string; all?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.userId) sp.set('userId', params.userId);
    if (params?.status) sp.set('status', params.status);
    if (params?.all) sp.set('all', 'true');
    const q = sp.toString();
    return fetchApi<TrainingEnrollment[]>(`/training-enrollments${q ? `?${q}` : ''}`);
  },
  mine: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return fetchApi<TrainingEnrollmentWithCourse[]>(`/training-enrollments/mine${q}`);
  },
  get: (id: string) =>
    fetchApi<TrainingEnrollmentWithCourse>(`/training-enrollments/${id}`),
  selfEnroll: (courseId: string) =>
    fetchApi<TrainingEnrollmentWithCourse & { created: boolean }>(
      '/training-enrollments/self-enroll',
      { method: 'POST', body: JSON.stringify({ courseId }) }
    ),
  start: (id: string) =>
    fetchApi<TrainingEnrollment>(`/training-enrollments/${id}/start`, { method: 'POST' }),
  markStep: (
    id: string,
    data: {
      stepId: string;
      timeSpentMs?: number;
      submissionId?: string;
      score?: number;
      passed?: boolean;
      advance?: boolean;
    }
  ) =>
    fetchApi<TrainingEnrollment>(`/training-enrollments/${id}/step`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  complete: (id: string, data?: { finalScore?: number; autoAwardSkills?: boolean }) =>
    fetchApi<TrainingEnrollment & { skillsGranted: string[] }>(
      `/training-enrollments/${id}/complete`,
      { method: 'POST', body: JSON.stringify(data ?? {}) }
    ),
};

// ============================================
// CERTIFICATIONS (Phase 4)
// ============================================

export type CertificationCategory =
  | 'regulatory'
  | 'training'
  | 'clearance'
  | 'license'
  | 'medical'
  | string;

export interface Certification {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  category: string | null;
  issuer: string | null;
  validityMonths: number | null;
  reminderDaysBeforeExpiry: number[];
  requiredCourseId: string | null;
  renewalCourseId: string | null;
  requiredEvidenceTypes: string[];
  awardsSkillIds: string[];
  isMandatoryForRoles: string[];
  enabled: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CertificationStatus = 'active' | 'expiring_soon' | 'expired' | 'revoked';

export interface CertificationIssued {
  id: string;
  tenantId: string;
  userId: string;
  certificationId: string;
  issuedAt: string;
  expiresAt: string | null;
  status: CertificationStatus;
  evidence: Array<Record<string, unknown>>;
  approvedBy: string | null;
  approvedAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revokeReason: string | null;
  externalCertificateNumber: string | null;
  issuingEnrollmentId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CertificationIssuedWithUser extends CertificationIssued {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string | null;
  };
}

export const certificationsApi = {
  list: (params?: { enabledOnly?: boolean; category?: string }) => {
    const sp = new URLSearchParams();
    if (params?.enabledOnly) sp.set('enabledOnly', 'true');
    if (params?.category) sp.set('category', params.category);
    const q = sp.toString();
    return fetchApi<Certification[]>(`/certifications${q ? `?${q}` : ''}`);
  },
  get: (id: string) => fetchApi<Certification>(`/certifications/${id}`),
  create: (data: Partial<Certification>) =>
    fetchApi<Certification>('/certifications', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<Certification>) =>
    fetchApi<Certification>(`/certifications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/certifications/${id}`, { method: 'DELETE' }),
  coverage: (id: string, userIds: string[]) => {
    const q = userIds.length ? `?userIds=${userIds.join(',')}` : '';
    return fetchApi<{
      certificationId: string;
      total: number;
      covered: number;
      missing: number;
      coveredUserIds: string[];
      missingUserIds: string[];
    }>(`/certifications/${id}/coverage${q}`);
  },
};

export const certificationsIssuedApi = {
  list: (params?: { userId?: string; certId?: string; status?: string }) => {
    const sp = new URLSearchParams();
    if (params?.userId) sp.set('userId', params.userId);
    if (params?.certId) sp.set('certId', params.certId);
    if (params?.status) sp.set('status', params.status);
    const q = sp.toString();
    return fetchApi<CertificationIssued[] | CertificationIssuedWithUser[]>(
      `/certifications-issued${q ? `?${q}` : ''}`
    );
  },
  mine: () => fetchApi<CertificationIssued[]>('/certifications-issued/mine'),
  get: (id: string) => fetchApi<CertificationIssued>(`/certifications-issued/${id}`),
  expiring: (days?: number) => {
    const q = days ? `?days=${days}` : '';
    return fetchApi<CertificationIssued[]>(`/certifications-issued/expiring${q}`);
  },
  issue: (data: {
    userId: string;
    certificationId: string;
    issuedAt?: string;
    expiresAt?: string | null;
    externalCertificateNumber?: string;
    notes?: string;
    evidence?: Array<Record<string, unknown>>;
  }) =>
    fetchApi<CertificationIssued>('/certifications-issued', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  revoke: (id: string, reason: string) =>
    fetchApi<CertificationIssued>(`/certifications-issued/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  uploadEvidence: async (id: string, file: File, description?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (description) form.append('description', description);
    // Multipart — can't use fetchApi (forces JSON content-type); use the
    // auth store's getAuthHeaders so this matches the rest of the app.
    const { getAuthHeaders } = useAuthStore.getState();
    const res = await fetch(`${getApiBase()}/certifications-issued/${id}/upload-evidence`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Upload failed: ${res.status} ${text}`);
    }
    return (await res.json()) as { success: boolean; data: CertificationIssued };
  },
  evidenceUrl: (id: string, idx: number) =>
    fetchApi<{ url: string; filename?: string; mimetype?: string }>(
      `/certifications-issued/${id}/evidence/${idx}`
    ),
};

// ============================================
// COMPLIANCE REPORTS (Phase 5)
// ============================================

export interface CoverageRow {
  certificationId: string;
  certification: Certification;
  total: number;
  covered: number;
  missing: number;
  expiringSoon: number;
  coveredUserIds: string[];
  missingUserIds: string[];
}

export interface StaffGap {
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string | null;
    roles?: string[];
  };
  missing: Array<{ certificationId: string; name: string; reason: string }>;
}

export interface ExpiringBuckets {
  within7d: number;
  within30d: number;
  within90d: number;
  expired: number;
  total: number;
}

export interface QualificationCheckResult {
  qualified: boolean;
  missingSkillIds: string[];
  missingCertificationIds: string[];
  missingSkillNames: string[];
  missingCertificationNames: string[];
}

export const complianceReportsApi = {
  coverageMatrix: (role?: string) => {
    const q = role ? `?role=${encodeURIComponent(role)}` : '';
    return fetchApi<CoverageRow[]>(`/compliance-reports/coverage-matrix${q}`);
  },
  coverageForCertification: (certId: string, opts?: { role?: string; userIds?: string[] }) => {
    const sp = new URLSearchParams();
    if (opts?.role) sp.set('role', opts.role);
    if (opts?.userIds?.length) sp.set('userIds', opts.userIds.join(','));
    const q = sp.toString();
    return fetchApi<CoverageRow>(`/compliance-reports/coverage/${certId}${q ? `?${q}` : ''}`);
  },
  staffGaps: () => fetchApi<StaffGap[]>('/compliance-reports/staff-gaps'),
  expiringSummary: () => fetchApi<ExpiringBuckets>('/compliance-reports/expiring-summary'),
  qualificationCheck: (data: {
    userId: string;
    requiredSkillIds?: string[];
    requiredCertificationIds?: string[];
  }) =>
    fetchApi<QualificationCheckResult>('/compliance-reports/qualification-check', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  bulkQualificationCheck: (data: {
    userIds: string[];
    requiredSkillIds?: string[];
    requiredCertificationIds?: string[];
  }) =>
    fetchApi<Array<{ userId: string; check: QualificationCheckResult }>>(
      '/compliance-reports/bulk-qualification-check',
      { method: 'POST', body: JSON.stringify(data) }
    ),
};

// ─────────────────────────────────────────────────────────────────
// Quiz Module V1 — standalone LMS quiz engine
// ─────────────────────────────────────────────────────────────────

export type QuizQuestionType =
  | 'multiple_choice_single'
  | 'multiple_choice_multi'
  | 'true_false'
  | 'ordering'
  | 'matching'
  | 'fill_in_blank'
  | 'numeric'
  | 'short_answer';

export type QuizDifficulty = 'easy' | 'medium' | 'hard';

export type QuizAssemblyMode = 'explicit' | 'random_from_pool';
export type QuizScoringStrategy = 'points' | 'percentage' | 'pass_fail';
export type QuizShowAnswersPolicy = 'never' | 'after_pass' | 'after_any_attempt' | 'after_final_attempt';
export type QuizShowScorePolicy = 'immediate' | 'after_cooldown' | 'never';

export type QuizTemplateStatus = 'draft' | 'published' | 'archived';
export type QuizAttemptStatus = 'in_progress' | 'submitted' | 'expired' | 'graded' | 'graded_pending';

export interface QuizQuestionCategory {
  id: string;
  tenantId: string;
  slug: string;
  name: string;
  description: string | null;
  color: string;
  sortOrder: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface QuizQuestion {
  id: string;
  tenantId: string;
  categoryId: string | null;
  questionType: QuizQuestionType;
  prompt: string;
  explanation: string | null;
  difficulty: QuizDifficulty;
  tags: string[];
  payload: Record<string, unknown>;
  defaultPoints: number;
  enabled: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface QuizTemplate {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  category: string | null;
  status: QuizTemplateStatus;
  version: number;
  assemblyMode: QuizAssemblyMode;
  questionIds: Array<{ questionId: string; points?: number }>;
  poolConfig: {
    categoryIds?: string[];
    difficulty?: string;
    tags?: string[];
    pickCount?: number;
  };
  shuffleQuestions: boolean;
  timeLimitMinutes: number | null;
  maxAttempts: number | null;
  cooldownMinutes: number;
  passingScore: number | null;
  scoringStrategy: QuizScoringStrategy;
  showAnswersPolicy: QuizShowAnswersPolicy;
  showScorePolicy: QuizShowScorePolicy;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface QuizAttempt {
  id: string;
  tenantId: string;
  templateId: string;
  templateVersion: number;
  userId: string;
  enrollmentId: string | null;
  courseId: string | null;
  stepId: string | null;
  status: QuizAttemptStatus;
  startedAt: string;
  expiresAt: string | null;
  submittedAt: string | null;
  gradedAt: string | null;
  questionSnapshot: Array<Record<string, unknown>>;
  answers: Record<string, { value: unknown; answeredAt?: string; timeSpentMs?: number }>;
  scorePoints: number | null;
  scoreMaxPoints: number | null;
  scorePercent: number | null;
  passed: boolean | null;
  perQuestionResults: Array<Record<string, unknown>>;
  attemptNumber: number;
  metadata: Record<string, unknown>;
}

export interface QuizAttemptStartResult {
  attemptId: string;
  templateVersion: number;
  questions: Array<{
    questionId: string;
    questionType: QuizQuestionType;
    prompt: string;
    explanation?: string;
    payload: Record<string, unknown>;
    points: number;
  }>;
  expiresAt: string | null;
  attemptNumber: number;
  maxAttempts: number | null;
  shuffle: boolean;
}

export interface QuizAttemptSubmitResult {
  attemptId: string;
  scorePercent: number;
  scorePoints: number;
  scoreMaxPoints: number;
  passed: boolean;
  status: QuizAttemptStatus;
  reviewAvailable: boolean;
  reviewPolicy: string;
}

export interface QuizAttemptReview {
  attemptId: string;
  policy: string;
  visible: boolean;
  reason?: string;
  items?: Array<{
    questionId: string;
    prompt: string;
    explanation?: string | null;
    payload: Record<string, unknown>;
    learnerAnswer: unknown;
    correct: boolean;
    pointsEarned: number;
    pointsMax: number;
    manualPending?: boolean;
  }>;
}

export interface QuizTemplateAnalytics {
  templateId: string;
  totalAttempts: number;
  submittedAttempts: number;
  passRate: number;
  avgScorePercent: number;
  avgCompletionSeconds: number | null;
  perQuestion: Array<{
    questionId: string;
    prompt: string;
    attemptCount: number;
    correctCount: number;
    passRate: number;
    avgTimeSeconds: number | null;
  }>;
}

export const quizQuestionCategoriesApi = {
  list: () => fetchApi<QuizQuestionCategory[]>('/quiz-question-categories'),
  create: (data: {
    slug: string;
    name: string;
    description?: string | null;
    color?: string;
    sortOrder?: number;
  }) =>
    fetchApi<QuizQuestionCategory>('/quiz-question-categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<QuizQuestionCategory>) =>
    fetchApi<QuizQuestionCategory>(`/quiz-question-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/quiz-question-categories/${id}`, { method: 'DELETE' }),
};

export const quizQuestionsApi = {
  list: (params?: {
    q?: string;
    categoryId?: string;
    questionType?: QuizQuestionType;
    difficulty?: QuizDifficulty;
    enabledOnly?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set('q', params.q);
    if (params?.categoryId) sp.set('categoryId', params.categoryId);
    if (params?.questionType) sp.set('questionType', params.questionType);
    if (params?.difficulty) sp.set('difficulty', params.difficulty);
    if (params?.enabledOnly === false) sp.set('enabledOnly', 'false');
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.offset) sp.set('offset', String(params.offset));
    const q = sp.toString();
    return fetchApi<QuizQuestion[]>(`/quiz-questions${q ? `?${q}` : ''}`);
  },
  get: (id: string) => fetchApi<QuizQuestion>(`/quiz-questions/${id}`),
  create: (data: {
    categoryId?: string | null;
    questionType: QuizQuestionType;
    prompt: string;
    explanation?: string | null;
    difficulty?: QuizDifficulty;
    tags?: string[];
    payload: Record<string, unknown>;
    defaultPoints?: number;
    enabled?: boolean;
  }) =>
    fetchApi<QuizQuestion>('/quiz-questions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<QuizQuestion>) =>
    fetchApi<QuizQuestion>(`/quiz-questions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/quiz-questions/${id}`, { method: 'DELETE' }),
};

export const quizTemplatesApi = {
  list: (params?: { status?: QuizTemplateStatus }) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    const q = sp.toString();
    return fetchApi<QuizTemplate[]>(`/quiz-templates${q ? `?${q}` : ''}`);
  },
  get: (id: string) => fetchApi<QuizTemplate>(`/quiz-templates/${id}`),
  create: (data: Partial<QuizTemplate>) =>
    fetchApi<QuizTemplate>('/quiz-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<QuizTemplate>) =>
    fetchApi<QuizTemplate>(`/quiz-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/quiz-templates/${id}`, { method: 'DELETE' }),
  publish: (id: string) =>
    fetchApi<QuizTemplate>(`/quiz-templates/${id}/publish`, { method: 'POST' }),
  archive: (id: string) =>
    fetchApi<QuizTemplate>(`/quiz-templates/${id}/archive`, { method: 'POST' }),
  analytics: (id: string) =>
    fetchApi<QuizTemplateAnalytics>(`/quiz-templates/${id}/analytics`),
  exportTemplate: (id: string) =>
    fetchApi<{ template: QuizTemplate; questions: QuizQuestion[] }>(
      `/quiz-templates/${id}/export`
    ),
  import: (data: { template: Record<string, unknown>; questions: Array<Record<string, unknown>> }) =>
    fetchApi<{ template: QuizTemplate; importedQuestionCount: number }>(
      `/quiz-templates/import`,
      { method: 'POST', body: JSON.stringify(data) }
    ),
};

export const quizAttemptsApi = {
  start: (data: {
    templateId: string;
    enrollmentId?: string | null;
    courseId?: string | null;
    stepId?: string | null;
    metadata?: Record<string, unknown>;
  }) =>
    fetchApi<QuizAttemptStartResult>('/quiz-attempts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  get: (id: string) => fetchApi<QuizAttempt>(`/quiz-attempts/${id}`),
  mine: (limit?: number) =>
    fetchApi<QuizAttempt[]>(`/quiz-attempts/mine${limit ? `?limit=${limit}` : ''}`),
  saveAnswers: (
    id: string,
    answers: Record<string, { value: unknown; answeredAt?: string; timeSpentMs?: number }>
  ) =>
    fetchApi<{ attemptId: string; status: QuizAttemptStatus }>(
      `/quiz-attempts/${id}/answers`,
      { method: 'PATCH', body: JSON.stringify({ answers }) }
    ),
  submit: (id: string) =>
    fetchApi<QuizAttemptSubmitResult>(`/quiz-attempts/${id}/submit`, { method: 'POST' }),
  review: (id: string) => fetchApi<QuizAttemptReview>(`/quiz-attempts/${id}/review`),
};

// ============================================================
// ACCESS CONTROL (cardholders / credentials / groups / schedules / enrollment)
// ============================================================

export interface AcCapabilities {
  doorControl: boolean;
  userManagement: boolean;
  credentialManagement: boolean;
  groupManagement: boolean;
  accessLevelManagement: boolean;
  lockdown: boolean;
  scheduleManagement?: boolean;
  holidayGroups?: boolean;
  biometricEnrollment?: boolean;
  photoEnrollment?: boolean;
  photoManagement?: boolean;
  mobileInvite?: boolean;
  cardSwipeEnrollment?: boolean;
  temporaryCredentials?: boolean;
  badgePrinting?: boolean;
  multiSite?: boolean;
  customFieldSchema?: boolean;
  userSearch?: boolean;
  lostStolenTracking?: boolean;
}

export interface AcCredential {
  credentialId: string;
  userId?: string;
  type: string;
  numbers: string[];
  number?: string;
  cardFormat?: string;
  facilityCode?: string;
  status?: string;
  enabled: boolean;
  validFrom?: string;
  validUntil?: string;
  isTemporary?: boolean;
  lostReportedAt?: string;
  stolenReportedAt?: string;
}

export interface AcCardholder {
  userId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  siteId?: string;
  department?: string;
  company?: string;
  employeeId?: string;
  badgeId?: string;
  photoUrl?: string;
  status: string;
  credentials: AcCredential[];
  accessGroups?: string[];
  accessLevels?: string[];
  validFrom?: string;
  validUntil?: string;
  customFields?: Record<string, string | number | boolean | null>;
}

export interface AcGroup {
  groupId: string;
  name: string;
  description?: string;
  siteId?: string;
  doors: string[];
  memberCount?: number;
}

export interface AcAccessLevel {
  accessLevelId: string;
  name: string;
  description?: string;
  siteId?: string;
  doors: string[];
  scheduleId?: string;
  priority?: number;
}

export interface AcSchedule {
  id: string;
  siteId?: string;
  name: string;
  description?: string;
  timezone: string;
  intervals: { daysOfWeek: number[]; startTime: string; endTime: string }[];
  holidayGroupIds?: string[];
}

export interface AcEnrollmentSession {
  sessionId: string;
  userId: string;
  mode: 'biometric' | 'photo' | 'card_swipe' | 'mobile_invite';
  state: 'pending' | 'capturing' | 'captured' | 'verifying' | 'completed' | 'failed' | 'cancelled';
  samplesCaptured?: number;
  samplesRequired?: number;
  qualityScore?: number;
  expiresAt: string;
  resultCredentialId?: string;
  errorMessage?: string;
}

export interface AcCardholderSearchOptions {
  q?: string;
  siteId?: string;
  groupId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

function acBase(instanceId: string, _deviceId: string, _tenantId: string): string {
  return `/access-control/${instanceId}`;
}
function acQs(deviceId: string, tenantId: string, extra?: Record<string, string | undefined>): string {
  const params = new URLSearchParams({ tenantId, deviceId });
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v !== undefined) params.set(k, v);
    }
  }
  return `?${params.toString()}`;
}

export const accessControlApi = {
  getCapabilities: (instanceId: string, deviceId: string, tenantId: string) =>
    fetchApi<AcCapabilities>(`${acBase(instanceId, deviceId, tenantId)}/capabilities${acQs(deviceId, tenantId)}`),

  getCustomFieldSchema: (instanceId: string, deviceId: string, tenantId: string, siteId?: string) =>
    fetchApi<unknown>(`${acBase(instanceId, deviceId, tenantId)}/custom-field-schema${acQs(deviceId, tenantId, { siteId })}`),

  // Cardholders
  searchCardholders: (instanceId: string, deviceId: string, tenantId: string, opts: AcCardholderSearchOptions = {}) =>
    fetchApi<{ users: AcCardholder[]; total: number }>(
      `${acBase(instanceId, deviceId, tenantId)}/cardholders${acQs(deviceId, tenantId, {
        q: opts.q,
        siteId: opts.siteId,
        groupId: opts.groupId,
        status: opts.status,
        limit: opts.limit !== undefined ? String(opts.limit) : undefined,
        offset: opts.offset !== undefined ? String(opts.offset) : undefined,
      })}`),

  getCardholder: (instanceId: string, deviceId: string, tenantId: string, userId: string) =>
    fetchApi<AcCardholder>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}${acQs(deviceId, tenantId)}`),

  createCardholder: (instanceId: string, deviceId: string, tenantId: string, input: Partial<AcCardholder>) =>
    fetchApi<AcCardholder>(`${acBase(instanceId, deviceId, tenantId)}/cardholders${acQs(deviceId, tenantId)}`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateCardholder: (instanceId: string, deviceId: string, tenantId: string, userId: string, updates: Partial<AcCardholder>) =>
    fetchApi<AcCardholder>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}${acQs(deviceId, tenantId)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),

  deleteCardholder: (instanceId: string, deviceId: string, tenantId: string, userId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}${acQs(deviceId, tenantId)}`, {
      method: 'DELETE',
    }),

  suspendCardholder: (instanceId: string, deviceId: string, tenantId: string, userId: string, reason?: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/suspend${acQs(deviceId, tenantId)}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  reinstateCardholder: (instanceId: string, deviceId: string, tenantId: string, userId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/reinstate${acQs(deviceId, tenantId)}`, {
      method: 'POST',
    }),

  uploadCardholderPhoto: (instanceId: string, deviceId: string, tenantId: string, userId: string, photoDataUrl: string) =>
    fetchApi<{ photoUrl: string }>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/photo${acQs(deviceId, tenantId)}`, {
      method: 'POST',
      body: JSON.stringify({ photoDataUrl }),
    }),

  deleteCardholderPhoto: (instanceId: string, deviceId: string, tenantId: string, userId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/photo${acQs(deviceId, tenantId)}`, {
      method: 'DELETE',
    }),

  getCardholderPhoto: (instanceId: string, deviceId: string, tenantId: string, userId: string) =>
    fetchApi<{ photoUrl: string | null }>(
      `${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/photo${acQs(deviceId, tenantId)}`,
    ),

  // Credentials
  listCredentials: (instanceId: string, deviceId: string, tenantId: string, userId: string) =>
    fetchApi<AcCredential[]>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/credentials${acQs(deviceId, tenantId)}`),

  addCredential: (instanceId: string, deviceId: string, tenantId: string, userId: string, credential: Partial<AcCredential>) =>
    fetchApi<AcCredential>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/credentials${acQs(deviceId, tenantId)}`, {
      method: 'POST',
      body: JSON.stringify(credential),
    }),

  deleteCredential: (instanceId: string, deviceId: string, tenantId: string, userId: string, credentialId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/credentials/${credentialId}${acQs(deviceId, tenantId)}`, {
      method: 'DELETE',
    }),

  reportCredentialLost: (instanceId: string, deviceId: string, tenantId: string, userId: string, credentialId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/credentials/${credentialId}/report-lost${acQs(deviceId, tenantId)}`, {
      method: 'POST',
    }),

  reportCredentialStolen: (instanceId: string, deviceId: string, tenantId: string, userId: string, credentialId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/cardholders/${userId}/credentials/${credentialId}/report-stolen${acQs(deviceId, tenantId)}`, {
      method: 'POST',
    }),

  findCredentialByNumber: (instanceId: string, deviceId: string, tenantId: string, cardNumber: string, facilityCode?: string) =>
    fetchApi<AcCredential | null>(
      `${acBase(instanceId, deviceId, tenantId)}/credentials/lookup${acQs(deviceId, tenantId, { cardNumber, facilityCode })}`,
    ),

  // Groups / access levels
  listGroups: (instanceId: string, deviceId: string, tenantId: string, siteId?: string) =>
    fetchApi<AcGroup[]>(`${acBase(instanceId, deviceId, tenantId)}/groups${acQs(deviceId, tenantId, { siteId })}`),

  listAccessLevels: (instanceId: string, deviceId: string, tenantId: string, siteId?: string) =>
    fetchApi<AcAccessLevel[]>(`${acBase(instanceId, deviceId, tenantId)}/access-levels${acQs(deviceId, tenantId, { siteId })}`),

  addCardholderToGroup: (instanceId: string, deviceId: string, tenantId: string, groupId: string, userId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/groups/${groupId}/members/${userId}${acQs(deviceId, tenantId)}`, {
      method: 'POST',
    }),

  removeCardholderFromGroup: (instanceId: string, deviceId: string, tenantId: string, groupId: string, userId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/groups/${groupId}/members/${userId}${acQs(deviceId, tenantId)}`, {
      method: 'DELETE',
    }),

  // Schedules
  listSchedules: (instanceId: string, deviceId: string, tenantId: string, siteId?: string) =>
    fetchApi<AcSchedule[]>(`${acBase(instanceId, deviceId, tenantId)}/schedules${acQs(deviceId, tenantId, { siteId })}`),

  // Enrollment
  getEnrollmentReaders: (instanceId: string, deviceId: string, tenantId: string) =>
    fetchApi<Array<{ readerId: string; name: string; capabilities: string[] }>>(
      `${acBase(instanceId, deviceId, tenantId)}/enrollment/readers${acQs(deviceId, tenantId)}`),

  startEnrollment: (instanceId: string, deviceId: string, tenantId: string, input: { userId: string; mode: string; biometricType?: string; readerId?: string; photoDataUrl?: string; ttlSeconds?: number }) =>
    fetchApi<AcEnrollmentSession>(`${acBase(instanceId, deviceId, tenantId)}/enrollment/start${acQs(deviceId, tenantId)}`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  getEnrollment: (instanceId: string, deviceId: string, tenantId: string, sessionId: string) =>
    fetchApi<AcEnrollmentSession>(`${acBase(instanceId, deviceId, tenantId)}/enrollment/${sessionId}${acQs(deviceId, tenantId)}`),

  cancelEnrollment: (instanceId: string, deviceId: string, tenantId: string, sessionId: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/enrollment/${sessionId}${acQs(deviceId, tenantId)}`, {
      method: 'DELETE',
    }),

  // Lockdown
  getLockdownStatus: (instanceId: string, deviceId: string, tenantId: string, siteId?: string) =>
    fetchApi<{ active: boolean; activatedAt?: string; reason?: string; level?: 'partial' | 'full' }>(
      `${acBase(instanceId, deviceId, tenantId)}/lockdown${acQs(deviceId, tenantId, { siteId })}`),

  activateLockdown: (instanceId: string, deviceId: string, tenantId: string, command: { reason?: string; level?: 'partial' | 'full'; siteId?: string }) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/lockdown${acQs(deviceId, tenantId)}`, {
      method: 'POST',
      body: JSON.stringify(command),
    }),

  deactivateLockdown: (instanceId: string, deviceId: string, tenantId: string, siteId?: string) =>
    fetchApi<void>(`${acBase(instanceId, deviceId, tenantId)}/lockdown${acQs(deviceId, tenantId, { siteId })}`, {
      method: 'DELETE',
    }),
};

// ─────────────────────────────────────────────────────────────────────
// Work Permits v2 — form-template-driven
// ─────────────────────────────────────────────────────────────────────

export interface PermitTypeApprovalStep {
  stepNumber: number;
  name: string;
  anyOf?: string[];
  roles?: string[];
  requireCert?: string;
  slaHours?: number;
}

/** Mirrors PermitFormSettings in @iosentra/shared. Lives in the linked
 *  form_templates.settings jsonb. */
export interface PermitFormSettings {
  isPermitTemplate: true;
  slug: string;
  display: { icon: string; color: string; category: string };
  numberPrefix: string;
  defaultValidityMinutes: number;
  applicantRequirements?: { skillIds?: string[]; certificationIds?: string[] };
  approvalChain: PermitTypeApprovalStep[];
  notifyOnApproveUserIds?: string[];
  fieldBindings: {
    requestedStart: string;
    requestedEnd: string;
    location?: string;
    workDescription?: string;
    fireWatchRequired?: string;
  };
}

/** Thin registry row — humans see this in the catalog; everything else
 *  about the permit lives in the linked form template's settings. */
export interface PermitType {
  id: string;
  tenantId: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  category: string;
  formTemplateId: string;
  enabled: boolean;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PermitStatus =
  | 'draft'
  | 'submitted'
  | 'in_review'
  | 'approved'
  | 'active'
  | 'expired'
  | 'closed'
  | 'rejected'
  | 'revoked';

export interface PermitApprovalHistoryEntry {
  stepNumber: number;
  approverUserId: string;
  approverName: string;
  decision: 'approved' | 'rejected';
  comments?: string | null;
  decidedAt: string;
}

export interface Permit {
  id: string;
  tenantId: string;
  permitNumber: string;
  permitTypeId: string;
  applicantType: 'user' | 'contractor_employee';
  applicantId: string;
  applicantName: string;
  siteId: string | null;
  buildingId: string | null;
  floorId: string | null;
  requestedStart: string;
  requestedEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  status: PermitStatus;
  formSubmissionId: string;
  currentStep: number;
  approvalHistory: PermitApprovalHistoryEntry[];
  qualificationOverride: { overriddenBy: string; overriddenByName: string; reason: string; at: string } | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revokeReason: string | null;
  closedAt: string | null;
  closedBy: string | null;
  closeNotes: string | null;
  metadata: Record<string, unknown>;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const permitTypesApi = {
  list: (params?: { enabledOnly?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.enabledOnly) sp.set('enabledOnly', 'true');
    const q = sp.toString();
    return fetchApi<PermitType[]>(`/permit-types${q ? `?${q}` : ''}`);
  },
  get: (id: string) => fetchApi<{ permitType: PermitType; template: FormTemplate | null }>(`/permit-types/${id}`),
  create: (data: {
    slug: string;
    name: string;
    description?: string | null;
    icon?: string | null;
    color?: string | null;
    category: string;
    formTemplateId: string;
    enabled?: boolean;
    sortOrder?: number;
  }) =>
    fetchApi<PermitType>('/permit-types', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<PermitType>) =>
    fetchApi<PermitType>(`/permit-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    fetchApi<{ deleted: boolean }>(`/permit-types/${id}`, { method: 'DELETE' }),
  seed: () => fetchApi<{ inserted: number; skipped: number }>('/permit-types/seed', { method: 'POST' }),
};

export interface PermitListParams {
  status?: string;
  permitTypeId?: string;
  applicantId?: string;
  siteId?: string;
  buildingId?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  pendingMine?: boolean;
  mine?: boolean;
  page?: number;
  limit?: number;
}

export const permitsApi = {
  list: (params?: PermitListParams) => {
    const sp = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v == null || v === '') continue;
        sp.set(k, String(v));
      }
    }
    const q = sp.toString();
    return fetchApi<Permit[]>(`/permits${q ? `?${q}` : ''}`);
  },
  get: (id: string) =>
    fetchApi<{
      permit: Permit;
      submission: FormSubmission | null;
      permitType: PermitType | null;
      template: FormTemplate | null;
    }>(`/permits/${id}`),
  submit: (data: {
    permitTypeId: string;
    applicantId?: string;
    applicantName?: string;
    formData: Record<string, unknown>;
    qualificationOverrideReason?: string;
    metadata?: Record<string, unknown>;
  }) =>
    fetchApi<Permit>('/permits', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  decide: (id: string, decision: 'approved' | 'rejected', comments?: string) =>
    fetchApi<Permit>(`/permits/${id}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision, comments }),
    }),
  revoke: (id: string, reason: string) =>
    fetchApi<Permit>(`/permits/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  close: (id: string, notes?: string) =>
    fetchApi<Permit>(`/permits/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),
};

// Health Center API — mirrors apps/api-gateway/src/services/health-center.service.ts
export interface HealthCheckRow {
  name: string;
  pass: boolean;
  severity: 'ok' | 'warning' | 'critical';
  detail?: string;
  value?: number;
  threshold?: number;
}

export interface DeviceHealthRow {
  id: string;
  name: string;
  category: string;
  driverSlug: string;
  driverInstanceId: string;
  siteId: string | null;
  siteName: string | null;
  buildingName: string | null;
  status: string;
  healthScore: number;
  checks: HealthCheckRow[];
  lastHeartbeat: string | null;
}

export interface VideoChannelHealthRow {
  deviceId: string;
  deviceName: string;
  driverInstanceId: string;
  driverSlug: string;
  channelId: number;
  siteName: string | null;
  buildingName: string | null;
  online: boolean;
  recording: boolean;
  recordingMode?: string;
  recordingStart?: string;
  recordingEnd?: string;
  retentionDays?: number;
  configuredRetentionDays?: number;
  actualRetentionDays?: number;
  storagePercent?: number;
  resolution?: string;
  codec?: string;
  recordingCloudManaged?: boolean;
  retentionCloudManaged?: boolean;
  storageCloudManaged?: boolean;
  recordingUnknown?: boolean;
  retentionUnknown?: boolean;
  storageUnknown?: boolean;
  healthScore: number;
}

export interface SecurityStatusRow {
  deviceId: string;
  deviceName: string;
  firmwareVersion?: string;
  firmwareLatest?: string;
  firmwareUpToDate?: boolean;
  httpsEnabled?: boolean;
  defaultPasswordChanged?: boolean;
  certificateExpiry?: string;
  cyberScore: number;
  // Device clock-sync (drift vs server). clockStatus 'unsupported' when
  // the driver can't read the device clock.
  deviceClockUtc?: string;
  clockDriftSeconds?: number;
  clockStatus?: 'ok' | 'warning' | 'critical' | 'unsupported';
}

export interface HealthShareRow {
  id: string;
  shareToken: string;
  label: string;
  url: string;
  expiresAt?: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface HealthSummary {
  systemScore: number;
  totalDevices: number;
  online: number;
  offline: number;
  critical: number;
  warnings: number;
  lastScanTime: string;
  sites: Array<{
    siteId: string;
    siteName: string;
    totalDevices: number;
    online: number;
    critical: number;
    warnings: number;
    score: number;
  }>;
  topProblems: Array<{
    deviceId: string;
    deviceName: string;
    driverSlug: string;
    category: string;
    healthScore: number;
    failingChecks: Array<{ name: string; severity: 'warning' | 'critical'; detail?: string }>;
  }>;
  driverMix: Array<{ driverSlug: string; count: number }>;
  history7d: Array<{
    capturedAt: string;
    systemScore: number;
    online: number;
    critical: number;
    warnings: number;
    recordingCameras: number;
  }>;
  delta24h?: {
    systemScore: number;
    online: number;
    critical: number;
  };
}

export interface PredictionAlert {
  type: 'storage_full' | 'retention_breach' | 'device_failure' | 'firmware_outdated';
  deviceId: string;
  deviceName: string;
  detail: string;
  severity: 'low' | 'medium' | 'high';
  estimatedDate?: string;
  confidence?: number;
}

export interface VisionResultRow {
  deviceId: string;
  deviceName: string;
  deviceCategory: string;
  channelId: number | null;
  capturedAt: string;
  brightness: number | null;
  blurScore: number | null;
  frameHash: string | null;
  histogramSpread: number | null;
  baselineDistance: number | null;
  baselineHash: string | null;
  baselineAt: string | null;
  shiftCheckDisabled: boolean;
  issues: Array<
    | 'too_dark'
    | 'too_bright'
    | 'blurry'
    | 'frozen'
    | 'lens_covered'
    | 'camera_shifted'
  >;
  qualityScore: number | null;
}

export const healthCenterApi = {
  summary: () => fetchApi<HealthSummary>('/health-center/summary'),
  devices: () => fetchApi<{ devices: DeviceHealthRow[] }>('/health-center/devices'),
  video: () => fetchApi<{ channels: VideoChannelHealthRow[] }>('/health-center/video'),
  security: () => fetchApi<{ devices: SecurityStatusRow[] }>('/health-center/security'),
  predictions: () => fetchApi<{ alerts: PredictionAlert[] }>('/health-center/predictions'),
  vision: () => fetchApi<{ results: VisionResultRow[] }>('/health-center/vision'),
  rebaseline: (deviceId: string) =>
    fetchApi<{ ok: boolean }>(`/health-center/vision/baseline/${deviceId}`, { method: 'POST' }),
  setShiftCheck: (deviceId: string, disabled: boolean) =>
    fetchApi<{ ok: boolean; disabled: boolean }>(
      `/health-center/vision/shift-config/${deviceId}`,
      { method: 'PATCH', body: JSON.stringify({ disabled }) },
    ),
  scan: () => fetchApi<HealthSummary>('/health-center/scan', { method: 'POST' }),
  probeNow: () =>
    fetchApi<{ devices: SecurityStatusRow[] }>('/health-center/probe-now', { method: 'POST' }),
  syncClock: (deviceId: string) =>
    fetchApi<{ ok: boolean; reason?: string }>(
      `/health-center/clock/sync/${deviceId}`,
      { method: 'POST' },
    ),
  // Public status-page share management.
  listShares: () =>
    fetchApi<{ shares: HealthShareRow[] }>('/health-center/shares'),
  createShare: (label: string, expiresInHours?: number) =>
    fetchApi<HealthShareRow>('/health-center/shares', {
      method: 'POST',
      body: JSON.stringify({ label, expiresInHours }),
    }),
  deleteShare: (id: string) =>
    fetchApi<{ ok: boolean }>(`/health-center/shares/${id}`, { method: 'DELETE' }),
  getProbeConfig: () =>
    fetchApi<{ defaultPasswordCheckEnabled: boolean }>('/health-center/probe-config'),
  setProbeConfig: (enabled: boolean) =>
    fetchApi<{ defaultPasswordCheckEnabled: boolean }>('/health-center/probe-config', {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),
};

export { ApiError, fetchApi };
